import { useState, useEffect, useRef, Fragment } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  "https://lmdtewumbeueuirdcovn.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxtZHRld3VtYmV1ZXVpcmRjb3ZuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgxODc2ODQsImV4cCI6MjA5Mzc2MzY4NH0.rS1GE5rqwEllieWNJI9m5Z5p97dJUPdqaQLpwuElE5E",
  { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storage: localStorage } }
);

const callFn = async (name, body) => {
  const res = await fetch(`/.netlify/functions/${name}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
  });
  return res.json();
};

const uploadMedia = async (file, userId) => {
  const ext = file.name.split(".").pop() || (file.type.includes("video") ? "mp4" : "jpg");
  const path = `${userId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("Messages").upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from("Messages").getPublicUrl(path);
  return data.publicUrl;
};

// ── UTILS ─────────────────────────────────────────────────────────────────────
const calcAge = (d) => {
  if (!d) return null;
  const t = new Date(), b = new Date(d);
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() - b.getMonth() < 0 || (t.getMonth() - b.getMonth() === 0 && t.getDate() < b.getDate())) a--;
  return a;
};
const fmtDate = (iso) => {
  if (!iso) return "";
  // Date-only strings (YYYY-MM-DD) parse as UTC midnight, which shifts back one day in US timezones.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + "T12:00:00") : new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
const fmtLastActive = (iso) => {
  if (!iso) return "No sessions yet";
  const diffDays = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Active today";
  if (diffDays === 1) return "Active yesterday";
  if (diffDays <= 7) return `Active ${diffDays} days ago`;
  return `Last active ${fmtDate(iso)}`;
};
const statusColor = (s) => s === "strong" ? "#3a6b3a" : s === "developing" ? "#c9a84c" : "#b80303";

// Dynamic day map based on program's days_per_week
const getDayMapForProgram = (daysPerWeek) => {
  switch (daysPerWeek) {
    case 1: return { 1: 1 };
    case 2: return { 1: 1, 4: 2 };
    case 3: return { 1: 1, 3: 2, 5: 3 };
    case 4: return { 1: 1, 2: 2, 3: 3, 4: 4 };
    case 5: return { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 };
    default: return { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 };
  }
};

const getProgramDay = (startDate, dateStr, dayMap) => {
  const d = new Date(dateStr + "T12:00:00");
  return (dayMap || getDayMapForProgram(5))[d.getDay()] || null;
};

// Builds {jsDay: day_number} from workouts with day_of_week set (1=Mon..7=Sun → jsDay 0=Sun,1=Mon..6=Sat)
// Returns null if no workouts have day_of_week set (falls back to getDayMapForProgram)
const buildDayMapFromWorkouts = (workouts) => {
  if (!workouts?.some(w => w.day_of_week != null)) return null;
  const map = {};
  workouts.filter(w => w.day_of_week != null).forEach(w => {
    const jsDay = w.day_of_week === 7 ? 0 : w.day_of_week;
    map[jsDay] = w.day_number;
  });
  return map;
};

const getWeekNum = (startDate, dateStr) => {
  if (!startDate) return 1;
  const s = new Date(startDate + "T12:00:00");
  const t = new Date(dateStr + "T12:00:00");
  return Math.max(1, Math.floor((t - s) / (7 * 24 * 60 * 60 * 1000)) + 1);
};

// Maps session logs back to their scheduled program calendar dates using day_number.
// Sessions are sorted ascending so the earliest session claims the earliest matching date.
const buildCompletedDatesFromSessions = (allSessions, progData) => {
  if (!allSessions || !progData?.start_date) {
    return (allSessions || []).map(s => s.completed_at.split("T")[0]);
  }
  const startMs = new Date(progData.start_date + "T12:00:00").getTime();
  const dayMap = getDayMapForProgram(progData.days_per_week || 5);
  const programDates = [];
  for (let w = 0; w < (progData.weeks || 4); w++) {
    for (let d = 0; d < 7; d++) {
      const ms = startMs + (w * 7 + d) * 24 * 60 * 60 * 1000;
      const dateStr = new Date(ms).toISOString().split("T")[0];
      const dayNum = getProgramDay(progData.start_date, dateStr, dayMap);
      if (dayNum) programDates.push({ dateStr, dayNum });
    }
  }
  const sortedSessions = [...allSessions].sort((a, b) =>
    new Date(a.completed_at) - new Date(b.completed_at)
  );
  const claimed = new Set();
  const completedDatesArr = [];
  sortedSessions.forEach(ses => {
    const sesDate = ses.completed_at.split("T")[0];
    const match = programDates.find(pd =>
      pd.dayNum === ses.day_number &&
      !claimed.has(pd.dateStr) &&
      pd.dateStr <= sesDate
    );
    if (match) {
      claimed.add(match.dateStr);
      completedDatesArr.push(match.dateStr);
    } else {
      completedDatesArr.push(sesDate);
    }
  });
  return completedDatesArr;
};

// Epley formula: predict weight for target reps given known weight/reps
const predictWeight = (knownWeight, knownReps, targetReps) => {
  if (!knownWeight || !knownReps || knownReps >= 30) return null;
  const orm = knownWeight * (1 + knownReps / 30);
  return Math.round(orm / (1 + targetReps / 30));
};

const SS_COLORS = ["#b80303", "#c9a84c", "#3a6b3a", "#6b63a4", "#2a7a8a"];

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : null;
};

// ── MARKS STANDARDS DATA ──────────────────────────────────────────────────────
const MARKS_STANDARDS = [
  {
    cat: "ATG Split Squat",
    exercises: ["ATG Split Squat"],
    follower: { desc: "Unassisted × 10 reps", weight: 0, reps: 10 },
    disciple: { desc: "Floor level × 10 reps", weight: 0, reps: 10 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw*0.25)} lbs ea hand × 8 reps`, weight: Math.round(bw*0.25), reps: 8 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.15)} lbs ea hand × 8 reps`, weight: Math.round(bw*0.15), reps: 8 }),
  },
  {
    cat: "Hip Flexor Raise",
    exercises: ["Hip Flexor Raise"],
    follower: { desc: "Knee above hip × 20 reps", weight: 0, reps: 20 },
    disciple: { desc: "Controlled × 20 reps", weight: 0, reps: 20 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw*0.1)} lbs × 20 reps`, weight: Math.round(bw*0.1), reps: 20 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.06)} lbs × 20 reps`, weight: Math.round(bw*0.06), reps: 20 }),
  },
  {
    cat: "FHL Calf Raise",
    exercises: ["FHL Calf Raise", "Calf Raise"],
    follower: { desc: "2-leg × 20 reps", weight: 0, reps: 20 },
    disciple: { desc: "1-leg × 15 reps", weight: 0, reps: 15 },
    apostle_m: (bw) => ({ desc: `1-leg ${Math.round(bw*0.25)} lbs × 10 reps`, weight: Math.round(bw*0.25), reps: 10 }),
    apostle_f: (bw) => ({ desc: `1-leg ${Math.round(bw*0.15)} lbs × 10 reps`, weight: Math.round(bw*0.15), reps: 10 }),
  },
  {
    cat: "Tibialis Raise",
    exercises: ["Tibialis Raise"],
    follower: { desc: "20 reps on wall", weight: 0, reps: 20 },
    disciple: { desc: "20 reps with weight", weight: 0, reps: 20 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw*0.2)} lbs × 15 reps`, weight: Math.round(bw*0.2), reps: 15 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.12)} lbs × 15 reps`, weight: Math.round(bw*0.12), reps: 15 }),
  },
  {
    cat: "Back Extension",
    exercises: ["Back Extension"],
    follower: { desc: "2-min hold 2-leg", weight: 0, reps: 1 },
    disciple: { desc: "20 reps 2-leg / 15 reps 1-leg", weight: 0, reps: 20 },
    apostle_m: (bw) => ({ desc: "Single leg 45 lbs × 8 reps", weight: 45, reps: 8 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.4)} lbs × 8 reps`, weight: Math.round(bw*0.4), reps: 8 }),
  },
  {
    cat: "Pull-Ups",
    exercises: ["Pull-Ups", "Pull Up", "Pullup"],
    follower: { desc: "Control down 10 seconds", weight: 0, reps: 1 },
    disciple: { desc: "1 full pull-up", weight: 0, reps: 1 },
    apostle_m: (bw) => ({ desc: "12 reps @ 31X0", weight: 0, reps: 12 }),
    apostle_f: (bw) => ({ desc: "1 strict rep", weight: 0, reps: 1 }),
  },
  {
    cat: "Incline Press",
    exercises: ["DB Incline Press", "Incline Press"],
    follower: { desc: "Full range of motion reps", weight: 0, reps: 8 },
    disciple_m: (bw) => ({ desc: `${Math.round(bw*0.375)} lbs per hand × 8`, weight: Math.round(bw*0.375), reps: 8 }),
    disciple_f: (bw) => ({ desc: `${Math.round(bw*0.2)} lbs per hand × 8`, weight: Math.round(bw*0.2), reps: 8 }),
    apostle_m: (bw) => ({ desc: `${Math.round(bw)} lbs total × 5`, weight: Math.round(bw), reps: 5 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.5)} lbs total × 5`, weight: Math.round(bw*0.5), reps: 5 }),
  },
  {
    cat: "Squat",
    exercises: ["Goblet Squat", "Squat", "Front Squat"],
    follower: { desc: "Full range × 10 reps @ 45 lbs", weight: 45, reps: 10 },
    disciple: { desc: "95 lbs × 10 reps", weight: 95, reps: 10 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw)} lbs × 10 reps`, weight: Math.round(bw), reps: 10 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.8)} lbs × 10 reps`, weight: Math.round(bw*0.8), reps: 10 }),
  },
  {
    cat: "RDL",
    exercises: ["RDL", "Romanian Deadlift", "Single Leg RDL"],
    follower: { desc: "Form mastery @ 45 lbs × 12", weight: 45, reps: 12 },
    disciple: { desc: "95 lbs × 12 reps", weight: 95, reps: 12 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw)} lbs × 12 reps`, weight: Math.round(bw), reps: 12 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.8)} lbs × 12 reps`, weight: Math.round(bw*0.8), reps: 12 }),
  },
  {
    cat: "Lower Abs",
    exercises: ["Garhammer Raise", "Reverse Squat"],
    follower: { desc: "Garhammer × 20 reps floor to 90°", weight: 0, reps: 20 },
    disciple: { desc: "Garhammer 30s hold at 90°", weight: 0, reps: 1 },
    apostle_m: (bw) => ({ desc: `Reverse Squat ${Math.round(bw*0.5)} lbs × 20`, weight: Math.round(bw*0.5), reps: 20 }),
    apostle_f: (bw) => ({ desc: `Reverse Squat ${Math.round(bw*0.3)} lbs × 20`, weight: Math.round(bw*0.3), reps: 20 }),
  },
  {
    cat: "QL Extension",
    exercises: ["QL Extension"],
    follower: { desc: "Unassisted × 10 each side", weight: 0, reps: 10 },
    disciple: { desc: "On back extension × 10 each side", weight: 0, reps: 10 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw*0.25)} lbs × 10 each side`, weight: Math.round(bw*0.25), reps: 10 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.1)} lbs × 10 each side`, weight: Math.round(bw*0.1), reps: 10 }),
  },
  {
    cat: "Pullover",
    exercises: ["Pullover"],
    follower: { desc: "Flat bench × 10 reps", weight: 0, reps: 10 },
    disciple: { desc: "Cross bench × 10 reps", weight: 0, reps: 10 },
    apostle_m: (bw) => ({ desc: `${Math.round(bw*0.25)} lbs × 10 @ 31X1`, weight: Math.round(bw*0.25), reps: 10 }),
    apostle_f: (bw) => ({ desc: `${Math.round(bw*0.12)} lbs × 10`, weight: Math.round(bw*0.12), reps: 10 }),
  },
];

const getClientLevel = (std, clientBestWeight, clientBestReps, bw, gender = "m") => {
  if (!clientBestReps && clientBestReps !== 0) return { level: null, pct: 0 };
  const g = gender === "f" ? "f" : "m";
  const resolve = (tier) => {
    if (!tier) return null;
    if (typeof tier === "function") return tier(bw);
    const gKey = `${tier.constructor?.name === "Object" ? "" : ""}`;
    return tier;
  };
  const follower = resolve(std.follower);
  const disciple = resolve(std[`disciple_${g}`] || std.disciple);
  const apostle = resolve(std[`apostle_${g}`]);
  if (apostle && clientBestWeight >= apostle.weight && clientBestReps >= apostle.reps) {
    return { level: "apostle", pct: 100, next: null, nextDesc: "Peak achieved" };
  }
  if (disciple && clientBestWeight >= disciple.weight && clientBestReps >= disciple.reps) {
    return { level: "disciple", pct: 75, next: "apostle", nextDesc: apostle?.desc, nextWeight: apostle?.weight, nextReps: apostle?.reps };
  }
  if (follower && clientBestWeight >= follower.weight && clientBestReps >= follower.reps) {
    return { level: "follower", pct: 40, next: "disciple", nextDesc: disciple?.desc || apostle?.desc, nextWeight: disciple?.weight || apostle?.weight, nextReps: disciple?.reps || apostle?.reps };
  }
  return { level: null, pct: 10, next: "follower", nextDesc: follower?.desc, nextWeight: follower?.weight, nextReps: follower?.reps };
};

const RESOURCES = [
  { id: 1, cat: "Methodology", title: "The Three Pillars of Max Strength", desc: "How we approach training — and why it works." },
  { id: 2, cat: "Methodology", title: "Understanding Tempo", desc: "Why slowing down makes you stronger." },
  { id: 3, cat: "Recovery", title: "The Case for Walking More", desc: "Simple, free, and one of the most underrated tools." },
  { id: 4, cat: "Lifestyle", title: "Sleep & Strength", desc: "You're leaving gains on the table without this." },
];

const REST_TIPS = [
  "Sleep is when your body actually gets stronger. Prioritize 8 hours tonight.",
  "Walk for 20-30 minutes today. It's active recovery, not laziness.",
  "Drink more water than you think you need. Aim for half your bodyweight in ounces.",
  "Spend 10 minutes in prayer or quiet reflection. Mental recovery matters.",
  "Eat enough protein today — your muscles are repairing right now.",
  "Foam roll your hips and thoracic spine for 5 minutes.",
  "Get outside. Sunlight and fresh air are recovery tools.",
  "Your next session will be better because you rested today. Trust the process.",
];

const MOBILITY_FLOW = [
  { name: "Couch Stretch", duration: 60, note: "each side" },
  { name: "Elephant Walk", reps: 15, duration: 0, note: "each side" },
  { name: "Pigeon Pose", duration: 45, note: "each side" },
  { name: "Prayer Stretch", duration: 45, note: "" },
  { name: "Cow Stretch", duration: 60, note: "each side" },
];

const playDing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880; gain.gain.value = 0.3;
    osc.start(); setTimeout(() => { osc.stop(); ctx.close(); }, 300);
  } catch (e) {}
};

function useTimer(initialSecs = 0) {
  const [running, setRunning] = useState(false);
  const [secs, setSecs] = useState(initialSecs);
  const [dinged, setDinged] = useState(false);
  useEffect(() => { setSecs(initialSecs); setDinged(false); setRunning(false); }, [initialSecs]);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSecs(s => {
      if (s <= 1) {
        setRunning(false);
        if (!dinged) { playDing(); setDinged(true); }
        return 0;
      }
      return s - 1;
    }), 1000);
    return () => clearInterval(id);
  }, [running, dinged]);
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  return { running, secs, fmt: fmt(secs), toggle: () => setRunning(r => !r), reset: (v) => { setRunning(false); setSecs(v ?? initialSecs); setDinged(false); } };
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  :root {
    --cr:#f5f0e8;--cr2:#ede8de;--cr3:#e0d9cc;
    --ink:#1a1714;--ink2:#3d3830;--ink3:#6b6358;
    --red:#b80303;--gold:#c9a84c;--grn:#3a6b3a;--wh:#ffffff;
    --sh:0 2px 16px rgba(26,23,20,0.08);--shl:0 8px 40px rgba(26,23,20,0.12);
    --r:3px;--fd:'Cormorant Garamond',Georgia,serif;--fb:'DM Sans',system-ui,sans-serif;
    --xs:13px;--sm:15px;--md:16px;--lg:20px;--xl:26px;--2xl:36px;
  }
  .lt{--xs:14px;--sm:16px;--md:19px;--lg:23px;--xl:30px;--2xl:40px;}
  *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
  body{background:var(--cr);font-family:var(--fb);font-size:var(--md);color:var(--ink);min-height:100vh;-webkit-font-smoothing:antialiased;}
  .app{max-width:430px;margin:0 auto;min-height:100vh;background:var(--cr);position:relative;}

  .spl{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 32px;position:relative;overflow:hidden;}
  .spl-bg{position:absolute;inset:0;background:linear-gradient(160deg,var(--cr) 0%,var(--cr2) 60%,var(--cr3) 100%);}
  .spl-ln{position:absolute;top:0;left:50%;transform:translateX(-50%);width:1px;height:80px;background:linear-gradient(to bottom,transparent,var(--red));}
  .spl-c{position:relative;text-align:center;width:100%;}
  .spl-wm{font-family:var(--fd);font-size:56px;font-weight:600;color:var(--ink);letter-spacing:-1px;line-height:1;margin-bottom:6px;}
  .spl-wm span{color:var(--red);}
  .spl-tg{font-size:var(--xs);letter-spacing:4px;text-transform:uppercase;color:var(--ink3);margin-bottom:56px;}
  .spl-v{font-family:var(--fd);font-style:italic;font-size:var(--lg);color:var(--ink2);line-height:1.5;margin-bottom:6px;padding:0 8px;}
  .spl-vr{font-size:var(--xs);letter-spacing:3px;text-transform:uppercase;color:var(--ink3);margin-bottom:48px;}
  .spl-dv{width:40px;height:1px;background:var(--red);margin:0 auto 48px;}

  .bp{width:100%;padding:16px 24px;background:var(--ink);color:var(--cr);font-family:var(--fb);font-size:var(--sm);font-weight:500;letter-spacing:2px;text-transform:uppercase;border:none;cursor:pointer;transition:background 0.2s;border-radius:var(--r);}
  .bp:hover{background:var(--ink2);}  .bp:disabled{background:var(--cr3);cursor:not-allowed;}  .bp.grn{background:var(--grn);}
  .bg{width:100%;padding:14px 24px;background:transparent;color:var(--ink3);font-family:var(--fb);font-size:var(--xs);font-weight:500;letter-spacing:2px;text-transform:uppercase;border:1px solid var(--cr3);cursor:pointer;transition:all 0.2s;border-radius:var(--r);margin-top:10px;}
  .bg:hover{border-color:var(--ink3);color:var(--ink);}
  .bs{padding:11px 16px;min-height:44px;background:var(--ink);color:var(--cr);font-family:var(--fb);font-size:var(--xs);font-weight:500;letter-spacing:1px;text-transform:uppercase;border:none;cursor:pointer;transition:background 0.2s;border-radius:var(--r);display:inline-flex;align-items:center;justify-content:center;}
  .bs:hover{background:var(--ink2);}  .bs.red{background:var(--red);}  .bs.grn{background:var(--grn);}
  .bs.o{background:transparent;color:var(--ink2);border:1px solid var(--cr3);}  .bs.o:hover{border-color:var(--ink3);}
  .bl{background:none;border:none;font-size:var(--xs);letter-spacing:2px;text-transform:uppercase;color:var(--ink3);cursor:pointer;padding:16px 0 0;transition:color 0.2s;display:block;}
  .bl:hover{color:var(--ink);}

  .ap{padding:0 28px 60px;}
  .ah{padding:40px 0 28px;border-bottom:1px solid var(--cr3);margin-bottom:32px;}
  .at{font-family:var(--fd);font-size:var(--2xl);font-weight:600;margin-bottom:4px;}
  .as{font-size:var(--sm);color:var(--ink3);}
  .fl{margin-bottom:20px;}
  .fl label{display:block;font-size:var(--xs);letter-spacing:2px;text-transform:uppercase;color:var(--ink3);margin-bottom:7px;font-weight:500;}
  .fi{width:100%;background:var(--wh);border:1px solid var(--cr3);color:var(--ink);padding:13px 16px;font-family:var(--fb);font-size:var(--md);outline:none;transition:border-color 0.2s;border-radius:var(--r);}
  .fi:focus{border-color:var(--ink);}  .fi::placeholder{color:var(--cr3);}
  .em{background:#fff0f0;border:1px solid #ffd0d0;color:var(--red);padding:12px 14px;font-size:var(--sm);border-radius:var(--r);margin-bottom:16px;}

  .pg{padding:0 24px 100px;}
  .ph{padding:28px 0 20px;border-bottom:1px solid var(--cr3);margin-bottom:24px;}
  .pe{font-size:var(--xs);letter-spacing:3px;text-transform:uppercase;color:var(--red);margin-bottom:5px;font-weight:500;}
  .ptt{font-family:var(--fd);font-size:var(--2xl);font-weight:600;line-height:1.1;}
  .pss{font-size:var(--sm);color:var(--ink3);margin-top:4px;}
  .sl{font-size:var(--xs);letter-spacing:3px;text-transform:uppercase;color:var(--ink3);margin-bottom:10px;padding-bottom:7px;border-bottom:1px solid var(--cr3);font-weight:500;}

  .vc{background:var(--wh);border:1px solid var(--cr3);border-left:3px solid var(--red);padding:18px 20px;margin-bottom:24px;border-radius:var(--r);}
  .vt{font-family:var(--fd);font-style:italic;font-size:var(--lg);color:var(--ink);line-height:1.5;margin-bottom:6px;}
  .vr{font-size:var(--xs);letter-spacing:2px;text-transform:uppercase;color:var(--ink3);}
  .sr{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:24px;}
  .sc{background:var(--wh);border:1px solid var(--cr3);padding:16px 12px;text-align:center;border-radius:var(--r);}
  .sn{font-family:var(--fd);font-size:var(--2xl);font-weight:600;color:var(--red);line-height:1;}
  .sl2{font-size:var(--xs);letter-spacing:1px;text-transform:uppercase;color:var(--ink3);margin-top:4px;}
  .tb{background:var(--ink);padding:22px 20px;margin-bottom:8px;position:relative;cursor:pointer;transition:background 0.2s;border-radius:var(--r);}
  .tb::after{content:'→';position:absolute;right:20px;top:50%;transform:translateY(-50%);color:rgba(255,255,255,0.4);font-size:20px;}
  .tb:hover{background:var(--ink2);}  .tb.missed{background:var(--red);}
  .te{font-size:12px;letter-spacing:3px;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-bottom:5px;}
  .tn{font-family:var(--fd);font-size:var(--xl);font-weight:600;color:var(--wh);margin-bottom:3px;}
  .tm{font-size:var(--xs);color:rgba(255,255,255,0.5);}
  .ml{display:flex;flex-direction:column;gap:8px;}
  .mi{background:var(--wh);border:1px solid var(--cr3);padding:15px 16px;display:flex;align-items:center;gap:14px;cursor:pointer;transition:border-color 0.2s;border-radius:var(--r);}
  .mi:hover{border-color:var(--ink3);}
  .mico{width:36px;height:36px;background:var(--cr2);display:flex;align-items:center;justify-content:center;font-size:16px;border-radius:var(--r);flex-shrink:0;}
  .mna{font-size:var(--sm);font-weight:500;color:var(--ink);}
  .mde{font-size:var(--xs);color:var(--ink3);margin-top:1px;}
  .maro{color:var(--cr3);font-size:16px;margin-left:auto;}

  /* CALENDAR */
  .cal-day{aspect-ratio:1;display:flex;align-items:center;justify-content:center;border-radius:50%;font-size:var(--xs);font-weight:500;cursor:pointer;border:1px solid var(--cr3);transition:all 0.15s;position:relative;flex-direction:column;gap:3px;background:transparent;color:var(--ink);}
  .cal-day.rest{cursor:default;pointer-events:none;}
  .cal-day.today{background:var(--ink);color:white;}
  .cal-dot{width:5px;height:5px;border-radius:50%;}
  .cal-dot.dot-done{background:var(--grn);}
  .cal-dot.dot-missed{background:var(--red);}
  .cal-dot.dot-today{background:white;}
  .cal-dot.dot-scheduled{background:transparent;border:1px solid var(--cr3);}

  /* WORKOUT */
  .wmt{display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;}
  .wtag{font-size:var(--xs);letter-spacing:1px;text-transform:uppercase;color:var(--ink3);background:var(--cr2);padding:4px 10px;border-radius:20px;}
  .ss-group{margin-bottom:12px;}
  .ss-label{font-size:11px;letter-spacing:2px;text-transform:uppercase;font-weight:600;padding:6px 0 8px 14px;display:flex;align-items:center;gap:6px;}
  .ss-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
  .ss-exs{border-left:3px solid;border-radius:0 var(--r) var(--r) 0;overflow:hidden;}
  .ec{background:var(--wh);border:1px solid var(--cr3);border-left:none;transition:border-color 0.2s;}
  .ec:not(:last-child){border-bottom:1px solid var(--cr2);}
  .ec.active{background:var(--wh);border-color:var(--ink);border-left:none;box-shadow:var(--sh);}
  .ec.complete{background:#f8fbf8;}
  .standalone{border-radius:var(--r) !important;border-left:1px solid var(--cr3) !important;margin-bottom:8px;}
  .standalone.active{border-color:var(--ink) !important;}
  .et{padding:14px 16px;display:flex;align-items:center;gap:12px;cursor:pointer;}
  .enu{font-family:var(--fd);font-size:var(--xl);font-weight:600;color:var(--cr3);width:28px;flex-shrink:0;line-height:1;}
  .ec.active .enu{color:var(--red);}  .ec.complete .enu{color:var(--grn);}
  .em2{flex:1;}
  .ena{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .eset{font-size:var(--xs);color:var(--ink3);margin-top:2px;}
  .elast{font-size:var(--xs);color:var(--grn);margin-top:3px;font-style:italic;}
  .epred{font-size:var(--xs);color:var(--gold);margin-top:2px;font-style:italic;}
  .eck{width:22px;height:22px;border:1.5px solid var(--cr3);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.2s;flex-shrink:0;border-radius:50%;}
  .eck.chk{background:var(--grn);border-color:var(--grn);}
  .eex{padding:14px 16px 16px 56px;border-top:1px solid var(--cr2);}
  .sinp{width:100%;background:var(--cr2);border:1px solid var(--cr3);color:var(--ink);padding:8px 10px;font-family:var(--fb);font-size:var(--sm);outline:none;border-radius:var(--r);text-align:center;}
  .sinp:focus{border-color:var(--ink);}  .sinp::placeholder{color:var(--cr3);}
  .sunit{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:var(--ink3);margin-bottom:4px;}
  .eact{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;}
  .trow{display:flex;align-items:center;gap:10px;background:var(--cr2);padding:10px 12px;border-radius:var(--r);margin-bottom:10px;}
  .tdis{font-family:var(--fd);font-size:var(--xl);font-weight:600;flex:1;}
  .timer-overlay{position:fixed;top:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;height:100%;background:rgba(0,0,0,0.75);z-index:500;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px;}
  .timer-big{font-family:var(--fd);font-size:72px;font-weight:700;color:#fff;line-height:1;letter-spacing:2px;}
  .timer-done{font-size:28px;color:#f0f5f0;font-family:var(--fd);font-weight:600;}
  .timer-btns{display:flex;gap:16px;}
  .timer-btn{padding:14px 32px;font-size:var(--sm);font-weight:600;border:none;border-radius:var(--r);cursor:pointer;letter-spacing:1px;font-family:var(--fb);}
  .timer-btn.start{background:var(--grn);color:#fff;}
  .timer-btn.pause{background:var(--gold);color:#fff;}
  .timer-btn.reset{background:rgba(255,255,255,0.15);color:#fff;}
  .cb{width:100%;padding:17px;background:var(--ink);color:var(--cr);font-family:var(--fb);font-size:var(--sm);font-weight:500;letter-spacing:2px;text-transform:uppercase;border:none;cursor:pointer;transition:background 0.2s;border-radius:var(--r);}
  .cb:hover{background:var(--ink2);}  .cb.rdy{background:var(--grn);}
  .vpc{background:#f0f8f0;border:1px solid #c0dcc0;border-radius:var(--r);padding:12px;margin-bottom:8px;}
  .vpct{font-size:var(--xs);letter-spacing:2px;text-transform:uppercase;color:var(--grn);margin-bottom:8px;font-weight:600;}
  .vpci{font-size:var(--sm);color:var(--ink);margin-bottom:4px;}
  .vpca{display:flex;gap:8px;margin-top:10px;}
  .mic{background:var(--ink);border:none;color:var(--cr);width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;transition:all 0.2s;border-radius:50%;}
  .mic:hover{background:var(--ink2);}  .mic.on{background:var(--red);animation:pulse 1s infinite;}

  /* SUMMARY */
  .sum-hero{background:var(--grn);padding:32px 24px;text-align:center;margin:-28px -24px 24px;}
  .sum-icon{font-size:48px;margin-bottom:12px;}
  .sum-title{font-family:var(--fd);font-size:var(--2xl);font-weight:600;color:white;margin-bottom:4px;}
  .sum-sub{font-size:var(--sm);color:rgba(255,255,255,0.7);}
  .sum-pb{background:linear-gradient(135deg,#c9a84c,#e8c76b);border-radius:var(--r);padding:14px 16px;margin-bottom:20px;display:flex;align-items:center;gap:12px;}
  .sum-pb-icon{font-size:20px;}
  .sum-pb-text{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .sum-ex{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:12px 14px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;}
  .sum-ex.skipped{opacity:0.5;}
  .sum-ex-name{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .sum-ex-data{font-size:var(--xs);color:var(--ink3);margin-top:2px;}
  .sum-ex-result{text-align:right;}
  .sum-ex-val{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .sum-ex-skip{font-size:var(--xs);color:var(--ink3);}
  .sum-stat{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:14px;text-align:center;}
  .sum-stat-n{font-family:var(--fd);font-size:var(--2xl);font-weight:600;color:var(--red);}
  .sum-stat-l{font-size:var(--xs);letter-spacing:1px;text-transform:uppercase;color:var(--ink3);margin-top:3px;}

  /* MESSAGING */
  .msg{display:flex;flex-direction:column;height:100vh;}
  .mhdr{padding:28px 24px 16px;border-bottom:1px solid var(--cr3);flex-shrink:0;}
  .mhdr h2{font-family:var(--fd);font-size:var(--2xl);font-weight:600;}
  .mhdr p{font-size:var(--xs);color:var(--ink3);margin-top:3px;}
  .tri{flex:1;padding:24px;display:flex;flex-direction:column;gap:10px;}
  .trl{font-size:var(--xs);letter-spacing:3px;text-transform:uppercase;color:var(--ink3);margin-bottom:4px;}
  .trb{background:var(--wh);border:1px solid var(--cr3);padding:16px;display:flex;align-items:center;gap:14px;cursor:pointer;transition:border-color 0.2s;text-align:left;width:100%;border-radius:var(--r);}
  .trb:hover{border-color:var(--ink);}
  .tri2{font-size:20px;flex-shrink:0;}  .trt{font-size:var(--sm);font-weight:600;color:var(--ink);}  .trd{font-size:var(--xs);color:var(--ink3);margin-top:2px;}
  .thr{flex:1;overflow-y:auto;padding:16px 24px 180px;display:flex;flex-direction:column;gap:14px;}
  .thr::-webkit-scrollbar{width:0;}
  .bw{display:flex;flex-direction:column;gap:3px;}
  .bw.me{align-items:flex-end;}  .bw.them{align-items:flex-start;}
  .bsn{font-size:12px;letter-spacing:1px;text-transform:uppercase;color:var(--ink3);padding:0 4px;}
  .bb{max-width:80%;padding:11px 14px;font-size:var(--sm);line-height:1.55;border-radius:12px;}
  .bb.me{background:var(--ink);color:var(--cr);border-bottom-right-radius:3px;}
  .bb.them{background:var(--wh);color:var(--ink);border:1px solid var(--cr3);border-bottom-left-radius:3px;}
  .bb.sys{background:var(--cr2);color:var(--ink2);border:1px solid var(--cr3);font-size:var(--xs);border-bottom-left-radius:3px;}
  .bt{font-size:12px;color:var(--ink3);padding:0 4px;}
  .mia{position:fixed;bottom:56px;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:var(--cr);border-top:1px solid var(--cr3);padding:10px 16px 14px;z-index:200;}
  .atr{display:flex;gap:7px;margin-bottom:8px;}
  .ab{background:var(--wh);border:1px solid var(--cr3);color:var(--ink3);padding:10px 14px;min-height:44px;display:inline-flex;align-items:center;font-family:var(--fb);font-size:13px;letter-spacing:1px;text-transform:uppercase;cursor:pointer;transition:all 0.2s;border-radius:var(--r);}
  .ab:hover{border-color:var(--ink);color:var(--ink);}
  .ir{display:flex;gap:8px;align-items:flex-end;}
  .mta{flex:1;background:var(--wh);border:1px solid var(--cr3);color:var(--ink);padding:11px 14px;font-family:var(--fb);font-size:var(--sm);outline:none;border-radius:20px;resize:none;transition:border-color 0.2s;}
  .mta:focus{border-color:var(--ink);}  .mta::placeholder{color:var(--cr3);}
  .snd{background:var(--ink);border:none;color:var(--cr);width:48px;height:48px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:18px;flex-shrink:0;border-radius:50%;}
  .snd:hover{background:var(--ink2);}
  .bbt{background:none;border:none;color:var(--ink3);font-family:var(--fb);font-size:13px;letter-spacing:2px;text-transform:uppercase;cursor:pointer;padding:14px 24px;display:block;min-height:44px;}
  .bbt:hover{color:var(--ink);}

  /* COACH */
  .ca{max-width:430px;margin:0 auto;min-height:100vh;background:var(--cr);}
  .cha{padding:28px 24px 16px;border-bottom:1px solid var(--cr3);}
  .che{font-size:var(--xs);letter-spacing:3px;text-transform:uppercase;color:var(--red);margin-bottom:4px;}
  .cht{font-family:var(--fd);font-size:var(--2xl);font-weight:600;color:var(--ink);}
  .chs{font-size:var(--xs);color:var(--ink3);margin-top:3px;}
  .cstat{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;padding:16px 24px;border-bottom:1px solid var(--cr3);}
  .csb{background:var(--wh);border:1px solid var(--cr3);padding:12px;border-radius:var(--r);text-align:center;}
  .csn{font-family:var(--fd);font-size:var(--2xl);font-weight:600;color:var(--red);line-height:1;}
  .csl{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:var(--ink3);margin-top:3px;}
  .ctabs{display:flex;border-bottom:1px solid var(--cr3);}
  .ctab{flex:1;padding:12px;font-family:var(--fb);font-size:var(--xs);letter-spacing:2px;text-transform:uppercase;background:none;border:none;color:var(--ink3);cursor:pointer;transition:color 0.2s;border-bottom:2px solid transparent;}
  .ctab.on{color:var(--ink);border-bottom-color:var(--red);}
  .cc{padding:20px 24px 100px;}
  .clc{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:16px;margin-bottom:10px;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:14px;}
  .clc:hover{border-color:var(--ink3);}
  .cla{width:42px;height:42px;background:var(--red);border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--fd);font-size:18px;color:white;flex-shrink:0;}
  .cln{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .clm{font-size:var(--xs);color:var(--ink3);margin-top:2px;}
  .dc{background:var(--wh);border:1px solid var(--cr3);border-left:3px solid var(--gold);border-radius:var(--r);padding:16px;margin-bottom:10px;}
  .de{font-size:12px;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:6px;font-weight:600;}
  .dcl{font-size:var(--xs);color:var(--ink3);margin-bottom:8px;}
  .dta{width:100%;background:var(--cr2);border:1px solid var(--cr3);color:var(--ink);padding:10px 12px;font-family:var(--fb);font-size:var(--sm);outline:none;border-radius:var(--r);resize:none;line-height:1.55;margin-bottom:10px;}
  .dta:focus{border-color:var(--ink);}
  .da{display:flex;gap:8px;}
  .cthb{background:none;border:none;color:var(--ink3);font-size:18px;cursor:pointer;padding:0;}
  .cthn{font-family:var(--fd);font-size:var(--xl);font-weight:600;color:var(--ink);}
  .cthr{overflow-y:auto;padding:16px 24px 180px;display:flex;flex-direction:column;gap:14px;}
  .cthr::-webkit-scrollbar{width:0;}
  .cmi{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:var(--cr);border-top:1px solid var(--cr3);padding:12px 16px 28px;z-index:200;}
  .cmr{display:flex;gap:8px;align-items:flex-end;}
  .cta{flex:1;background:var(--wh);border:1px solid var(--cr3);color:var(--ink);padding:11px 14px;font-family:var(--fb);font-size:var(--sm);outline:none;border-radius:20px;resize:none;transition:border-color 0.2s;}
  .cta:focus{border-color:var(--ink);}  .cta::placeholder{color:var(--cr3);}
  .csend{background:var(--red);border:none;color:white;width:48px;height:48px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:18px;border-radius:50%;flex-shrink:0;}

  /* STANDARDS */
  .std-card{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:14px;margin-bottom:10px;}
  .std-hdr{display:flex;align-items:center;gap:8px;margin-bottom:10px;cursor:pointer;}
  .std-icon{font-size:18px;}
  .std-name{font-size:var(--sm);font-weight:600;color:var(--ink);flex:1;}
  .std-toggle{font-size:12px;color:var(--ink3);}
  .std-row{display:flex;gap:8px;margin-bottom:6px;align-items:flex-start;}
  .std-level{font-size:11px;letter-spacing:2px;text-transform:uppercase;font-weight:700;width:75px;flex-shrink:0;padding-top:2px;}
  .std-level.f{color:var(--ink3);}  .std-level.d{color:var(--gold);}  .std-level.a{color:var(--grn);}
  .std-desc{font-size:var(--xs);color:var(--ink2);line-height:1.5;flex:1;}
  .std-bw{font-size:11px;color:var(--ink3);margin-top:2px;}

  /* PROGRESS / BALANCE */
  .bac{background:var(--wh);border:1px solid var(--cr3);padding:16px;margin-bottom:10px;border-radius:var(--r);}
  .ban{font-size:var(--sm);font-weight:600;margin-bottom:6px;}
  .bab{height:5px;background:var(--cr3);border-radius:3px;overflow:hidden;margin-bottom:6px;}
  .baf{height:100%;border-radius:3px;transition:width 0.5s;}
  .bam{display:flex;justify-content:space-between;font-size:var(--xs);color:var(--ink3);}
  .bas{font-size:12px;letter-spacing:1px;text-transform:uppercase;font-weight:600;}
  .bas.weak{color:var(--red);}  .bas.developing{color:var(--gold);}  .bas.strong{color:var(--grn);}

  /* PROFILE */
  .pav{width:72px;height:72px;background:var(--ink);border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--fd);font-size:28px;color:var(--cr);margin-bottom:14px;}
  .pnm{font-family:var(--fd);font-size:var(--2xl);font-weight:600;}
  .pmt{font-size:var(--sm);color:var(--ink3);margin-top:3px;}
  .ir2{display:flex;justify-content:space-between;align-items:center;padding:13px 0;border-bottom:1px solid var(--cr2);}
  .il{font-size:var(--xs);letter-spacing:1px;text-transform:uppercase;color:var(--ink3);}
  .iv{font-size:var(--sm);font-weight:500;}

  .op{display:flex;gap:5px;margin-bottom:28px;padding-top:20px;}
  .os{flex:1;height:2px;background:var(--cr3);border-radius:1px;transition:background 0.3s;}
  .os.done{background:var(--red);}  .os.act{background:var(--red);opacity:0.4;}
  .oq{font-family:var(--fd);font-size:var(--xl);font-weight:600;margin-bottom:6px;line-height:1.2;}
  .oo{font-size:var(--sm);color:var(--ink3);margin-bottom:20px;line-height:1.5;}
  .cg{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:20px;}
  .cp{background:var(--wh);border:1px solid var(--cr3);color:var(--ink3);font-size:var(--xs);letter-spacing:1px;text-transform:uppercase;padding:7px 13px;cursor:pointer;transition:all 0.2s;border-radius:20px;font-weight:500;}
  .cp:hover{border-color:var(--ink3);color:var(--ink);}  .cp.on{background:var(--ink);border-color:var(--ink);color:var(--cr);}

  .rc{background:var(--wh);border:1px solid var(--cr3);padding:16px;margin-bottom:8px;border-radius:var(--r);cursor:pointer;transition:border-color 0.2s;}
  .rc:hover{border-color:var(--ink3);}
  .rcat{font-size:12px;letter-spacing:2px;text-transform:uppercase;color:var(--red);margin-bottom:4px;font-weight:500;}
  .rt{font-size:var(--sm);font-weight:600;}
  .rd{font-size:var(--xs);color:var(--ink3);margin-top:3px;line-height:1.5;}

  .bn{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:var(--wh);border-top:1px solid var(--cr3);display:flex;z-index:100;}
  .nb{flex:1;padding:12px 6px 14px;display:flex;flex-direction:column;align-items:center;gap:4px;cursor:pointer;background:none;border:none;color:var(--ink3);transition:color 0.2s;}
  .nb.on{color:var(--red);}  .nb:hover{color:var(--ink);}
  .ni{font-size:24px;}  .nl{font-size:11px;letter-spacing:1px;text-transform:uppercase;font-weight:600;}
  .cbn{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:var(--wh);border-top:1px solid var(--cr3);display:flex;z-index:100;}
  .cnt{flex:1;padding:12px 6px 16px;display:flex;flex-direction:column;align-items:center;gap:4px;cursor:pointer;background:none;border:none;color:var(--ink3);transition:color 0.2s;}
  .cnt.on{color:var(--red);}  .cnt:hover{color:var(--ink);}
  .cni{font-size:24px;}  .cnl{font-size:11px;letter-spacing:1px;text-transform:uppercase;font-weight:600;}

  .notif{position:fixed;top:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:var(--ink);color:var(--cr);padding:14px 20px;font-size:var(--sm);z-index:999;display:flex;align-items:center;gap:10px;animation:sld 0.3s ease;}
  @keyframes sld{from{transform:translateX(-50%) translateY(-100%)}to{transform:translateX(-50%) translateY(0)}}
  .nd{width:8px;height:8px;background:var(--red);border-radius:50%;flex-shrink:0;}
  .med{max-width:80%;border-radius:12px;overflow:hidden;border:1px solid var(--cr3);}
  .med img{width:100%;display:block;}
  .uprog{background:var(--cr2);border:1px solid var(--cr3);border-radius:12px;padding:14px 16px;max-width:80%;}
  .upb{height:3px;background:var(--cr3);border-radius:2px;margin-top:8px;overflow:hidden;}
  .upf{height:100%;background:var(--ink);border-radius:2px;animation:upl 1.5s infinite;}
  @keyframes upl{0%{width:10%}50%{width:70%}100%{width:90%}}

  @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  .fu{animation:fadeUp 0.3s ease both;}  .fu2{animation:fadeUp 0.3s 0.06s ease both;}
  .fu3{animation:fadeUp 0.3s 0.12s ease both;}  .fu4{animation:fadeUp 0.3s 0.18s ease both;}
  @keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}

  /* PROGRAM BUILDER */
  .pb-day{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:16px;margin-bottom:12px;}
  .pb-day-hdr{font-family:var(--fd);font-size:var(--lg);font-weight:600;margin-bottom:10px;color:var(--red);}
  .pb-ex{background:var(--cr2);border:1px solid var(--cr3);border-radius:var(--r);padding:10px 12px;margin-bottom:0;position:relative;}
  .pb-ex-row{display:grid;grid-template-columns:1fr 2fr 1fr;gap:6px;align-items:end;}
  .pb-lbl{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:var(--ink3);margin-bottom:3px;}
  .pb-inp{width:100%;background:var(--wh);border:1px solid var(--cr3);color:var(--ink);padding:7px 8px;font-family:var(--fb);font-size:var(--xs);outline:none;border-radius:var(--r);}
  .pb-inp:focus{border-color:var(--ink);}
  .pb-rm{position:absolute;top:8px;right:8px;background:none;border:none;color:var(--ink3);cursor:pointer;font-size:14px;padding:2px 4px;}
  .pb-rm:hover{color:var(--red);}
  .pb-ss-wrap{display:flex;justify-content:center;align-items:center;height:18px;position:relative;z-index:2;}
  .pb-ss-btn{width:34px;height:34px;border-radius:50%;background:var(--cr);border:1px solid var(--cr3);color:var(--ink3);cursor:pointer;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;padding:0;}
  .pb-ss-btn.active{background:var(--ink);color:var(--wh);border-color:var(--ink);}
  .pb-toggle{display:flex;gap:5px;margin-top:7px;}
  .pb-tbtn{font-size:11px;letter-spacing:1px;text-transform:uppercase;padding:6px 12px;border-radius:99px;border:1px solid var(--cr3);background:var(--cr);color:var(--ink3);cursor:pointer;font-family:var(--fb);}
  .pb-tbtn.on{background:var(--ink);color:var(--wh);border-color:var(--ink);}
  .pb-ex-gap{height:8px;}
  .pb-rm:hover{color:var(--red);}
  .pb-sel{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;}
  .pb-opt{padding:6px 14px;background:var(--wh);border:1px solid var(--cr3);border-radius:20px;font-size:var(--xs);cursor:pointer;transition:all 0.15s;font-family:var(--fb);}
  .pb-opt.on{background:var(--ink);color:var(--cr);border-color:var(--ink);}

  /* EXERCISE LIBRARY */
  .ex-card{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:14px 16px;margin-bottom:8px;cursor:pointer;transition:border-color 0.2s;}
  .ex-card:hover{border-color:var(--ink3);}
  .ex-card.open{border-color:var(--ink);}
  .ex-name{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .ex-meta{font-size:var(--xs);color:var(--ink3);margin-top:3px;}
  .ex-cues{background:var(--cr2);border-radius:var(--r);padding:10px 12px;margin-top:10px;font-size:var(--xs);color:var(--ink2);line-height:1.7;}

  /* MONTH CALENDAR */
  .month-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:3px;}
  .month-cal-hdr{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:var(--ink3);text-align:center;padding:4px 0 8px;}
  .month-cal-cell{aspect-ratio:1;display:flex;flex-direction:column;align-items:center;justify-content:center;border-radius:50%;font-size:13px;font-weight:500;cursor:pointer;border:1px solid var(--cr3);transition:all 0.15s;position:relative;background:transparent;color:var(--ink);}
  .month-cal-cell.other-month{color:var(--cr3);cursor:default;}
  .month-cal-cell.rest{cursor:default;pointer-events:none;}
  .month-cal-cell.today{background:var(--ink);color:white;}
  .month-cal-cell .cal-dot{width:5px;height:5px;border-radius:50%;margin-top:2px;}
  .month-cal-cell .cal-dot.dot-done{background:var(--grn);}
  .month-cal-cell .cal-dot.dot-missed{background:var(--red);}
  .month-cal-cell .cal-dot.dot-today{background:white;}
  .month-cal-cell .cal-dot.dot-scheduled{background:transparent;border:1px solid var(--cr3);}

  /* RANGE SLIDER */
  .pb-range{width:100%;accent-color:var(--red);cursor:pointer;height:4px;margin:8px 0;}
  .range-val{text-align:center;font-family:var(--fd);font-size:var(--xl);font-weight:600;color:var(--red);}

  /* TRAINING BLOCK SECTION */
  .pb-block{background:var(--cr);border:1px solid var(--cr3);border-radius:var(--r);padding:12px;margin-bottom:10px;}
  .pb-block-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}

  /* DAYS +/- CONTROL */
  .day-ctrl{display:flex;align-items:center;gap:10px;}
  .day-ctrl-val{font-family:var(--fd);font-size:var(--xl);font-weight:600;color:var(--red);min-width:24px;text-align:center;}

  /* COACH CALENDAR ACTION SHEET */
  .cal-action-sheet{background:var(--wh);border:1px solid var(--ink);border-radius:var(--r);padding:8px;margin-top:4px;box-shadow:var(--sh);}
  .cal-action-btn{display:block;width:100%;text-align:left;background:none;border:none;padding:10px 12px;font-family:var(--fb);font-size:var(--sm);color:var(--ink);cursor:pointer;border-radius:var(--r);}
  .cal-action-btn:hover{background:var(--cr2);}
  .cal-plus{position:absolute;top:1px;right:1px;width:20px;height:20px;background:var(--gold);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;color:white;font-weight:700;cursor:pointer;border:none;}

  /* CALENDAR TOGGLE */
  .cal-toggle{display:flex;gap:4px;}
  .cal-toggle-btn{padding:10px 16px;min-height:44px;display:inline-flex;align-items:center;font-family:var(--fb);font-size:var(--xs);letter-spacing:1px;text-transform:uppercase;border:1px solid var(--cr3);background:transparent;color:var(--ink3);cursor:pointer;border-radius:var(--r);transition:all 0.15s;}
  .cal-toggle-btn.on{background:var(--ink);color:var(--cr);border-color:var(--ink);}

  /* COACH CALENDAR POPUP */
  .cal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:499;}
  .cal-popup{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:var(--cr);border-top:1px solid var(--cr3);padding:24px 24px 40px;z-index:500;border-radius:16px 16px 0 0;}
  .cal-popup-date{font-size:var(--xs);letter-spacing:3px;text-transform:uppercase;color:var(--ink3);margin-bottom:8px;}
  .cal-popup-status{font-family:var(--fd);font-size:var(--xl);font-weight:600;margin-bottom:4px;}
  .cal-popup-workout{font-size:var(--sm);color:var(--ink3);margin-bottom:16px;}
  .cal-popup-actions{display:flex;flex-direction:column;gap:8px;margin-top:16px;padding-top:16px;border-top:1px solid var(--cr3);}

  /* NOTIF BANNER */
  .notif-banner{background:var(--cr2);border:1px solid var(--cr3);border-radius:var(--r);padding:12px 14px;display:flex;align-items:center;gap:10px;margin-bottom:16px;}
  .notif-banner-text{flex:1;font-size:var(--xs);color:var(--ink2);}

  /* PROGRAMS TAB */
  .prog-card{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:14px 16px;margin-bottom:8px;cursor:pointer;transition:border-color 0.2s;}
  .prog-card:hover{border-color:var(--ink3);}
  .prog-card.open{border-color:var(--ink);}
  .prog-card-name{font-size:var(--sm);font-weight:600;color:var(--ink);}
  .prog-card-meta{font-size:var(--xs);color:var(--ink3);margin-top:3px;}

  /* STREAK */
  .streak-fire{font-size:16px;}

  /* REST DAY */
  .rest-card{background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);padding:20px;margin-bottom:16px;}
  .rest-card-title{font-family:var(--fd);font-size:var(--xl);font-weight:600;margin-bottom:8px;}
  .rest-card-tip{font-size:var(--sm);color:var(--ink2);line-height:1.6;margin-bottom:12px;font-style:italic;}
  .rest-scripture{font-size:var(--xs);color:var(--ink3);line-height:1.5;border-left:2px solid var(--red);padding-left:10px;margin-bottom:14px;}

  /* FEEL RATING */
  .feel-btns{display:flex;gap:8px;margin-bottom:12px;}
  .feel-btn{flex:1;padding:12px 8px;background:var(--wh);border:1px solid var(--cr3);border-radius:var(--r);cursor:pointer;font-size:var(--sm);text-align:center;transition:all 0.15s;font-family:var(--fb);}
  .feel-btn.on{background:var(--ink);color:var(--cr);border-color:var(--ink);}

  /* BILLING BADGE */
  .bill-dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:5px;flex-shrink:0;}
  .bill-badge{display:inline-flex;align-items:center;padding:4px 10px;border-radius:10px;font-size:12px;letter-spacing:1px;text-transform:uppercase;font-weight:500;}
  .bill-active{background:#e8f5e8;color:var(--grn);}
  .bill-trial{background:#fdf3de;color:var(--gold);}
  .bill-inactive{background:var(--cr2);color:var(--ink3);}
  .bill-pending{background:#e8eef5;color:#3a6b8a;}

  /* CLIENT FILTER */
  .cli-filter{display:flex;gap:6px;margin-bottom:12px;flex-wrap:wrap;}
  .cli-filter-btn{padding:10px 16px;min-height:44px;display:inline-flex;align-items:center;font-size:var(--xs);border:1px solid var(--cr3);background:transparent;color:var(--ink3);cursor:pointer;border-radius:20px;transition:all 0.15s;font-family:var(--fb);}
  .cli-filter-btn.on{background:var(--ink);color:var(--cr);border-color:var(--ink);}

  /* INTAKE CHIPS */
  .intake-chip{display:inline-flex;padding:3px 10px;border-radius:20px;font-size:var(--xs);margin:3px 3px 3px 0;}
  .intake-chip.injury{background:#fff0f0;color:var(--red);border:1px solid #ffd0d0;}
  .intake-chip.goal{background:#f0f8f0;color:var(--grn);border:1px solid #c0dcc0;}

  /* CHECKLIST */
  .checklist-item{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--cr2);cursor:pointer;}
  .checklist-item:last-child{border-bottom:none;}
  .check-circle{width:20px;height:20px;border-radius:50%;border:1.5px solid var(--cr3);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .check-circle.done{background:var(--grn);border-color:var(--grn);}

  /* SPARKLINE */
  .sparkline-wrap{padding:8px 0;}
  .sparkline-labels{display:flex;justify-content:space-between;font-size:var(--xs);color:var(--ink3);margin-top:4px;}

  /* LOADING */
  @keyframes spin{to{transform:rotate(360deg)}}
  .spin{width:22px;height:22px;border:2px solid var(--cr3);border-top-color:var(--ink3);border-radius:50%;animation:spin 0.7s linear infinite;flex-shrink:0;}
  .load-center{display:flex;justify-content:center;align-items:center;padding:80px 0;}
  .pull-hint{text-align:center;padding:6px 0 2px;font-size:var(--xs);color:var(--ink3);}
  .err-center{text-align:center;padding:60px 0 20px;color:var(--ink3);}
`;

export default function App() {
  // AUTH
  const [screen, setScreen] = useState("splash");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [uname, setUname] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState(null);
  const [pullDistance, setPullDistance] = useState(0);
  const pullStartY = useRef(0);
  // CLIENT
  const [navTab, setNavTab] = useState("home");
  const [triage, setTriage] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [techMsgs, setTechMsgs] = useState([{ id: 1, from: "sys", sender: "Support", text: "Hi! What's going on?", time: "Now" }]);
  const [msgTxt, setMsgTxt] = useState("");
  const [obStep, setObStep] = useState(0);
  const [dob, setDob] = useState("");
  const [bwVal, setBwVal] = useState("");
  const [goals, setGoals] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [uploading, setUploading] = useState(false);
  const [notif, setNotif] = useState(null);
  const [prog, setProg] = useState(null);
  const [activeEx, setActiveEx] = useState(null);
  const [checked, setChecked] = useState({});
  const [setData, setSetData] = useState({});
  const [viewingDate, setViewingDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [viewingWorkout, setViewingWorkout] = useState(null);
  const [completedDates, setCompletedDates] = useState([]);
  const [completedSessionLog, setCompletedSessionLog] = useState(null);
  const [verse, setVerse] = useState(null);
  const [clientRecord, setClientRecord] = useState(null);
  const [exerciseHistory, setExerciseHistory] = useState({});
  const [sessionSummary, setSessionSummary] = useState(null);
  const [myRecentSessions, setMyRecentSessions] = useState([]);
  const [expandedStd, setExpandedStd] = useState(null);
  const [progressTab, setProgressTab] = useState("marks");
  // COACH
  const [coachTab, setCoachTab] = useState("clients");
  const [selClient, setSelClient] = useState(null);
  const [cpTab, setCpTab] = useState("overview");
  const [clientList, setClientList] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editDraft, setEditDraft] = useState({});
  const [clientMsgs, setClientMsgs] = useState([]);
  const [coachDrafts, setCoachDrafts] = useState({});
  const [unreadClientIds, setUnreadClientIds] = useState(new Set());
  const [coachConversations, setCoachConversations] = useState([]);
  const [coachMsgOpenClient, setCoachMsgOpenClient] = useState(null);
  const [coachMsgThread, setCoachMsgThread] = useState([]);
  const [clientSessions, setClientSessions] = useState([]);
  const [clientProg, setClientProg] = useState(null);
  const [fullSessionHistory, setFullSessionHistory] = useState([]);
  const [mySelSession, setMySelSession] = useState(null);
  const [editingBw, setEditingBw] = useState(false);
  const [newBwVal, setNewBwVal] = useState("");
  const [selSession, setSelSession] = useState(null);
  const [logClient, setLogClient] = useState(null);
  const [logWorkout, setLogWorkout] = useState(null);
  const [sesLogs, setSesLogs] = useState({});
  const [sesNotes, setSesNotes] = useState("");
  const [sesTx, setSesTx] = useState("");
  const [sesListen, setSesListen] = useState(false);
  const [sesParsing, setSesParsing] = useState(false);
  const [activeLogEx, setActiveLogEx] = useState(null);
  // PROGRAM BUILDER
  const [pbProgName, setPbProgName] = useState("");
  const [pbDaysPerWeek, setPbDaysPerWeek] = useState(3);
  const [pbWeeks, setPbWeeks] = useState(4);
  const [pbDays, setPbDays] = useState([]);
  const [pbSaved, setPbSaved] = useState(null);
  const [pbAssignClient, setPbAssignClient] = useState("");
  const [pbVoiceListening, setPbVoiceListening] = useState(false);
  const [pbVoiceParsing, setPbVoiceParsing] = useState(false);
  // STRUCTURAL BALANCE
  const [balanceData, setBalanceData] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [clientBalanceData, setClientBalanceData] = useState([]);
  const [clientBalanceLoading, setClientBalanceLoading] = useState(false);
  // PASSWORD RESET
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  // MULTIPLE PROGRAMS
  const [allPrograms, setAllPrograms] = useState([]);
  const [allBlocks, setAllBlocks] = useState([]);
  const [showAssignProgram, setShowAssignProgram] = useState(false);
  const [assignProgramId, setAssignProgramId] = useState("");
  // LIBRARY
  const [librarySection, setLibrarySection] = useState("programs");
  const [libraryNewChoice, setLibraryNewChoice] = useState(false);
  const [deleteConfirmProgId, setDeleteConfirmProgId] = useState(null);
  const [buildingBlock, setBuildingBlock] = useState(false);
  const [blockDraft, setBlockDraft] = useState({ id: null, name: "", goal: "", programs: [] });
  const [viewingProgram, setViewingProgram] = useState(null);
  const [programEditDraft, setProgramEditDraft] = useState(null);
  const [editingProgram, setEditingProgram] = useState(false);
  const [assigningProgram, setAssigningProgram] = useState(null);
  const [assignStartDate, setAssignStartDate] = useState(new Date().toLocaleDateString("en-CA"));
  // EXERCISE LIBRARY
  const [exercises, setExercises] = useState([]);
  const [exerciseSearch, setExerciseSearch] = useState("");
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [newExercise, setNewExercise] = useState({ name: "", muscle_groups: "", tempo: "", video_url: "", cue_1: "", cue_2: "", cue_3: "" });
  // PROGRAM BUILDER — TRAINING BLOCKS
  const [pbBlockName, setPbBlockName] = useState("");
  const [pbBlockGoal, setPbBlockGoal] = useState("");
  const [pbSequenceOrder, setPbSequenceOrder] = useState(1);
  // MONTH CALENDAR
  const [monthView, setMonthView] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  // COACH CLIENT CALENDAR
  const [coachClientCalMonth, setCoachClientCalMonth] = useState(new Date());
  const [coachClientCompletedDates, setCoachClientCompletedDates] = useState([]);
  const [coachClientProg, setCoachClientProg] = useState(null);
  const [coachCalActionDay, setCoachCalActionDay] = useState(null);
  const [coachCalAssignProg, setCoachCalAssignProg] = useState(false);
  const [coachCalAssignProgId, setCoachCalAssignProgId] = useState("");
  const [selClientRecord, setSelClientRecord] = useState(null);
  // CALENDAR VIEWS
  const [calView, setCalView] = useState("week");
  const [coachCalView, setCoachCalView] = useState("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [coachWeekOffset, setCoachWeekOffset] = useState(0);
  // COACH CALENDAR POPUP
  const [coachCalDay, setCoachCalDay] = useState(null);
  const [coachCalPopup, setCoachCalPopup] = useState(false);
  const [coachCalAction, setCoachCalAction] = useState(false);
  const [coachCalDayWorkout, setCoachCalDayWorkout] = useState(null);
  const [coachCalAssignMode, setCoachCalAssignMode] = useState(null);
  const [coachCalSessionMode, setCoachCalSessionMode] = useState(null); // null | "confirm-delete" | "move" | "edit-notes" | "move-workout" | "confirm-remove"
  const [coachCalMoveDate, setCoachCalMoveDate] = useState("");
  const [coachCalEditNotes, setCoachCalEditNotes] = useState("");
  const [coachClientPreview, setCoachClientPreview] = useState(null);
  // COACH NOTES
  const [coachPrivateNotes, setCoachPrivateNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  // PUSH NOTIFICATION BANNER
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  // FEATURE 1 — HISTORY BY EXERCISE
  const [historyView, setHistoryView] = useState("all");
  const [exerciseHistoryData, setExerciseHistoryData] = useState({});
  const [expandedHistoryEx, setExpandedHistoryEx] = useState(null);
  // FEATURE 2 — REST DAY
  const [showMobilityFlow, setShowMobilityFlow] = useState(false);
  const [mobilityTimers, setMobilityTimers] = useState({});
  // FEATURE 3 — SESSION FEEL
  const [sessionFeel, setSessionFeel] = useState("");
  const [sessionClientNote, setSessionClientNote] = useState("");
  const [lastSessionId, setLastSessionId] = useState(null);
  const [editingSummaryEx, setEditingSummaryEx] = useState(null);
  const [editSummaryData, setEditSummaryData] = useState({});
  const [selSessionWorkoutExercises, setSelSessionWorkoutExercises] = useState([]);
  // FEATURE 4 — BW HISTORY
  const [bwHistory, setBwHistory] = useState([]);
  // FEATURE 6 — BIBLE VERSE MANAGER
  const [verses, setVerses] = useState([]);
  const [showAddVerse, setShowAddVerse] = useState(false);
  const [newVerse, setNewVerse] = useState({ week_of: "", verse: "", reference: "" });
  const [editingVerse, setEditingVerse] = useState(null);
  // FEATURE 7 — RESOURCES MANAGER
  const [resourcesList, setResourcesList] = useState([]);
  const [showAddResource, setShowAddResource] = useState(false);
  const [newResource, setNewResource] = useState({ category: "Methodology", title: "", description: "", url: "" });
  // FEATURE 8 — BILLING STATUS
  const [clientFilter, setClientFilter] = useState("all");
  // FEATURE 9 — CHECKLIST
  const [dismissedChecklists, setDismissedChecklists] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissed_checklists") || "{}"); } catch { return {}; }
  });
  // MARKS
  const [clientBestPerformances, setClientBestPerformances] = useState({});
  const [expandedMark, setExpandedMark] = useState(null);
  // LOADED WORKOUTS (for calendar missed-day gating)
  const [loadedWorkouts, setLoadedWorkouts] = useState([]);
  const [coachClientWorkouts, setCoachClientWorkouts] = useState([]);
  // STANDALONE WORKOUTS (Library — no program_id)
  const [standaloneWorkouts, setStandaloneWorkouts] = useState([]);
  const [viewingStandaloneWorkout, setViewingStandaloneWorkout] = useState(null);
  const [standaloneWorkoutExercises, setStandaloneWorkoutExercises] = useState([]);
  const [standaloneExerciseDraft, setStandaloneExerciseDraft] = useState({});
  const [assigningStandaloneWorkout, setAssigningStandaloneWorkout] = useState(null);
  const [deleteConfirmWorkoutId, setDeleteConfirmWorkoutId] = useState(null);
  // BROADCAST
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [broadcastTargets, setBroadcastTargets] = useState("all");
  const [selectedBroadcastClients, setSelectedBroadcastClients] = useState([]);
  // WORKOUT BUILDER
  const [workoutDraft, setWorkoutDraft] = useState({ name: "", focus: "", exercises: [] });
  const [workoutBuilderListening, setWorkoutBuilderListening] = useState(false);
  const [workoutBuilderParsing, setWorkoutBuilderParsing] = useState(false);
  const [workoutBuilderSaved, setWorkoutBuilderSaved] = useState(false);

  const thrRef = useRef(null);
  const cThrRef = useRef(null);
  const fileRef = useRef(null);
  const fileType = useRef(null);
  const [timerDuration, setTimerDuration] = useState(60);
  const [showTimerOverlay, setShowTimerOverlay] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const timer = useTimer(timerDuration);

  const isLarge = calcAge(dob) >= 45 || (profile?.age >= 45);
  const isCoach = profile?.role === "coach";
  const go = (s, t) => { setScreen(s); if (t) setNavTab(t); };
  const showNotif = (m) => { setNotif(m); setTimeout(() => setNotif(null), 4000); };
  const todayStr = new Date().toLocaleDateString("en-CA");
  const PULL_THRESHOLD = 60;
  const onPullStart = (e) => { pullStartY.current = e.touches[0].clientY; };
  const onPullMove = (e) => {
    if (e.currentTarget.scrollTop > 0) { if (pullDistance > 0) setPullDistance(0); return; }
    const dy = e.touches[0].clientY - pullStartY.current;
    if (dy > 0) setPullDistance(Math.min(dy, PULL_THRESHOLD + 20));
  };
  const onPullEnd = async () => {
    if (pullDistance >= PULL_THRESHOLD && !clientLoading) {
      setPullDistance(0);
      await Promise.all([loadClientData(), loadVerse()]);
    } else {
      setPullDistance(0);
    }
  };

  const workout = viewingWorkout ? {
    name: viewingWorkout.name,
    focus: viewingWorkout.focus,
    duration: "55 min",
    exercises: (viewingWorkout.workout_exercises || [])
      .sort((a, b) => a.order_index - b.order_index)
  } : null;

  const allDone = (workout?.exercises?.length ?? 0) > 0;

  // Superset grouping
  const groupExercises = (exercises) => {
    const result = [];
    const seen = new Set();
    exercises.forEach((ex, i) => {
      const g = ex.superset_group;
      if (g && !seen.has(g)) {
        seen.add(g);
        const group = exercises.filter(e => e.superset_group === g);
        result.push({ type: "group", group: g, exercises: group.map(e => ({ ...e, globalIdx: exercises.indexOf(e) })) });
      } else if (!g) {
        result.push({ type: "single", ex: { ...ex, globalIdx: i } });
      }
    });
    return result;
  };

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Feature 4: catch Supabase password-recovery token in URL hash
    const hashParams = new URLSearchParams(window.location.hash.replace("#", "?"));
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const type = hashParams.get("type");
    if (type === "recovery" && accessToken) {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken || "" })
        .then(() => setScreen("reset-password"));
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        try {
          const stored = localStorage.getItem(`completedDates_${session.user.id}`);
          if (stored) setCompletedDates(JSON.parse(stored));
        } catch (e) {}
        loadProfile(session.user.id);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((ev) => {
      if (ev === "PASSWORD_RECOVERY") setScreen("reset-password");
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
    if (!appId) return;
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      await OneSignal.init({ appId, notifyButton: { enable: false }, autoRegister: false });
    });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
    if (!appId) return;
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try { await OneSignal.login(user.id); } catch (e) {}
    });
  }, [user?.id]);

  useEffect(() => {
    if (!user || !profile || profile.role === "coach") return;
    loadClientData();
    loadVerse();
  }, [user, profile]);

  useEffect(() => {
    if (!viewingWorkout || !clientRecord) return;
    loadExerciseHistory();
  }, [viewingWorkout, clientRecord]);

  useEffect(() => {
    if (!viewingWorkout || !completedDates.includes(viewingDate)) return;
    const allChecked = {};
    (viewingWorkout.workout_exercises || []).forEach((_, i) => { allChecked[i] = true; });
    setChecked(allChecked);
  }, [viewingWorkout, viewingDate, completedDates]);

  useEffect(() => {
    if (progressTab !== "history" || !clientRecord) return;
    (async () => {
      const { data } = await supabase.from("session_logs")
        .select("id, completed_at, feel_rating, client_notes, workouts(name,focus), set_logs(set_number, weight, reps_completed, workout_exercise_id, workout_exercises(exercises(name)))")
        .eq("client_id", clientRecord.id)
        .order("completed_at", { ascending: false })
        .limit(20);
      if (data) setFullSessionHistory(data);
    })();
  }, [progressTab, clientRecord?.id]);

  // Feature 1: load by-exercise history
  useEffect(() => {
    if (progressTab !== "history" || historyView !== "byExercise" || !clientRecord) return;
    (async () => {
      const { data: sesIds } = await supabase.from("session_logs")
        .select("id, completed_at").eq("client_id", clientRecord.id)
        .order("completed_at", { ascending: false });
      if (!sesIds?.length) return;
      const { data: logs } = await supabase.from("set_logs")
        .select("weight, reps_completed, set_number, session_log_id, workout_exercises(exercises(name))")
        .in("session_log_id", sesIds.map(s => s.id));
      if (!logs) return;
      const dateMap = Object.fromEntries(sesIds.map(s => [s.id, s.completed_at]));
      const grouped = {};
      logs.forEach(sl => {
        const name = sl.workout_exercises?.exercises?.name;
        if (!name) return;
        if (!grouped[name]) grouped[name] = [];
        grouped[name].push({ ...sl, completed_at: dateMap[sl.session_log_id] });
      });
      Object.values(grouped).forEach(arr => arr.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at)));
      setExerciseHistoryData(grouped);
    })();
  }, [progressTab, historyView, clientRecord?.id]);

  useEffect(() => {
    if (!clientRecord) return;
    loadBalanceData();
  }, [clientRecord?.id, completedDates.length]);

  useEffect(() => {
    if (progressTab !== "marks" || !clientRecord) return;
    loadMarksData();
  }, [progressTab, clientRecord?.id]);

  useEffect(() => {
    if (!user || !profile || profile.role === "coach") return;
    if ('Notification' in window && Notification.permission === 'default') {
      const asked = localStorage.getItem(`notif_requested_${user.id}`);
      if (!asked) setShowNotifBanner(true);
    }
  }, [user, profile]);

  // Feature 4: load BW history when on profile screen
  useEffect(() => {
    if (screen !== "profile" || !user) return;
    // SQL needed: create table if not exists body_weight_log (id uuid primary key default gen_random_uuid(), client_id uuid references profiles(id), weight numeric, logged_at timestamptz default now());
    // SQL needed: create policy "Users can manage own weight log" on body_weight_log for all using (client_id = auth.uid());
    (async () => {
      const { data } = await supabase.from("body_weight_log")
        .select("weight, logged_at").eq("client_id", user.id)
        .order("logged_at", { ascending: true }).limit(30);
      if (data) setBwHistory(data);
    })();
  }, [screen, user?.id]);

  // Feature 6 & 7: load verses and resources when coach settings tab opens
  useEffect(() => {
    if (!isCoach || coachTab !== "settings") return;
    (async () => {
      const { data: vData } = await supabase.from("bible_verses").select("*").order("week_of", { ascending: true });
      if (vData) setVerses(vData);
      const { data: rData } = await supabase.from("resources").select("*").order("created_at", { ascending: true });
      if (rData) setResourcesList(rData);
    })();
  }, [isCoach, coachTab]);

  // Feature 7: load resources for client resources screen
  useEffect(() => {
    if (screen !== "resources" || isCoach) return;
    // SQL needed: create table if not exists resources (id uuid primary key default gen_random_uuid(), category text, title text, description text, url text, created_at timestamptz default now());
    (async () => {
      const { data } = await supabase.from("resources").select("*").order("created_at", { ascending: true });
      if (data?.length) setResourcesList(data);
      else setResourcesList(RESOURCES.map((r, i) => ({ ...r, id: r.id?.toString() || String(i) })));
    })();
  }, [screen]);

  useEffect(() => {
    if (!viewingDate || !clientRecord || !completedDates.includes(viewingDate)) {
      setCompletedSessionLog(null);
      return;
    }
    (async () => {
      const { data: ses } = await supabase.from("session_logs")
        .select("*, set_logs(*, workout_exercises(exercises(*)))")
        .eq("client_id", clientRecord.id)
        .gte("completed_at", viewingDate + "T00:00:00")
        .lte("completed_at", viewingDate + "T23:59:59")
        .maybeSingle();
      setCompletedSessionLog(ses || null);
    })();
  }, [viewingDate, clientRecord?.id, completedDates.length]);

  // ── DATA LOADING ──────────────────────────────────────────────────────────
  const loadProfile = async (uid) => {
    const { data } = await supabase.from("profiles").select("*").eq("id", uid).maybeSingle();
    if (data) { setProfile(data); setScreen(data.role === "coach" ? "coach-home" : "home"); }
  };

  const loadVerse = async () => {
    try {
      const today = new Date();
      const mon = new Date(today);
      mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      const monStr = mon.toISOString().split("T")[0];
      let { data } = await supabase.from("bible_verses").select("*").eq("week_of", monStr).maybeSingle();
      if (!data) {
        const r = await supabase.from("bible_verses").select("*").order("week_of", { ascending: false }).limit(1).maybeSingle();
        data = r.data;
      }
      if (data) setVerse(data);
    } catch (e) {}
  };

  const loadClientData = async () => {
    if (!user) return;
    setClientLoading(true);
    setClientError(null);
    try {
      const { data: cr, error: crErr } = await supabase.from("clients").select("id").eq("profile_id", user.id).maybeSingle();
      if (crErr) throw crErr;
      if (!cr) { setClientLoading(false); return; }
      setClientRecord(cr);
      const { data: p } = await supabase.from("programs").select("id, name, client_id, active, start_date, weeks, days_per_week, created_at, block_id, sequence_order").eq("client_id", cr.id).eq("active", true).maybeSingle();
      setProg(p || null);
      let allWkts = [];
      if (p) {
        const { data: wktData } = await supabase.from("workouts").select("id, day_number, day_of_week, program_id").eq("program_id", p.id);
        allWkts = wktData || [];
        setLoadedWorkouts(allWkts);
      } else {
        setLoadedWorkouts([]);
      }
      const { data: ses } = await supabase
        .from("session_logs")
        .select("completed_at")
        .eq("client_id", cr.id);
      if (ses) {
        const dbDates = ses.map(s => s.completed_at.split("T")[0]);
        let localDates = [];
        try {
          const stored = localStorage.getItem(`completedDates_${user.id}`);
          if (stored) localDates = JSON.parse(stored);
        } catch (e) {}
        const merged = [...new Set([...dbDates, ...localDates])];
        setCompletedDates(merged);
        localStorage.setItem(`completedDates_${user.id}`, JSON.stringify(merged));
      }
      const { data: recentSes } = await supabase
        .from("session_logs")
        .select("id, completed_at, workouts(name,focus), set_logs(set_number, weight, reps_completed, workout_exercise_id, workout_exercises(exercises(name)))")
        .eq("client_id", cr.id)
        .order("completed_at", { ascending: false })
        .limit(20);
      if (recentSes) {
        setMyRecentSessions(recentSes.slice(0, 5));
        setFullSessionHistory(recentSes);
      }
      if (p) await loadWorkoutForDate(p, todayStr, allWkts || []);
    } catch (e) {
      console.error("[loadClientData]", e);
      setClientError("Something went wrong — pull to refresh");
    } finally {
      setClientLoading(false);
    }
  };

  const loadWorkoutForDate = async (p, dateStr, allWorkouts) => {
    const d = new Date(dateStr + "T12:00:00");
    const jsDay = d.getDay();
    const ourDOW = jsDay === 0 ? 7 : jsDay;
    const { data: wktByDOW } = await supabase.from("workouts")
      .select("*, workout_exercises(*, exercises(*))")
      .eq("program_id", p.id).eq("day_of_week", ourDOW).maybeSingle();
    if (wktByDOW) {
      setViewingWorkout(wktByDOW); setViewingDate(dateStr); setChecked({}); setSetData({}); setActiveEx(null); return;
    }
    const wkts = allWorkouts || loadedWorkouts;
    const dayMap = buildDayMapFromWorkouts(wkts) || getDayMapForProgram(p.days_per_week || 5);
    const dayNum = getProgramDay(p.start_date, dateStr, dayMap);
    if (!dayNum) { setViewingWorkout(null); setViewingDate(dateStr); setChecked({}); setSetData({}); setActiveEx(null); return; }
    const { data: wkt } = await supabase.from("workouts")
      .select("*, workout_exercises(*, exercises(*))")
      .eq("program_id", p.id).eq("day_number", dayNum).maybeSingle();
    setViewingWorkout(wkt || null);
    setViewingDate(dateStr);
    setChecked({}); setSetData({}); setActiveEx(null);
  };

  const loadExerciseHistory = async () => {
    if (!clientRecord || !viewingWorkout) return;
    const weIds = (viewingWorkout.workout_exercises || []).map(we => we.id);
    if (weIds.length === 0) return;
    const { data: sessionIds } = await supabase
      .from("session_logs")
      .select("id")
      .eq("client_id", clientRecord.id)
      .order("completed_at", { ascending: false });
    if (!sessionIds || sessionIds.length === 0) return;
    const sesIdList = sessionIds.map(s => s.id);
    const { data } = await supabase
      .from("set_logs")
      .select("workout_exercise_id, weight, reps_completed, set_number, session_log_id")
      .in("workout_exercise_id", weIds)
      .in("session_log_id", sesIdList);
    const history = {};
    if (data) {
      // Group by (workout_exercise_id, session_log_id), keep most recent session,
      // prefer set 3 within that session; fall back to highest set number
      const byWeId = {};
      data.forEach(sl => {
        const weId = sl.workout_exercise_id;
        if (!byWeId[weId]) byWeId[weId] = {};
        const sesIdx = sesIdList.indexOf(sl.session_log_id);
        if (sesIdx === -1) return;
        if (!byWeId[weId][sesIdx]) byWeId[weId][sesIdx] = [];
        byWeId[weId][sesIdx].push(sl);
      });
      Object.entries(byWeId).forEach(([weId, bySes]) => {
        const indices = Object.keys(bySes).map(Number).sort((a, b) => a - b);
        const recentSets = bySes[indices[0]];
        const set3 = recentSets.find(s => s.set_number === 3);
        const chosen = set3 || recentSets.reduce((best, s) => s.set_number > best.set_number ? s : best, recentSets[0]);
        history[weId] = { weight: chosen.weight, reps: chosen.reps_completed };
      });
    }
    setExerciseHistory(history);
  };

  // Calendar
  const calDays = () => {
    if (!prog?.start_date) return [];
    const days = [];
    console.log("[calDays] prog:", prog?.name, "days_per_week:", prog?.days_per_week);
    const dayMap = buildDayMapFromWorkouts(loadedWorkouts) || getDayMapForProgram(prog.days_per_week || 5);
    console.log("[calDays] dayMap:", dayMap);
    console.log("[calDays] loadedWorkouts day_numbers:", loadedWorkouts.map(w => w.day_number));
    const startDate = new Date(prog.start_date + "T12:00:00");
    // Snap to Monday of the week containing start_date (Mon=0 offset, Sun=6 offset back)
    const daysToMon = (startDate.getDay() + 6) % 7;
    const weekStartMs = startDate.getTime() - daysToMon * 24 * 60 * 60 * 1000;
    const todayMs = new Date(todayStr + "T12:00:00").getTime();
    for (let w = 0; w < (prog.weeks || 4); w++) {
      for (let d = 0; d < 7; d++) {
        const ms = weekStartMs + (w * 7 + d) * 24 * 60 * 60 * 1000;
        const date = new Date(ms);
        const dateStr = date.toISOString().split("T")[0];
        const dayNum = getProgramDay(prog.start_date, dateStr, dayMap);
        const isToday = dateStr === todayStr;
        const isPast = ms < todayMs;
        const isDone = completedDates.includes(dateStr);
        let status = "rest";
        if (dayNum) {
          if (isDone) status = "done";
          else if (isToday) status = "today";
          else if (isPast && dateStr !== todayStr) status = "missed";
          else status = "scheduled";
        }
        days.push({ date, dateStr, dayNum, status, weekNum: w + 1 });
      }
    }
    return days;
  };

  const missedYesterday = () => {
    if (!prog) return null;
    const yMs = new Date(todayStr + "T12:00:00").getTime() - 24 * 60 * 60 * 1000;
    const ys = new Date(yMs).toISOString().split("T")[0];
    if (prog.start_date && ys < prog.start_date) return null;
    if (!getProgramDay(prog.start_date, ys, buildDayMapFromWorkouts(loadedWorkouts) || getDayMapForProgram(prog.days_per_week || 5))) return null;
    if (completedDates.includes(ys)) return null;
    return ys;
  };

  const calculateStreak = () => {
    if (!prog) return 0;
    let streak = 0;
    const dayMap = buildDayMapFromWorkouts(loadedWorkouts) || getDayMapForProgram(prog.days_per_week || 5);
    const checkDate = new Date(todayStr + "T12:00:00");
    for (let safety = 0; safety < 400; safety++) {
      const ds = checkDate.toISOString().split("T")[0];
      const dayNum = getProgramDay(prog.start_date, ds, dayMap);
      if (!dayNum) { checkDate.setDate(checkDate.getDate() - 1); continue; } // rest day: skip
      if (ds === todayStr && !completedDates.includes(ds)) {
        checkDate.setDate(checkDate.getDate() - 1); continue; // today not done yet: skip, don't break
      }
      if (completedDates.includes(ds)) { streak++; checkDate.setDate(checkDate.getDate() - 1); }
      else break; // past missed workout: stop
    }
    return streak;
  };

  const buildMonthCalGrid = (monthDate, progData, completedArr, programWorkouts = []) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysToMon = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(firstDay.getTime() - daysToMon * 24 * 60 * 60 * 1000);
    const todayMs = new Date(todayStr + "T12:00:00").getTime();
    const monthDayMap = progData ? (buildDayMapFromWorkouts(programWorkouts) || getDayMapForProgram(progData.days_per_week || 5)) : null;
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const ms = gridStart.getTime() + i * 24 * 60 * 60 * 1000;
      const date = new Date(ms);
      const dateStr = date.toISOString().split("T")[0];
      const isCurrentMonth = date.getMonth() === month;
      const dayNum = progData ? getProgramDay(progData.start_date, dateStr, monthDayMap) : null;
      const isDone = completedArr.includes(dateStr);
      const isPast = ms < todayMs;
      const isToday = dateStr === todayStr;
      const hasWorkout = dayNum ? programWorkouts.some(w => w.day_number === dayNum) : false;
      let status = isCurrentMonth ? "rest" : "other-month";
      if (isCurrentMonth && dayNum) {
        if (isDone) status = "done";
        else if (isToday && hasWorkout) status = "today";
        else if (isPast && dateStr !== todayStr && hasWorkout) status = "missed";
        else if (!isPast && !isToday) status = "scheduled";
      }
      cells.push({ date, dateStr, dayNum, status, isCurrentMonth });
    }
    return cells;
  };

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const signIn = async () => {
    setLoading(true); setAuthErr("");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password: pw });
      if (error) { setAuthErr(error.message); setLoading(false); return; }
      setUser(data.user);
      const { data: p } = await supabase.from("profiles").select("*").eq("id", data.user.id).maybeSingle();
      if (p) { setProfile(p); setScreen(p.role === "coach" ? "coach-home" : "home"); }
      else { await supabase.from("profiles").insert({ id: data.user.id, full_name: email.split("@")[0], role: "client" }); setProfile({ id: data.user.id, role: "client" }); setScreen("onboarding"); }
    } catch (e) { setAuthErr(e.message); }
    setLoading(false);
  };

  // SQL needed: alter table clients add constraint clients_profile_id_unique unique (profile_id);
  const signUp = async () => {
    setLoading(true); setAuthErr("");
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password: pw,
        options: { data: { full_name: uname } }
      });
      if (error) { setAuthErr(error.message); setLoading(false); return; }
      if (data.user) {
        const userId = data.user.id;

        // Try profile insert up to 3 times
        let profileSaved = false;
        for (let i = 0; i < 3; i++) {
          const { error: pe } = await supabase.from("profiles").upsert({
            id: userId,
            full_name: uname || email.split("@")[0],
            role: "client"
          }, { onConflict: "id" });
          if (!pe) { profileSaved = true; break; }
          console.error("[signUp] profile attempt", i + 1, pe);
          await new Promise(r => setTimeout(r, 500));
        }

        if (!profileSaved) {
          setAuthErr("Account created but profile save failed. Please contact Coach Max.");
          setLoading(false);
          return;
        }

        // Create client record
        const { error: ce } = await supabase.from("clients").upsert({
          profile_id: userId,
          billing_status: "active"
        }, { onConflict: "profile_id" });
        if (ce) console.error("[signUp] client record failed:", ce);

        setUser(data.user);
        setProfile({ id: userId, full_name: uname || email.split("@")[0], role: "client" });
        setScreen("onboarding");
      }
    } catch (e) { setAuthErr(e.message); }
    setLoading(false);
  };

  const signOut = async () => { await supabase.auth.signOut(); setUser(null); setProfile(null); setScreen("splash"); };

  const saveBw = async () => {
    const val = parseFloat(newBwVal);
    if (!val || !user) return;
    await supabase.from("profiles").update({ body_weight: val }).eq("id", user.id);
    await supabase.from("body_weight_log").insert({ client_id: user.id, weight: val, logged_at: new Date().toISOString() });
    setProfile(p => ({ ...p, body_weight: val }));
    setBwHistory(prev => [...prev, { weight: val, logged_at: new Date().toISOString() }]);
    setEditingBw(false); setNewBwVal("");
    showNotif("Updated!");
  };
  const finishOb = async () => {
    const age = calcAge(dob);
    await supabase.from("profiles").update({
      age, body_weight: parseFloat(bwVal) || null, injuries, goals,
    }).eq("id", user.id);
    setProfile(p => ({ ...p, age, body_weight: parseFloat(bwVal) || null, injuries, goals }));
    // Load client data now so home screen has correct state from the start
    await loadClientData();
    setScreen("home");
  };
  const toggleChip = (list, setList, val) => setList(p => p.includes(val) ? p.filter(x => x !== val) : [...p, val]);

  // ── VOICE ─────────────────────────────────────────────────────────────────
  const startVoice = (idx) => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) { alert("Try Chrome."); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = "en-US";
    setListening(true); setTranscript("");
    r.onresult = e => { const t = e.results[0][0].transcript; setTranscript(t); parseVoice(t, idx); setListening(false); };
    r.onerror = () => setListening(false); r.onend = () => setListening(false); r.start();
  };

  const parseVoice = (text, idx) => {
    const WORD_NUMS = { one:1, two:2, to:2, too:2, three:3, four:4, five:5, six:6, seven:7, eight:8, nine:9, ten:10, eleven:11, twelve:12, thirteen:13, fourteen:14, fifteen:15, twenty:20, thirty:30, forty:40, fifty:50, sixty:60, seventy:70, eighty:80, ninety:90, hundred:100 };
    const norm = text.toLowerCase()
      .replace(/\b(one|two|to|too|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred)\b/g,
        w => WORD_NUMS[w] ?? w);

    // Split at "set N" boundaries
    const segments = [];
    const setRe = /\bset\s*(\d+)/gi;
    let lastIdx = 0, lastSetNum = null, m;
    while ((m = setRe.exec(norm)) !== null) {
      if (lastSetNum !== null) segments.push({ text: norm.slice(lastIdx, m.index), setNum: lastSetNum });
      lastSetNum = parseInt(m[1]) - 1;
      lastIdx = m.index + m[0].length;
    }
    if (lastSetNum !== null) segments.push({ text: norm.slice(lastIdx), setNum: lastSetNum });
    if (segments.length === 0) segments.push({ text: norm, setNum: 0 });

    const extractWeightReps = (str) => {
      let weight = null, reps = null;
      // seconds/timed
      const secM = str.match(/(\d+)\s*(?:sec(?:ond)?s?|s\b)/);
      if (secM) return { weight: null, reps: secM[1], isTimed: true };
      // "N lbs" / "N pounds" / "N pound"
      const wLbs = str.match(/(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?)/);
      if (wLbs) weight = wLbs[1];
      // "at N" / "@ N"
      if (!weight) { const wAt = str.match(/(?:\bat\b|@)\s*(\d+(?:\.\d+)?)/); if (wAt) weight = wAt[1]; }
      // "N reps"
      const rReps = str.match(/(\d+)\s*reps?/);
      if (rReps) reps = rReps[1];
      // "for N" / "x N" / "× N"
      if (!reps) { const rFor = str.match(/(?:\bfor\b|[×x])\s*(\d+)\b/); if (rFor) reps = rFor[1]; }
      // "N reps at N lbs" reverse pattern — reps first then weight
      if (!reps) { const rFirst = str.match(/(\d+)\s*reps?\s+(?:at|@)\s+(\d+)/); if (rFirst) { reps = rFirst[1]; weight = rFirst[2]; } }
      // "three sets of N for N" → ignore sets count, grab weight+reps
      if (!weight && !reps) {
        const sets3 = str.match(/\d+\s*sets?\s+of\s+(\d+)\s+(?:for|x|×)\s+(\d+)/);
        if (sets3) { weight = sets3[1]; reps = sets3[2]; }
      }
      // fallback: grab all bare numbers; first = weight, second = reps
      if (!weight || !reps) {
        const nums = [...str.matchAll(/\b(\d+(?:\.\d+)?)\b/g)].map(n => n[1]);
        if (!weight && !reps && nums.length >= 2) { weight = nums[0]; reps = nums[1]; }
        else if (!reps && weight && nums.length >= 1) { const o = nums.find(n => n !== weight); if (o) reps = o; }
        else if (!weight && reps && nums.length >= 1) { const o = nums.find(n => n !== reps); if (o) weight = o; }
        else if (!weight && !reps && nums.length === 1) { reps = nums[0]; }
      }
      return { weight, reps };
    };

    const results = segments
      .map(s => ({ setNum: s.setNum, ...extractWeightReps(s.text) }))
      .filter(r => r.weight || r.reps);

    if (results.length === 0) {
      setTranscript(`"${text}" → Couldn't parse — try again`);
      return;
    }

    setSetData(prev => {
      const nd = { ...prev };
      results.forEach(({ setNum, weight, reps }) => {
        if (weight) nd[`${idx}-${setNum}-w`] = weight;
        if (reps)   nd[`${idx}-${setNum}-r`] = reps;
      });
      return nd;
    });

    const summary = results.map(r =>
      `Set ${r.setNum + 1}: ${r.weight ? r.weight + ' lbs' : 'BW'} × ${r.reps ?? '?'} reps`
    ).join(' · ');
    setTranscript(`"${text}" → ${summary}`);
  };

  // ── FILE UPLOAD ───────────────────────────────────────────────────────────
  const triggerFile = (type) => { fileType.current = type; fileRef.current.accept = type === "video" ? "video/*" : "image/*"; fileRef.current.click(); };
  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !user) return; e.target.value = "";
    setUploading(true);
    const { data: cd } = await supabase.from("profiles").select("id").eq("role", "coach").maybeSingle();
    try {
      const url = await uploadMedia(file, user.id);
      const ex = workout?.exercises[activeEx]?.exercises?.name || "this exercise";
      setMsgs(p => [...p, { id: Date.now(), from_id: user.id, body: `Form check: ${ex}`, media_url: url, media_type: fileType.current, sent: true, created_at: new Date().toISOString() }]);
      let draft = "Client submitted a form check. Please review and respond.";
      try { const r = await callFn("analyze-form", { exerciseName: ex }); draft = r.draft || draft; } catch (e2) { }
      await callFn("save-message", { from_id: user.id, to_id: cd?.id, body: `Form check: ${ex}`, media_url: url, media_type: fileType.current, triage_type: "form_check", ai_draft: draft, sent: false });
      if (cd?.id) await callFn("send-notification", { title: "New Form Check", message: `${profile?.full_name || "Client"} submitted a form check`, externalId: cd.id });
      showNotif("Form check sent!");
    } catch (err) { showNotif("Upload failed — try again"); }
    setUploading(false);
  };

  // ── MESSAGING ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || isCoach) return;
    loadMsgs();
    const sub = supabase.channel("msgs").on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, p => {
      const m = p.new;
      if (m.sent && (m.from_id === user.id || m.to_id === user.id)) {
        setMsgs(prev => [...prev, m]);
        if (m.from_id !== user.id) showNotif("New message from Coach Max");
        setTimeout(() => { if (thrRef.current) thrRef.current.scrollTop = thrRef.current.scrollHeight; }, 100);
      }
    }).subscribe();
    return () => supabase.removeChannel(sub);
  }, [user, isCoach]);

  useEffect(() => {
    if (triage === "coach") {
      setTimeout(() => { if (thrRef.current) thrRef.current.scrollTop = thrRef.current.scrollHeight; }, 150);
    }
  }, [msgs, triage, screen]);

  useEffect(() => {
    if (cpTab === "messages") {
      setTimeout(() => { if (cThrRef.current) cThrRef.current.scrollTop = cThrRef.current.scrollHeight; }, 150);
    }
  }, [clientMsgs, cpTab]);

  const loadMsgs = async () => {
    if (!user) return;
    const { data } = await supabase.from("messages").select("*").or(`from_id.eq.${user.id},to_id.eq.${user.id}`).eq("sent", true).order("created_at", { ascending: true });
    if (data) {
      setMsgs(data);
      if (data.length > 0) setTriage("coach");
    }
  };
  const sendTxt = async () => {
    if (!msgTxt.trim() || !user) return;
    const t = msgTxt.trim(); setMsgTxt("");
    const { data: cd } = await supabase.from("profiles").select("id").eq("role", "coach").maybeSingle();
    const newMsg = { id: Date.now(), from_id: user.id, to_id: cd?.id, body: t, sent: true, created_at: new Date().toISOString() };
    setMsgs(prev => [...prev, newMsg]);
    console.log("[sendTxt] calling save-message", { from_id: user.id, to_id: cd?.id, body: t });
    let fnOk = false;
    try {
      const res = await callFn("save-message", { from_id: user.id, to_id: cd?.id, body: t, triage_type: "message", sent: true });
      console.log("[sendTxt] save-message result:", res);
      if (res && !res.error) fnOk = true;
    } catch (e) { console.error("[sendTxt] save-message threw:", e); }
    if (!fnOk) {
      console.log("[sendTxt] falling back to direct Supabase insert");
      const { error: insErr } = await supabase.from("messages").insert({ from_id: user.id, to_id: cd?.id, body: t, sent: true, created_at: new Date().toISOString() });
      if (insErr) console.error("[sendTxt] direct insert failed:", insErr);
    }
    if (cd?.id) {
      try { await callFn("send-notification", { title: `New message from ${profile?.full_name || "Client"}`, message: t.slice(0, 80), externalId: cd.id }); } catch (e) {}
    }
  };
  const sendTech = () => {
    if (!msgTxt.trim()) return;
    setTechMsgs(p => [...p, { id: Date.now(), from: "me", sender: "You", text: msgTxt.trim(), time: "Now" }]);
    setTimeout(() => setTechMsgs(p => [...p, { id: Date.now() + 1, from: "sys", sender: "Support", text: "Got it — try refreshing. If it continues I'll flag Coach Max.", time: "Now" }]), 1200);
    setMsgTxt("");
  };

  // ── COMPLETE WORKOUT ──────────────────────────────────────────────────────
  const completeWorkout = async () => {
    if (!prog || !viewingWorkout || !clientRecord) return;
    const exercises = (viewingWorkout.workout_exercises || []).sort((a, b) => a.order_index - b.order_index);

    const summaryExercises = exercises.map((we, i) => {
      const exName = we.exercises?.name || "Exercise";
      const wasChecked = !!checked[i];
      const numSets = we.sets || 1;
      const setsData = [];
      for (let s = 0; s < numSets; s++) {
        const w = parseFloat(setData[`${i}-${s}-w`]) || 0;
        const r = parseInt(setData[`${i}-${s}-r`]) || 0;
        setsData.push({ set: s + 1, weight: w, reps: r, logged: w > 0 || r > 0 });
      }
      const hasData = setsData.some(s => s.logged);
      const lastSetIdx = numSets - 1;
      const weight = parseFloat(setData[`${i}-${lastSetIdx}-w`]) || 0;
      const reps = parseInt(setData[`${i}-${lastSetIdx}-r`]) || 0;
      const prev = exerciseHistory[we.id];
      const improved = prev && hasData && (weight > prev.weight || (weight === prev.weight && reps > prev.reps));
      return { id: we.id, weId: we.id, name: exName, sets: numSets, setsData, weight, reps, hasData, wasChecked, improved, prevWeight: prev?.weight, prevReps: prev?.reps, idx: i };
    });

    const improvements = summaryExercises.filter(e => e.improved);

    // Insert session_log
    const sessionPayload = {
      client_id: clientRecord.id, workout_id: viewingWorkout.id, logged_by: user.id,
      completed_at: new Date(viewingDate + "T12:00:00").toISOString(), day_number: viewingWorkout.day_number
    };
    console.log("[completeWorkout] inserting session_log:", sessionPayload);
    const { data: ses, error } = await supabase.from("session_logs").insert(sessionPayload).select().single();
    console.log("[completeWorkout] session_logs result:", { data: ses, error });

    if (error) {
      console.error("[completeWorkout] session_logs insert FAILED:", error);
      showNotif("Save failed: " + (error.message || error.code || "unknown error"));
      return;
    }

    // Insert set_logs — workout_exercise_id is we.id (workout_exercises row), NOT exercises.id
    const toIns = [];
    summaryExercises.forEach(ex => {
      for (let s = 0; s < (ex.sets || 1); s++) {
        const w = parseFloat(setData[`${ex.idx}-${s}-w`]) || 0;
        const r = parseInt(setData[`${ex.idx}-${s}-r`]) || 0;
        if (w || r) toIns.push({
          session_log_id: ses.id,
          workout_exercise_id: ex.weId,
          set_number: s + 1, weight: w, reps_completed: r, notes: ""
        });
      }
    });
    console.log("[completeWorkout] set_logs payload (" + toIns.length + " rows):", JSON.stringify(toIns, null, 2));
    if (toIns.length > 0) {
      const { data: slData, error: slErr } = await supabase.from("set_logs").insert(toIns).select();
      console.log("[completeWorkout] set_logs result:", { data: slData, error: slErr });
      if (slErr) {
        console.error("[completeWorkout] set_logs insert FAILED:", slErr);
        showNotif("Set data not saved: " + (slErr.message || slErr.code || "unknown error"));
      } else {
        console.log("[completeWorkout] set_logs saved:", slData?.length, "rows");
      }
    } else {
      console.log("[completeWorkout] toIns empty — no weight/rep data was entered, set_logs skipped");
      showNotif("Session saved — no weight or rep data was entered");
    }

    // Reload recent sessions fresh from Supabase
    const { data: freshSessions } = await supabase.from("session_logs")
      .select("id, completed_at, workouts(name,focus), set_logs(set_number, weight, reps_completed, workout_exercise_id, workout_exercises(exercises(name)))")
      .eq("client_id", clientRecord.id)
      .order("completed_at", { ascending: false })
      .limit(5);
    if (freshSessions) {
      setMyRecentSessions(freshSessions);
      setFullSessionHistory(prev => {
        const existingIds = new Set(freshSessions.map(f => f.id));
        return [...freshSessions, ...prev.filter(p => !existingIds.has(p.id))].slice(0, 20);
      });
    }

    const newCompletedDates = [...completedDates, viewingDate].filter((d, i, arr) => arr.indexOf(d) === i);
    setCompletedDates(newCompletedDates);
    localStorage.setItem(`completedDates_${user.id}`, JSON.stringify(newCompletedDates));
    setLastSessionId(ses.id);
    setSessionFeel(""); setSessionClientNote("");
    setSessionSummary({
      workoutName: viewingWorkout.name,
      exercises: summaryExercises,
      improvements,
      totalDone: newCompletedDates.length,
    });
    setScreen("workout-summary");
  };

  const saveEditedSummaryEx = async (ex) => {
    if (!lastSessionId) return;
    await supabase.from("set_logs").delete()
      .eq("session_log_id", lastSessionId)
      .eq("workout_exercise_id", ex.weId);
    const rows = [];
    for (let s = 0; s < ex.sets; s++) {
      const w = parseFloat(editSummaryData[`${s}-w`] !== undefined ? editSummaryData[`${s}-w`] : (ex.setsData[s]?.weight || 0)) || 0;
      const r = parseInt(editSummaryData[`${s}-r`] !== undefined ? editSummaryData[`${s}-r`] : (ex.setsData[s]?.reps || 0)) || 0;
      if (w || r) rows.push({ session_log_id: lastSessionId, workout_exercise_id: ex.weId, set_number: s + 1, weight: w, reps_completed: r, notes: "" });
    }
    if (rows.length > 0) await supabase.from("set_logs").insert(rows);
    const newSetsData = ex.setsData.map((sd, s) => {
      const w = parseFloat(editSummaryData[`${s}-w`] !== undefined ? editSummaryData[`${s}-w`] : sd.weight) || 0;
      const r = parseInt(editSummaryData[`${s}-r`] !== undefined ? editSummaryData[`${s}-r`] : sd.reps) || 0;
      return { ...sd, weight: w, reps: r, logged: w > 0 || r > 0 };
    });
    const updatedEx = { ...ex, setsData: newSetsData, hasData: newSetsData.some(s => s.logged) };
    setSessionSummary(prev => ({
      ...prev,
      exercises: prev.exercises.map(e => e.idx === ex.idx ? updatedEx : e)
    }));
    setEditingSummaryEx(null);
    setEditSummaryData({});
  };

  useEffect(() => {
    if (!selClient || cpTab !== "progress") return;
    (async () => {
      setClientBalanceLoading(true);
      setClientBalanceData([]);
      const bw = selClient.body_weight || 150;
      const { data: cr2 } = await supabase.from("clients").select("id").eq("profile_id", selClient.id).maybeSingle();
      if (!cr2) { setClientBalanceLoading(false); return; }
      const { data: sessions2 } = await supabase.from("session_logs").select("id").eq("client_id", cr2.id);
      if (!sessions2?.length) { setClientBalanceLoading(false); return; }
      const { data: rawLogs2 } = await supabase.from("set_logs")
        .select("weight, reps_completed, session_log_id, workout_exercises(exercises(name))")
        .in("session_log_id", sessions2.map(s => s.id));
      if (rawLogs2?.length) setClientBalanceData(calculateStructuralBalance(rawLogs2, bw));
      setClientBalanceLoading(false);
    })();
  }, [selClient?.id, cpTab]);

  useEffect(() => {
    if (!selSession?.workout_id) { setSelSessionWorkoutExercises([]); return; }
    (async () => {
      const { data } = await supabase.from("workout_exercises")
        .select("id, order_index, sets, exercises(name)")
        .eq("workout_id", selSession.workout_id)
        .order("order_index");
      setSelSessionWorkoutExercises(data || []);
    })();
  }, [selSession?.id]);

  // ── COACH FUNCTIONS ───────────────────────────────────────────────────────
  const refreshUnreadBadges = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("messages")
      .select("from_id, created_at")
      .eq("to_id", user.id)
      .eq("sent", true)
      .order("created_at", { ascending: false });
    if (!data) return;
    const latestByClient = {};
    data.forEach(m => { if (!latestByClient[m.from_id]) latestByClient[m.from_id] = m.created_at; });
    const unread = new Set();
    for (const [fromId, latestAt] of Object.entries(latestByClient)) {
      const lastOpened = localStorage.getItem(`msgLastOpened_${fromId}`);
      if (!lastOpened || new Date(latestAt) > new Date(parseInt(lastOpened))) unread.add(fromId);
    }
    setUnreadClientIds(unread);
  };

  useEffect(() => {
    if (!user || !isCoach) return;
    loadCoachData();
    const sub = supabase.channel("cmsgs").on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, p => {
      if (p.new.from_id !== user.id) { showNotif("New client message"); loadCoachData(); refreshUnreadBadges(); }
    }).subscribe();
    return () => supabase.removeChannel(sub);
  }, [user, isCoach]);

  const loadCoachData = async () => {
    const { data: cl } = await supabase.from("profiles").select("*").eq("role", "client");
    if (cl) {
      // Ensure a clients record exists for every profile (handles clients created before this fix)
      for (const profile of cl) {
        const { data: existing } = await supabase
          .from("clients").select("id").eq("profile_id", profile.id).maybeSingle();
        if (!existing) {
          await supabase.from("clients").insert({ profile_id: profile.id, billing_status: "active" });
        }
      }
      const withActivity = await Promise.all(cl.map(async (c) => {
        const displayName = c.full_name && !c.full_name.includes("@") ? c.full_name : (c.full_name || c.email || "Unknown");
        const initials = displayName.split(" ").filter(Boolean).map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
        try {
          const { data: crData } = await supabase.from("clients").select("id, billing_status").eq("profile_id", c.id).maybeSingle();
          if (!crData) return { ...c, displayName, initials, lastActive: null, billing_status: "active" };
          const { data: lastSes } = await supabase.from("session_logs")
            .select("completed_at").eq("client_id", crData.id)
            .order("completed_at", { ascending: false }).limit(1).maybeSingle();
          return { ...c, displayName, initials, lastActive: lastSes?.completed_at || null, billing_status: crData.billing_status || "active" };
        } catch { return { ...c, displayName, initials, lastActive: null, billing_status: "active" }; }
      }));
      setClientList(withActivity);
      await refreshUnreadBadges();
    }
    const { data: dr } = await supabase.from("messages").select("*, profiles!from_id(full_name)").eq("sent", false).not("ai_draft", "is", null).order("created_at", { ascending: false });
    if (dr) setDrafts(dr);
    await loadAllPrograms();
    const { data: blkData } = await supabase.from("training_blocks")
      .select("*").eq("coach_id", user.id).order("created_at", { ascending: false });
    if (blkData) setAllBlocks(blkData);
    const { data: exData } = await supabase.from("exercises").select("*").order("name", { ascending: true });
    if (exData) setExercises(exData);
  };

  const loadAllPrograms = async () => {
    // programs.client_id references clients.id (not profiles.id), so we join through clients
    const [progResult, clientsResult, wktsResult] = await Promise.all([
      supabase.from("programs").select("id, name, client_id, active, start_date, weeks, days_per_week, created_at, block_id, sequence_order").order("created_at", { ascending: false }),
      supabase.from("clients").select("id, profiles!profile_id(full_name)"),
      supabase.from("workouts").select("id, name, focus, program_id").is("program_id", null).order("name", { ascending: true }),
    ]);
    console.log("[loadAllPrograms] programs:", progResult.data?.length, "error:", progResult.error);
    console.log("[loadAllPrograms] standalone workouts:", wktsResult.data?.length, "error:", wktsResult.error);
    if (progResult.data) {
      const clientMap = {};
      (clientsResult.data || []).forEach(c => { if (c.id) clientMap[c.id] = c.profiles?.full_name || null; });
      setAllPrograms(progResult.data.map(p => ({ ...p, clientName: p.client_id ? (clientMap[p.client_id] || null) : null })));
    }
    if (wktsResult.data) setStandaloneWorkouts(wktsResult.data);
  };

  const loadProgramDraft = async (p) => {
    setProgramEditDraft(null);
    const { data: workouts, error: wErr } = await supabase
      .from("workouts")
      .select("id, day_number, name")
      .eq("program_id", p.id)
      .order("day_number", { ascending: true });
    console.log("[loadProgramDraft] workouts:", workouts?.length, "error:", wErr);
    const wktList = workouts || [];
    let weByWorkout = {};
    if (wktList.length > 0) {
      const { data: weData, error: weErr } = await supabase
        .from("workout_exercises")
        .select("id, workout_id, order_index, sets, reps, tempo, exercises(id, name)")
        .in("workout_id", wktList.map(w => w.id))
        .order("order_index", { ascending: true });
      console.log("[loadProgramDraft] workout_exercises raw count:", weData?.length, "error:", weErr?.message);
      if (weErr) console.error("[loadProgramDraft] workout_exercises query error:", weErr);
      for (const we of weData || []) {
        if (!weByWorkout[we.workout_id]) weByWorkout[we.workout_id] = [];
        weByWorkout[we.workout_id].push(we);
      }
    }
    const wkts = wktList.map(w => ({
      id: w.id,
      day_number: w.day_number,
      name: w.name,
      isExpanded: true,
      originalWeIds: (weByWorkout[w.id] || []).map(we => we.id),
      exercises: (weByWorkout[w.id] || []).map(we => ({
        we_id: we.id,
        exercise_id: we.exercises?.id || null,
        name: we.exercises?.name || "",
        sets: we.sets || 3,
        reps: we.reps || "10",
        tempo: we.tempo || "",
      })),
    }));
    wkts.forEach(w => {
      if (w.exercises.length === 0) {
        console.log("[loadProgramDraft] workout id=", w.id, `"${w.name}"`, "has 0 exercises. Raw weByWorkout entry:", weByWorkout[w.id]);
      } else {
        console.log("[loadProgramDraft] workout id=", w.id, `"${w.name}"`, "exercises:", w.exercises.map(e => e.name).join(", "));
      }
    });
    console.log("[loadProgramDraft] total workouts=", wkts.length, "total exercises=", wkts.reduce((s, w) => s + w.exercises.length, 0));
    setProgramEditDraft({ id: p.id, name: p.name, weeks: p.weeks || 4, days_per_week: p.days_per_week || 5, workouts: wkts });
  };

  const loadClientSessionsForCoach = async (clientProfileId) => {
    const { data: cr } = await supabase
      .from("clients").select("id").eq("profile_id", clientProfileId).maybeSingle();
    if (!cr) { setClientSessions([]); return; }
    setSelClientRecord(cr);
    const { data: sessions } = await supabase
      .from("session_logs")
      .select("id, completed_at, coach_notes, client_notes, feel_rating, day_number, workout_id")
      .eq("client_id", cr.id).order("completed_at", { ascending: false }).limit(30);
    if (!sessions || sessions.length === 0) { setClientSessions([]); return; }
    const workoutIds = [...new Set(sessions.map(s => s.workout_id).filter(Boolean))];
    const { data: workouts } = workoutIds.length > 0
      ? await supabase.from("workouts").select("id, name, focus").in("id", workoutIds)
      : { data: [] };
    const sessionIds = sessions.map(s => s.id);
    const { data: setLogs } = await supabase
      .from("set_logs")
      .select("session_log_id, workout_exercise_id, weight, reps_completed, set_number, workout_exercises(exercises(name))")
      .in("session_log_id", sessionIds);
    const weIds = [...new Set((setLogs || []).map(sl => sl.workout_exercise_id).filter(Boolean))];
    let exerciseNames = {};
    if (weIds.length > 0) {
      const { data: wes } = await supabase.from("workout_exercises").select("id, exercises(name)").in("id", weIds);
      (wes || []).forEach(we => { if (we.exercises?.name) exerciseNames[we.id] = we.exercises.name; });
    }
    const assembled = sessions.map(s => ({
      ...s,
      workouts: workouts?.find(w => w.id === s.workout_id) || null,
      set_logs: (setLogs || [])
        .filter(sl => sl.session_log_id === s.id)
        .map(sl => ({ ...sl, exercise_name: exerciseNames[sl.workout_exercise_id] || sl.workout_exercises?.exercises?.name || "Unknown Exercise" }))
    }));
    setClientSessions(assembled);
    const { data: allSes } = await supabase
      .from("session_logs")
      .select("completed_at")
      .eq("client_id", cr.id);
    const allDates = [...new Set((allSes || []).map(s => s.completed_at.split("T")[0]))];
    setCoachClientCompletedDates(allDates);
  };

  const openClient = async (c) => {
    setSelClient(c); setCpTab("overview"); setSelSession(null); setClientSessions([]); setClientProg(null); setShowAssignProgram(false); setAssignProgramId(""); setClientBalanceData([]); setClientBalanceLoading(false);
    setSelClientRecord(null); setCoachCalDay(null); setCoachCalPopup(false); setCoachCalAction(false); setCoachCalDayWorkout(null);
    setCoachPrivateNotes(""); setSavingNotes(false); setCoachClientCompletedDates([]); setCoachClientProg(null); setCoachClientWorkouts([]);
    const { data: msgData } = await supabase.from("messages").select("*").or(`from_id.eq.${c.id},to_id.eq.${c.id}`).eq("sent", true).order("created_at", { ascending: true });
    if (msgData) {
      setClientMsgs(msgData);
      setTimeout(() => { if (cThrRef.current) cThrRef.current.scrollTop = cThrRef.current.scrollHeight; }, 150);
    }

    // Step 1 - find client record
    const { data: cr } = await supabase
      .from("clients")
      .select("id")
      .eq("profile_id", c.id)
      .maybeSingle();

    console.log("[openClient] profile_id:", c.id, "client record:", cr);

    if (!cr) {
      console.log("[openClient] No client record found for profile_id:", c.id);
      setClientSessions([]);
      return;
    }

    setSelClientRecord(cr);

    // Load program and notes using cr (always runs if client record found)
    const [progRes, notesRes] = await Promise.all([
      supabase.from("programs").select("id, name, client_id, active, start_date, weeks, days_per_week, created_at, block_id, sequence_order").eq("client_id", cr.id).eq("active", true).maybeSingle(),
      supabase.from("coach_notes").select("notes").eq("coach_id", user.id).eq("client_id", cr.id).maybeSingle(),
    ]);
    setClientProg(progRes.data || null);
    setCoachClientProg(progRes.data || null);
    if (notesRes.data?.notes) setCoachPrivateNotes(notesRes.data.notes);
    if (progRes.data) {
      const { data: cWkts } = await supabase.from("workouts").select("id, day_number, day_of_week, program_id").eq("program_id", progRes.data.id);
      setCoachClientWorkouts(cWkts || []);
    }

    // Step 2 - load sessions using cr.id
    const { data: sessions, error: sesError } = await supabase
      .from("session_logs")
      .select("id, completed_at, coach_notes, client_notes, feel_rating, day_number, workout_id")
      .eq("client_id", cr.id)
      .order("completed_at", { ascending: false })
      .limit(30);

    console.log("[openClient] sessions query - client_id:", cr.id, "results:", sessions, "error:", sesError);

    if (!sessions || sessions.length === 0) {
      setClientSessions([]);
      return;
    }

    // Step 3 - get workout names
    const workoutIds = [...new Set(sessions.map(s => s.workout_id).filter(Boolean))];
    const { data: workouts } = workoutIds.length > 0
      ? await supabase.from("workouts").select("id, name, focus").in("id", workoutIds)
      : { data: [] };

    // Step 4 - get set logs
    const sessionIds = sessions.map(s => s.id);
    const { data: setLogs } = await supabase
      .from("set_logs")
      .select("session_log_id, workout_exercise_id, weight, reps_completed, set_number")
      .in("session_log_id", sessionIds);

    console.log("[openClient] set_logs:", setLogs);

    // Step 5 - get exercise names
    const weIds = [...new Set((setLogs||[]).map(sl => sl.workout_exercise_id).filter(Boolean))];
    let exerciseNames = {};
    if (weIds.length > 0) {
      const { data: wes } = await supabase
        .from("workout_exercises")
        .select("id, exercises(name)")
        .in("id", weIds);
      console.log("[exercise names] weIds:", weIds, "wes result:", wes);
      (wes||[]).forEach(we => { exerciseNames[we.id] = we.exercises?.name || null; });
    }

    // Step 6 - assemble and set state
    const assembled = sessions.map(s => ({
      ...s,
      workouts: workouts?.find(w => w.id === s.workout_id) || { name: "Session", focus: "" },
      set_logs: (setLogs||[])
        .filter(sl => sl.session_log_id === s.id)
        .map(sl => ({ ...sl, exercise_name: exerciseNames[sl.workout_exercise_id] || "Unknown Exercise" }))
    }));

    console.log("[openClient] assembled sessions:", assembled.length);
    setClientSessions(assembled);

    // Unlimited query for all completed dates — raw completed_at dates
    const { data: allSessions } = await supabase
      .from("session_logs")
      .select("completed_at")
      .eq("client_id", cr.id);
    const dates = [...new Set((allSessions || []).map(s => s.completed_at.split("T")[0]))];
    setCoachClientCompletedDates(dates);
  };

  const openCoachCalDay = async (d) => {
    setCoachCalDay(d);
    setCoachCalPopup(true);
    setCoachCalAction(false);
    setCoachCalDayWorkout(null);
    setCoachCalAssignMode(null);
    setCoachCalSessionMode(null);
    setCoachCalMoveDate("");
    if (coachClientProg && (d.status === "scheduled" || d.status === "today" || d.status === "missed")) {
      const jsDay = new Date(d.dateStr + "T12:00:00").getDay();
      const ourDOW = jsDay === 0 ? 7 : jsDay;
      const { data: wktByDOW } = await supabase.from("workouts")
        .select("*, workout_exercises(order_index, sets, reps, exercises(name, tempo))")
        .eq("program_id", coachClientProg.id)
        .eq("day_of_week", ourDOW)
        .maybeSingle();
      if (wktByDOW) { setCoachCalDayWorkout(wktByDOW); return; }
      if (d.dayNum) {
        const { data: wkt } = await supabase.from("workouts")
          .select("*, workout_exercises(order_index, sets, reps, exercises(name, tempo))")
          .eq("program_id", coachClientProg.id)
          .eq("day_number", d.dayNum)
          .maybeSingle();
        if (wkt) setCoachCalDayWorkout(wkt);
      }
    }
  };

  const saveVerse = async () => {
    if (editingVerse) {
      await supabase.from("bible_verses").update({ verse: editingVerse.verse, reference: editingVerse.reference, week_of: editingVerse.week_of }).eq("id", editingVerse.id);
      setVerses(p => p.map(v => v.id === editingVerse.id ? editingVerse : v));
      setEditingVerse(null);
    } else {
      if (!newVerse.week_of || !newVerse.verse) { showNotif("Fill in week and verse"); return; }
      const { data } = await supabase.from("bible_verses").insert(newVerse).select().single();
      if (data) setVerses(p => [...p, data]);
      setNewVerse({ week_of: "", verse: "", reference: "" });
      setShowAddVerse(false);
    }
    showNotif("Saved!");
  };

  const saveResource = async () => {
    // SQL needed: create table if not exists resources (id uuid primary key default gen_random_uuid(), category text, title text, description text, url text, created_at timestamptz default now());
    if (!newResource.title.trim()) { showNotif("Enter a title"); return; }
    const { data } = await supabase.from("resources").insert({ ...newResource, title: newResource.title.trim(), description: newResource.description.trim() }).select().single();
    if (data) setResourcesList(p => [...p, data]);
    setNewResource({ category: "Methodology", title: "", description: "", url: "" });
    setShowAddResource(false);
    showNotif("Resource added!");
  };

  const deleteResource = async (id) => {
    await supabase.from("resources").delete().eq("id", id);
    setResourcesList(p => p.filter(r => r.id !== id));
  };

  const saveCoachNotes = async () => {
    // SQL needed: create table if not exists coach_notes (id uuid primary key default gen_random_uuid(), coach_id uuid references profiles(id), client_id uuid, notes text, updated_at timestamptz default now());
    // SQL needed: create policy "Coach can manage own notes" on coach_notes for all using (coach_id = auth.uid());
    if (!selClientRecord || !user) return;
    setSavingNotes(true);
    const { data: existing } = await supabase.from("coach_notes").select("id").eq("coach_id", user.id).eq("client_id", selClientRecord.id).maybeSingle();
    let error;
    if (existing) {
      ({ error } = await supabase.from("coach_notes").update({ notes: coachPrivateNotes, updated_at: new Date().toISOString() }).eq("id", existing.id));
    } else {
      ({ error } = await supabase.from("coach_notes").insert({ coach_id: user.id, client_id: selClientRecord.id, notes: coachPrivateNotes, updated_at: new Date().toISOString() }));
    }
    setSavingNotes(false);
    if (error) showNotif("Save failed: " + error.message);
    else showNotif("Notes saved");
  };

  const sendDraft = async (d) => {
    const text = editDraft[d.id] !== undefined ? editDraft[d.id] : d.ai_draft;
    await supabase.from("messages").update({ body: text, sent: true, ai_draft: null, from_id: user.id, to_id: d.from_id }).eq("id", d.id);
    await callFn("save-message", { from_id: user.id, to_id: d.from_id, body: text, triage_type: "form_check_response", sent: true });
    setDrafts(p => p.filter(x => x.id !== d.id));
    await callFn("send-notification", { title: "Message from Coach Max", message: text.slice(0, 80), externalId: d.from_id });
    showNotif("Sent!");
  };
  const discardDraft = async (id) => { await supabase.from("messages").delete().eq("id", id); setDrafts(p => p.filter(x => x.id !== id)); };

  const openClientMessages = () => {
    setCpTab("messages");
    if (selClient) {
      localStorage.setItem(`msgLastOpened_${selClient.id}`, Date.now().toString());
      setUnreadClientIds(prev => { const n = new Set(prev); n.delete(selClient.id); return n; });
    }
  };

  const sendCoachMsg = async () => {
    const draft = coachDrafts[selClient?.id] || "";
    if (!draft.trim() || !selClient || !user) return;
    const t = draft.trim();
    setCoachDrafts(p => ({ ...p, [selClient.id]: "" }));
    const newMsg = { id: Date.now(), from_id: user.id, to_id: selClient.id, body: t, sent: true, created_at: new Date().toISOString() };
    setClientMsgs(prev => [...prev, newMsg]);
    setTimeout(() => { if (cThrRef.current) cThrRef.current.scrollTop = cThrRef.current.scrollHeight; }, 100);
    await callFn("save-message", { from_id: user.id, to_id: selClient.id, body: t, triage_type: "message", sent: true });
    await callFn("send-notification", { title: `New message from ${profile?.full_name || "Coach Max"}`, message: t.slice(0, 80), externalId: selClient.id });
    const { data } = await supabase.from("messages").select("*").or(`from_id.eq.${selClient.id},to_id.eq.${selClient.id}`).eq("sent", true).order("created_at", { ascending: true });
    if (data) {
      setClientMsgs(data);
      setTimeout(() => { if (cThrRef.current) cThrRef.current.scrollTop = cThrRef.current.scrollHeight; }, 100);
    }
  };

  const loadCoachConversations = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("messages")
      .select("from_id, to_id, body, created_at, media_url")
      .or(`to_id.eq.${user.id},from_id.eq.${user.id}`)
      .eq("sent", true)
      .order("created_at", { ascending: false });
    if (!data) return;
    const byClient = {};
    data.forEach(m => {
      const clientId = m.from_id === user.id ? m.to_id : m.from_id;
      if (clientId === user.id) return;
      if (!byClient[clientId]) byClient[clientId] = m;
    });
    const convos = Object.entries(byClient).map(([clientId, lastMsg]) => {
      const client = clientList.find(c => c.id === clientId);
      const lastOpened = localStorage.getItem(`msgLastOpened_${clientId}`);
      const hasUnread = lastMsg.from_id !== user.id && (!lastOpened || new Date(lastMsg.created_at) > new Date(parseInt(lastOpened)));
      return {
        clientId,
        clientName: client?.displayName || client?.full_name || "Unknown",
        initials: client?.initials || "?",
        lastMessage: lastMsg.media_url ? "📎 Media" : (lastMsg.body || ""),
        lastAt: lastMsg.created_at,
        hasUnread,
        clientObj: client,
      };
    }).sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
    setCoachConversations(convos);
  };

  const openCoachMsgThread = async (convo) => {
    const client = convo.clientObj || clientList.find(c => c.id === convo.clientId);
    setCoachMsgOpenClient(client || { id: convo.clientId, displayName: convo.clientName, initials: convo.initials });
    localStorage.setItem(`msgLastOpened_${convo.clientId}`, Date.now().toString());
    setUnreadClientIds(prev => { const n = new Set(prev); n.delete(convo.clientId); return n; });
    setCoachConversations(prev => prev.map(c => c.clientId === convo.clientId ? { ...c, hasUnread: false } : c));
    const { data } = await supabase.from("messages").select("*")
      .or(`from_id.eq.${convo.clientId},to_id.eq.${convo.clientId}`)
      .eq("sent", true).order("created_at", { ascending: true });
    setCoachMsgThread(data || []);
  };

  const sendCoachMsgFromThread = async () => {
    const client = coachMsgOpenClient;
    const draft = coachDrafts[client?.id] || "";
    if (!draft.trim() || !client || !user) return;
    const t = draft.trim();
    setCoachDrafts(p => ({ ...p, [client.id]: "" }));
    setCoachMsgThread(prev => [...prev, { id: Date.now(), from_id: user.id, to_id: client.id, body: t, sent: true, created_at: new Date().toISOString() }]);
    await callFn("save-message", { from_id: user.id, to_id: client.id, body: t, triage_type: "message", sent: true });
    await callFn("send-notification", { title: `New message from ${profile?.full_name || "Coach Max"}`, message: t.slice(0, 80), externalId: client.id });
    const { data } = await supabase.from("messages").select("*").or(`from_id.eq.${client.id},to_id.eq.${client.id}`).eq("sent", true).order("created_at", { ascending: true });
    if (data) setCoachMsgThread(data);
  };

  const startLogSession = async (client) => {
    const { data: cr } = await supabase.from("clients").select("id").eq("profile_id", client.id).maybeSingle();
    if (!cr) { showNotif("No client record"); return; }
    const { data: p } = await supabase.from("programs").select("id, name, client_id, active, start_date, weeks, days_per_week, created_at, block_id, sequence_order").eq("client_id", cr.id).eq("active", true).maybeSingle();
    if (!p) { showNotif("No active program"); return; }
    const wkDay = getDayMapForProgram(p.days_per_week || 5)[new Date().getDay()];
    if (!wkDay) { showNotif("Rest day — no workout today"); return; }
    const { data: wkt } = await supabase.from("workouts").select("*, workout_exercises(*, exercises(*))").eq("program_id", p.id).eq("day_number", wkDay).maybeSingle();
    if (wkt) { setLogClient({ ...client, clientRecordId: cr.id }); setLogWorkout(wkt); setSesLogs({}); setSesNotes(""); setSesTx(""); }
    else showNotif("No workout found");
  };

  const startSesVoice = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) { alert("Try Chrome."); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = "en-US";
    setSesListen(true);
    r.onresult = async (e) => { const t = e.results[0][0].transcript; setSesTx(t); setSesListen(false); await parseSes(t); };
    r.onerror = () => setSesListen(false); r.onend = () => setSesListen(false); r.start();
  };

  const parseSes = async (text) => {
    if (!logWorkout) return;
    setSesParsing(true);
    const exercises = (logWorkout.workout_exercises || []).sort((a, b) => a.order_index - b.order_index).map(we => ({ name: we.exercises?.name, id: we.id }));
    try {
      const data = await callFn("parse-session", { transcript: text, exercises });
      if (data.exercises?.length > 0) {
        const nl = { ...sesLogs };
        data.exercises.forEach(ex => {
          let match = (logWorkout.workout_exercises || []).find(we => we.exercises?.name?.toLowerCase() === ex.exercise_name?.toLowerCase());
          if (!match) {
            const kw = ex.exercise_name?.toLowerCase().split(" ").filter(w => w.length > 3) || [];
            match = (logWorkout.workout_exercises || []).find(we => kw.some(k => (we.exercises?.name || "").toLowerCase().includes(k)));
          }
          if (match) nl[match.id] = { sets: ex.sets || [], last_weight: ex.last_set_weight || 0, last_reps: ex.last_set_reps || 0, notes: ex.notes || "" };
        });
        setSesLogs(nl);
      }
    } catch (e) { console.error(e); }
    setSesParsing(false);
  };

  const saveSesLog = async () => {
    if (!logClient || !logWorkout) return;
    const { data: ses, error } = await supabase.from("session_logs").insert({ client_id: logClient.clientRecordId, workout_id: logWorkout.id, logged_by: user.id, coach_notes: sesNotes, completed_at: new Date().toISOString(), day_number: logWorkout.day_number }).select().single();
    if (error) { showNotif("Save failed: " + error.message); return; }
    const toIns = [];
    Object.entries(sesLogs).forEach(([weId, log]) => {
      if (log.sets?.length > 0) {
        log.sets.forEach(s => { if (s.weight || s.reps) toIns.push({ session_log_id: ses.id, workout_exercise_id: weId, set_number: s.set_number, weight: s.weight || 0, reps_completed: s.reps || 0, notes: s.notes || "" }); });
      } else if (log.last_weight !== undefined && (log.last_weight || log.last_reps)) {
        toIns.push({ session_log_id: ses.id, workout_exercise_id: weId, set_number: 1, weight: log.last_weight || 0, reps_completed: log.last_reps || 0, notes: log.notes || "" });
      }
    });
    if (toIns.length > 0) await supabase.from("set_logs").insert(toIns);
    showNotif("Session logged!"); setLogWorkout(null); setLogClient(null);
    if (selClient) await loadClientSessionsForCoach(selClient.id);
  };

  // ── STRUCTURAL BALANCE ────────────────────────────────────────────────────
  const calculateStructuralBalance = (rawLogs, bw) => {
    const cats = [
      { name: "Squat", keys: ["squat"], aW: bw, aR: 10 },
      { name: "RDL", keys: ["rdl", "romanian deadlift"], aW: bw, aR: 12 },
      { name: "Hip Flexor Raise", keys: ["hip flexor"], aW: bw * 0.1, aR: 20 },
      { name: "ATG Split Squat", keys: ["atg split squat", "split squat"], aW: bw * 0.25, aR: 8 },
      { name: "Pull-Ups", keys: ["pull-up", "pullup", "pull up", "chin-up", "chin up", "chinup"], aW: 0, aR: 12, bwOnly: true },
      { name: "Incline Press", keys: ["incline press"], aW: bw * 0.5, aR: 5 },
      { name: "Calf Raise", keys: ["calf raise", "calf"], aW: bw * 0.33, aR: 10 },
      { name: "Tibialis Raise", keys: ["tibialis"], aW: bw * 0.2, aR: 15 },
    ];
    return cats.map(cat => {
      const matched = rawLogs.filter(sl => {
        const n = (sl.workout_exercises?.exercises?.name || "").toLowerCase();
        return cat.keys.some(k => n.includes(k));
      });
      if (matched.length === 0) {
        return { name: cat.name, status: "weak", current: 0, detail: "No data yet", next: `Target: ${cat.bwOnly ? `${cat.aR} reps` : `${Math.round(cat.aW)} lbs × ${cat.aR} reps`}` };
      }
      let bestERM = 0, bestW = 0, bestR = 0;
      matched.forEach(sl => {
        const w = sl.weight || 0;
        const r = sl.reps_completed || 0;
        if (r === 0 || r >= 30) return;
        const orm = cat.bwOnly ? r * (1 + r / 30) : (w || 1) * (1 + r / 30);
        if (orm > bestERM) { bestERM = orm; bestW = w; bestR = r; }
      });
      if (bestERM === 0) {
        return { name: cat.name, status: "weak", current: 0, detail: "No data yet", next: `Target: ${cat.bwOnly ? `${cat.aR} reps` : `${Math.round(cat.aW)} lbs × ${cat.aR} reps`}` };
      }
      const apostleERM = cat.bwOnly ? cat.aR * (1 + cat.aR / 30) : cat.aW * (1 + cat.aR / 30);
      const pct = Math.min(100, Math.round((bestERM / apostleERM) * 100));
      const status = pct < 40 ? "weak" : pct < 75 ? "developing" : "strong";
      const detail = cat.bwOnly
        ? `${bestR} reps / apostle: ${cat.aR} reps`
        : `${bestW > 0 ? bestW + " lbs" : "BW"} × ${bestR} reps`;
      const next = status === "strong"
        ? "Apostle standard reached!"
        : `Target: ${cat.bwOnly ? `${cat.aR} reps` : `${Math.round(cat.aW)} lbs × ${cat.aR} reps`}`;
      return { name: cat.name, status, current: isNaN(pct) ? 0 : pct, detail, next };
    });
  };

  const loadBalanceData = async () => {
    if (!clientRecord) return;
    setBalanceLoading(true);
    const bw = profile?.body_weight || 150;
    const { data: sessions } = await supabase.from("session_logs").select("id").eq("client_id", clientRecord.id);
    if (!sessions || sessions.length === 0) { setBalanceData([]); setBalanceLoading(false); return; }
    const sessionIds = sessions.map(s => s.id);
    const { data: rawLogs } = await supabase.from("set_logs")
      .select("weight, reps_completed, session_log_id, workout_exercises(exercises(name))")
      .in("session_log_id", sessionIds);
    if (!rawLogs || rawLogs.length === 0) { setBalanceData([]); setBalanceLoading(false); return; }
    setBalanceData(calculateStructuralBalance(rawLogs, bw));
    setBalanceLoading(false);
  };

  const loadMarksData = async () => {
    if (!clientRecord) return;
    const { data: sessionIds } = await supabase
      .from("session_logs")
      .select("id")
      .eq("client_id", clientRecord.id);
    if (!sessionIds?.length) return;
    const { data: setLogs } = await supabase
      .from("set_logs")
      .select("workout_exercise_id, weight, reps_completed, workout_exercises(exercises(name))")
      .in("session_log_id", sessionIds.map(s => s.id));
    if (!setLogs) return;
    const bestByExercise = {};
    setLogs.forEach(sl => {
      const name = sl.workout_exercises?.exercises?.name;
      if (!name) return;
      const key = name.toLowerCase();
      if (!bestByExercise[key] || sl.weight > bestByExercise[key].weight ||
          (sl.weight === bestByExercise[key].weight && sl.reps_completed > bestByExercise[key].reps)) {
        bestByExercise[key] = { weight: sl.weight || 0, reps: sl.reps_completed || 0 };
      }
    });
    console.log("[marks] bestByExercise:", bestByExercise);
    setClientBestPerformances(bestByExercise);
  };

  // ── ASSIGN WORKOUT TO DAY ─────────────────────────────────────────────────
  const assignWorkoutToDay = async (workout) => {
    if (!selClientRecord || !coachCalDay) return;
    const { data: newProg, error: progErr } = await supabase.from("programs").insert({
      client_id: selClientRecord.id,
      name: workout.name,
      start_date: coachCalDay.dateStr,
      active: false,
      days_per_week: 1,
      weeks: 1,
    }).select().single();
    if (progErr || !newProg) { showNotif("Failed: " + (progErr?.message || "unknown")); return; }
    const { error: wktErr } = await supabase.from("workouts").update({ program_id: newProg.id, day_number: 1 }).eq("id", workout.id);
    if (wktErr) { showNotif("Failed to link workout: " + wktErr.message); return; }
    showNotif("Workout assigned!");
    closeCoachCalPopup();
    await loadAllPrograms();
    if (selClient) await openClient(selClient);
  };

  const closeCoachCalPopup = () => {
    setCoachCalPopup(false); setCoachCalAction(false); setCoachCalDayWorkout(null);
    setCoachCalAssignMode(null); setCoachCalSessionMode(null); setCoachCalMoveDate(""); setCoachCalEditNotes("");
  };

  const reloadCoachCalendarDates = async () => {
    if (!selClientRecord) return;
    const { data } = await supabase.from("session_logs").select("completed_at").eq("client_id", selClientRecord.id);
    const dates = [...new Set((data || []).map(s => s.completed_at.split("T")[0]))];
    setCoachClientCompletedDates(dates);
    if (selClient) {
      const { data: sessions } = await supabase.from("session_logs")
        .select("id, completed_at, coach_notes, client_notes, feel_rating, day_number, workout_id")
        .eq("client_id", selClientRecord.id).order("completed_at", { ascending: false }).limit(30);
      setClientSessions(sessions || []);
    }
    const { data: prog } = await supabase.from("programs").select("id, name, client_id, active, start_date, weeks, days_per_week, created_at, block_id, sequence_order").eq("client_id", selClientRecord.id).eq("active", true).maybeSingle();
    setCoachClientProg(prog || null);
    if (prog) {
      const { data: wkts } = await supabase.from("workouts").select("id, day_number, day_of_week, program_id").eq("program_id", prog.id);
      setCoachClientWorkouts(wkts || []);
    }
  };

  const deleteProgram = async (progId) => {
    const { data: wkts } = await supabase.from("workouts").select("id").eq("program_id", progId);
    const wktIds = (wkts || []).map(w => w.id);
    if (wktIds.length > 0) {
      await supabase.from("workout_exercises").delete().in("workout_id", wktIds);
      await supabase.from("workouts").delete().in("id", wktIds);
    }
    const { error } = await supabase.from("programs").delete().eq("id", progId);
    if (error) { showNotif("Delete failed: " + error.message); return; }
    setDeleteConfirmProgId(null);
    showNotif("Program deleted");
    await loadAllPrograms();
  };

  const deleteSession = async (sesId) => {
    const { error } = await supabase.from("session_logs").delete().eq("id", sesId);
    if (error) { showNotif("Delete failed: " + error.message); return; }
    showNotif("Session deleted");
    closeCoachCalPopup();
    await reloadCoachCalendarDates();
  };

  const loadStandaloneWorkoutForEdit = async (w) => {
    setViewingStandaloneWorkout(w);
    setStandaloneExerciseDraft({});
    const { data } = await supabase.from("workout_exercises")
      .select("id, order_index, sets, reps, exercises(id, name, tempo)")
      .eq("workout_id", w.id)
      .order("order_index");
    setStandaloneWorkoutExercises(data || []);
    setScreen("standalone-workout-editor");
  };

  const saveStandaloneWorkoutEdits = async () => {
    for (const we of standaloneWorkoutExercises) {
      const d = standaloneExerciseDraft[we.id] || {};
      const sets = d.sets !== undefined ? Number(d.sets) : we.sets;
      const reps = d.reps !== undefined ? String(d.reps) : (we.reps || "");
      await supabase.from("workout_exercises").update({ sets, reps }).eq("id", we.id);
      if (we.exercises?.id && d.tempo !== undefined) {
        await supabase.from("exercises").update({ tempo: d.tempo }).eq("id", we.exercises.id);
      }
    }
    showNotif("Workout saved!");
    setStandaloneExerciseDraft({});
  };

  const deleteStandaloneWorkout = async (workoutId) => {
    await supabase.from("workout_exercises").delete().eq("workout_id", workoutId);
    const { error } = await supabase.from("workouts").delete().eq("id", workoutId);
    if (error) { showNotif("Delete failed: " + error.message); return; }
    setDeleteConfirmWorkoutId(null);
    showNotif("Workout deleted");
    await loadAllPrograms();
  };

  const assignStandaloneWorkoutToClient = async (workout, clientProfileId) => {
    const { data: cr } = await supabase.from("clients").select("id").eq("profile_id", clientProfileId).maybeSingle();
    if (!cr) { showNotif("Client record not found"); return; }
    const jsDay = new Date().getDay();
    const ourDOW = jsDay === 0 ? 7 : jsDay;
    const { data: newProg, error: progErr } = await supabase.from("programs").insert({
      name: workout.name,
      client_id: cr.id,
      active: true,
      start_date: todayStr,
      weeks: 1,
      days_per_week: 1,
    }).select().single();
    if (progErr || !newProg) { showNotif("Failed to create program: " + (progErr?.message || "unknown")); return; }
    await supabase.from("workouts").update({ program_id: newProg.id, day_number: 1, day_of_week: ourDOW }).eq("id", workout.id);
    showNotif("Workout assigned!");
    setAssigningStandaloneWorkout(null);
    await loadAllPrograms();
  };

  const moveSession = async (sesId, newDateStr) => {
    if (!newDateStr) { showNotif("Pick a date"); return; }
    const { error } = await supabase.from("session_logs")
      .update({ completed_at: new Date(newDateStr + "T12:00:00").toISOString() })
      .eq("id", sesId);
    if (error) { showNotif("Move failed: " + error.message); return; }
    showNotif("Session moved to " + newDateStr);
    closeCoachCalPopup();
    await reloadCoachCalendarDates();
  };

  const excuseDay = async (dateStr) => {
    if (!selClientRecord) return;
    const { error } = await supabase.from("session_logs").insert({
      client_id: selClientRecord.id,
      coach_notes: "Excused by coach",
      completed_at: new Date(dateStr + "T12:00:00").toISOString(),
      logged_by: user.id,
    });
    if (error) { showNotif("Failed: " + error.message); return; }
    showNotif("Day marked as excused");
    closeCoachCalPopup();
    await reloadCoachCalendarDates();
  };

  const removeWorkout = async (workoutId) => {
    const { error } = await supabase.from("workouts").update({ day_of_week: null }).eq("id", workoutId);
    if (error) { showNotif("Remove failed: " + error.message); return; }
    showNotif("Workout removed from this day");
    closeCoachCalPopup();
    await reloadCoachCalendarDates();
  };

  const moveWorkoutToDate = async (workoutId, newDateStr) => {
    if (!newDateStr) { showNotif("Pick a date"); return; }
    const d = new Date(newDateStr + "T12:00:00");
    const jsDay = d.getDay();
    const ourDOW = jsDay === 0 ? 7 : jsDay;
    const { error } = await supabase.from("workouts").update({ day_of_week: ourDOW }).eq("id", workoutId);
    if (error) { showNotif("Move failed: " + error.message); return; }
    showNotif("Workout moved to " + newDateStr);
    closeCoachCalPopup();
    await reloadCoachCalendarDates();
  };

  const editSessionNotes = async (sesId, notes) => {
    const { error } = await supabase.from("session_logs").update({ coach_notes: notes }).eq("id", sesId);
    if (error) { showNotif("Save failed: " + error.message); return; }
    showNotif("Notes saved");
    closeCoachCalPopup();
    await reloadCoachCalendarDates();
  };

  // ── ASSIGN PROGRAM ────────────────────────────────────────────────────────
  const assignProgramToClient = async (programId, clientProfileId, startDate) => {
    const { data: cr } = await supabase.from("clients").select("id").eq("profile_id", clientProfileId).maybeSingle();
    if (!cr) { showNotif("Client record not found"); return; }
    await supabase.from("programs").update({ active: false }).eq("client_id", cr.id);
    await supabase.from("programs").update({ client_id: cr.id, active: true, start_date: startDate || new Date().toISOString().split("T")[0] }).eq("id", programId);
    showNotif("Program assigned!");
    setShowAssignProgram(false);
    await loadAllPrograms();
    const { data: activeProg } = await supabase.from("programs").select("id, name, client_id, active, start_date, weeks, days_per_week, created_at, block_id, sequence_order").eq("client_id", cr.id).eq("active", true).maybeSingle();
    setClientProg(activeProg || null);
    if (selClient) await openClient(selClient);
  };

  // ── EXERCISE LIBRARY ──────────────────────────────────────────────────────
  const saveExercise = async () => {
    // SQL needed: alter table exercises add column if not exists video_url text;
    if (!newExercise.name.trim()) { showNotif("Enter an exercise name"); return; }
    const { error } = await supabase.from("exercises").insert({
      name: newExercise.name.trim(),
      muscle_groups: newExercise.muscle_groups || null,
      tempo: newExercise.tempo || null,
      video_url: newExercise.video_url || null,
      cue_1: newExercise.cue_1 || null,
      cue_2: newExercise.cue_2 || null,
      cue_3: newExercise.cue_3 || null,
    });
    if (error) { showNotif("Error: " + error.message); return; }
    setNewExercise({ name: "", muscle_groups: "", tempo: "", video_url: "", cue_1: "", cue_2: "", cue_3: "" });
    setShowAddExercise(false);
    showNotif("Exercise added!");
    const { data } = await supabase.from("exercises").select("*").order("name", { ascending: true });
    if (data) setExercises(data);
  };

  // ── PROGRAM BUILDER ───────────────────────────────────────────────────────
  const initPbDays = (n) => {
    setPbDays(Array.from({ length: n }, (_, i) => ({
      day_number: i + 1,
      name: "",
      day_of_week: null,
      blocks: [{ name: "", exercises: [{ name: "", sets: 3, reps: "10", tempo: "", superset_group: "", is_timed: false, duration_seconds: "", each_side: false }] }]
    })));
  };

  const startPbVoice = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) { alert("Try Chrome."); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = "en-US";
    setPbVoiceListening(true);
    r.onresult = async (e) => {
      const t = e.results[0][0].transcript;
      setPbVoiceListening(false);
      setPbVoiceParsing(true);
      try {
        const data = await callFn("parse-program", { transcript: t });
        if (data.program) {
          const { name, weeks, days } = data.program;
          if (name) setPbProgName(name);
          if (weeks) setPbWeeks(weeks);
          if (days?.length) {
            setPbDaysPerWeek(days.length);
            setPbDays(days.map(d => ({
              day_number: d.day_number,
              name: d.name || `Day ${d.day_number}`,
              day_of_week: d.day_of_week || null,
              blocks: [{
                name: d.name || `Day ${d.day_number}`,
                exercises: (d.exercises || []).map(ex => ({
                  name: ex.name || "",
                  sets: ex.sets || 3,
                  reps: String(ex.reps || "10"),
                  tempo: ex.tempo || "",
                  superset_group: ex.superset_group || "",
                  is_timed: false, duration_seconds: "", each_side: false
                }))
              }]
            })));
          }
        }
      } catch (err) { console.error(err); showNotif("Parse failed"); }
      setPbVoiceParsing(false);
    };
    r.onerror = () => setPbVoiceListening(false);
    r.onend = () => setPbVoiceListening(false);
    r.start();
  };

  const saveProgram = async () => {
    // SQL needed: alter table programs add column if not exists days_per_week integer default 5;
    if (!pbProgName.trim()) { showNotif("Enter a program name"); return; }
    if (pbDays.length === 0) { showNotif("Add at least one workout day"); return; }

    const progPayload = {
      name: pbProgName.trim(), weeks: pbWeeks, days_per_week: pbDaysPerWeek, active: false,
      start_date: new Date().toISOString().split("T")[0],
    };

    const { data: savedProg, error: progErr } = await supabase.from("programs").insert(progPayload).select().single();
    if (progErr) { showNotif("Error saving program: " + progErr.message); return; }
    console.log("[save program] program id:", savedProg.id, "days:", pbDays.length);

    for (const day of pbDays) {
      const blocks = day.blocks || [{ name: day.name || "", exercises: day.exercises || [] }];
      for (const block of blocks) {
        const wktName = block.name || day.name || `Day ${day.day_number}`;
        const { data: wkt, error: wktErr } = await supabase.from("workouts").insert({
          program_id: savedProg.id, day_number: day.day_number,
          day_of_week: day.day_of_week || null,
          name: wktName, focus: wktName
        }).select().single();
        if (wktErr) { console.error("[save program] workout insert error:", wktErr); continue; }
        if (!wkt) continue;
        console.log("[save program] workout inserted: id=", wkt.id, "day=", day.day_number, "program_id=", savedProg.id, "name=", wktName);
        let exCount = 0;
        for (let ei = 0; ei < block.exercises.length; ei++) {
          const ex = block.exercises[ei];
          if (!ex.name?.trim()) { console.log("[save program] skipping blank exercise at index", ei); continue; }
          let exId = null;
          const { data: found, error: findErr } = await supabase.from("exercises").select("id").ilike("name", ex.name.trim()).maybeSingle();
          if (findErr) console.warn("[save program] exercise lookup error:", findErr.message);
          if (found) {
            exId = found.id;
            console.log("[save program] found exercise id=", exId, "name=", ex.name);
          } else {
            const { data: created, error: createErr } = await supabase.from("exercises").insert({ name: ex.name.trim(), tempo: ex.tempo || null }).select().single();
            console.log("[save program] created exercise id=", created?.id, "name=", ex.name, "err=", createErr?.message);
            if (created) exId = created.id;
          }
          if (!exId) { console.warn("[save program] no exId for", ex.name, "— skipping"); continue; }
          const wePayload = {
            workout_id: wkt.id, exercise_id: exId,
            sets: Number(ex.sets) || 3,
            reps: ex.is_timed ? null : String(ex.reps || "10"),
            tempo: ex.tempo || null,
            superset_group: ex.superset_group ? Number(ex.superset_group) : null,
            is_timed: ex.is_timed || false,
            duration_seconds: ex.is_timed ? (Number(ex.duration_seconds) || null) : null,
            each_side: ex.each_side || false,
            order_index: ei
          };
          const { data: weRow, error: weErr } = await supabase.from("workout_exercises").insert(wePayload).select().single();
          console.log("[save program] workout_exercise insert: workout_id=", wkt.id, "exercise_id=", exId, "name=", ex.name, "→ we_id=", weRow?.id, "err=", weErr?.message);
          if (!weErr) exCount++;
        }
        console.log("[save program] done: workout_id=", wkt.id, "exercises linked=", exCount, "/", block.exercises.length);
      }
    }
    setPbSaved(savedProg.id);
    showNotif("Program saved!");
    await loadAllPrograms();
  };

  const assignSavedProgram = async () => {
    if (!pbSaved || !pbAssignClient) { showNotif("Select a client"); return; }
    const { data: cr } = await supabase.from("clients").select("id").eq("profile_id", pbAssignClient).maybeSingle();
    if (!cr) { showNotif("Client record not found"); return; }
    await supabase.from("programs").update({ active: false }).eq("client_id", cr.id);
    await supabase.from("programs").update({ client_id: cr.id, active: true, start_date: assignStartDate || new Date().toISOString().split("T")[0] }).eq("id", pbSaved);
    showNotif("Assigned!");
    await loadAllPrograms();
  };

  // ── EXERCISE CARD ─────────────────────────────────────────────────────────
  const renderExCard = (we, i, standalone) => {
    const isAct = activeEx === i;
    const isDone = checked[i];
    const exName = we.exercises?.name || "Exercise";
    const videoUrl = we.exercises?.video_url || null;
    const videoId = getYouTubeId(videoUrl);
    const thumbUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
    const isTimed = (we.reps?.includes("sec") || we.reps?.includes("min")) || false;
    const hist = exerciseHistory[we.id];
    const isWorkoutCompleted = completedDates.includes(viewingDate);
    const loggedSets = isWorkoutCompleted
      ? (completedSessionLog?.set_logs || [])
          .filter(sl => sl.workout_exercise_id === we.id)
          .sort((a, b) => a.set_number - b.set_number)
      : [];

    let histText = null;
    if (hist) {
      const lw = hist.weight || 0;
      const lr = hist.reps || 0;
      const weightStr = lw > 0 ? `${lw} lbs` : "bodyweight";
      histText = `Last time: ${weightStr} × ${lr}`;
    }

    return (
      <div key={we.id || i} className={`ec ${isAct ? "active" : ""} ${isDone ? "complete" : ""} ${standalone ? "standalone" : ""}`}>
        <div className="et" onClick={() => setActiveEx(isAct ? null : i)}>
          <div className="enu">{String(i + 1).padStart(2, "0")}</div>
          <div className="em2">
            <div className="ena">{exName}</div>
            <div className="eset">{we.sets} sets · {we.reps}{we.exercises?.tempo ? ` · ${we.exercises.tempo}` : ""}</div>
            {histText && <div className="elast">{histText}</div>}
          </div>
          <div className={`eck ${isDone ? "chk" : ""}`} onClick={e => { e.stopPropagation(); setChecked(p => ({ ...p, [i]: !p[i] })); }}>
            {isDone && <span style={{ color: "white", fontSize: 11 }}>✓</span>}
          </div>
        </div>
        {isAct && (
          <div className="eex">
            {thumbUrl && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <img
                  src={thumbUrl}
                  alt={exName}
                  style={{ width: 72, height: 48, objectFit: "cover", borderRadius: 4, cursor: "pointer", flexShrink: 0 }}
                  onClick={() => setPreviewVideo({ url: videoUrl, videoId, name: exName })}
                />
                <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 600, color: "var(--ink)", marginBottom: 2 }}>{exName}</div>
                  <div style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setPreviewVideo({ url: videoUrl, videoId, name: exName })}>Tap to preview</div>
                </div>
              </div>
            )}
            {(we.exercises?.cue_1 || we.exercises?.cue_2) && (
              <div style={{ background: "var(--cr2)", padding: "10px 12px", borderRadius: "var(--r)", marginBottom: 10, fontSize: "var(--xs)", color: "var(--ink2)", lineHeight: 1.6 }}>
                <strong>Cues:</strong> {[we.exercises?.cue_1, we.exercises?.cue_2, we.exercises?.cue_3].filter(Boolean).join(" · ")}
              </div>
            )}
            {transcript && activeEx === i && (
              <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "10px", fontSize: "var(--xs)", color: "var(--ink2)", marginBottom: 8, lineHeight: 1.5 }}>
                {transcript}
                {transcript && <button style={{ display: "block", marginTop: 6, background: "none", border: "none", fontSize: "var(--xs)", color: "var(--ink3)", cursor: "pointer", padding: 0, textDecoration: "underline" }} onClick={() => setTranscript("")}>Clear</button>}
              </div>
            )}
            {isTimed && (
              <div className="trow" style={{ cursor: "pointer" }} onClick={() => {
                const repStr = we.reps || "";
                const minMatch = repStr.match(/(\d+)\s*min/);
                const secMatch = repStr.match(/(\d+)\s*sec/);
                const dur = minMatch ? parseInt(minMatch[1]) * 60 : secMatch ? parseInt(secMatch[1]) : 60;
                setTimerDuration(dur);
                setShowTimerOverlay(true);
              }}>
                <div className="tdis" style={{ fontSize: 28, fontWeight: 700 }}>{timer.secs > 0 || timer.running ? timer.fmt : (we.reps || "Timer")}</div>
                <button className="bs" style={{ fontSize: "var(--xs)" }}>Open Timer</button>
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              {Array.from({ length: we.sets || 1 }).map((_, s) => {
                const logged = loggedSets[s];
                return (
                  <div key={s} style={{ marginBottom: 8 }}>
                    <div className="sunit">Set {s + 1}</div>
                    {isWorkoutCompleted && logged ? (
                      <div style={{ display: "flex", gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div className="sunit">lbs logged</div>
                          <div className="sinp" style={{ background: "var(--cr2)", color: "var(--ink2)", cursor: "default", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {logged.weight > 0 ? logged.weight : "BW"}
                          </div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="sunit">reps logged</div>
                          <div className="sinp" style={{ background: "var(--cr2)", color: "var(--ink2)", cursor: "default", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {logged.reps_completed}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8 }}>
                        {!isTimed ? (<>
                          <div style={{ flex: 1 }}><div className="sunit">lbs</div><input className="sinp" tabIndex={0} placeholder={hist?.weight > 0 ? `${hist.weight}` : "0"} type="number" value={setData[`${i}-${s}-w`] || ""} onChange={e => setSetData(p => ({ ...p, [`${i}-${s}-w`]: e.target.value }))} /></div>
                          <div style={{ flex: 1 }}><div className="sunit">reps</div><input className="sinp" tabIndex={0} placeholder={hist?.reps ? `${hist.reps}` : "0"} type="number" value={setData[`${i}-${s}-r`] || ""} onChange={e => setSetData(p => ({ ...p, [`${i}-${s}-r`]: e.target.value }))} /></div>
                        </>) : (
                          <div style={{ flex: 1 }}><div className="sunit">seconds</div><input className="sinp" tabIndex={0} placeholder="0" type="number" value={setData[`${i}-${s}-t`] || ""} onChange={e => setSetData(p => ({ ...p, [`${i}-${s}-t`]: e.target.value }))} /></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="eact">
              {!isWorkoutCompleted && <>
                <button className={`mic ${listening && activeEx === i ? "on" : ""}`} onClick={() => startVoice(i)}>{listening && activeEx === i ? "⏺" : "🎙️"}</button>
                <button className="bs o" onClick={() => { go("messages", "messages"); setTriage("coach"); }}>📷 Form check</button>
              </>}
              {videoUrl && (
                <button className="bs o" onClick={() => setPreviewVideo({ url: videoUrl, videoId, name: exName })}>▶ Watch</button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderExercises = (exercises) => {
    const groups = groupExercises(exercises);
    return groups.map((item, gi) => {
      if (item.type === "group") {
        const color = SS_COLORS[(item.group - 1) % SS_COLORS.length];
        const rounds = item.exercises[0]?.sets || 2;
        return (
          <div key={`g-${item.group}-${gi}`} className="ss-group">
            <div className="ss-label" style={{ color }}>
              <div className="ss-dot" style={{ background: color }} />
              Superset · {rounds} rounds
            </div>
            <div className="ss-exs" style={{ borderLeftColor: color }}>
              {item.exercises.map(ex => renderExCard(ex, ex.globalIdx, false))}
            </div>
          </div>
        );
      }
      return renderExCard(item.ex, item.ex.globalIdx, true);
    });
  };

  // ── COACH RENDER ──────────────────────────────────────────────────────────
  if (isCoach && !["splash", "auth"].includes(screen)) {

    if (logWorkout) {
      const exList = (logWorkout.workout_exercises || []).sort((a, b) => a.order_index - b.order_index);
      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div className="cha" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="cthb" onClick={() => { setLogWorkout(null); setLogClient(null); }}>←</button>
              <div>
                <div className="che">Session Log</div>
                <div className="cht" style={{ fontSize: "var(--xl)" }}>{logClient?.full_name}</div>
                <div className="chs">{logWorkout.name} · {new Date().toLocaleDateString()}</div>
              </div>
            </div>
            <div style={{ padding: "16px 24px 0" }}>
              {sesTx && <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "10px 12px", fontSize: "var(--xs)", color: "var(--ink2)", marginBottom: 12, fontStyle: "italic" }}>"{sesTx}"</div>}
              {sesParsing && <div style={{ background: "var(--cr2)", padding: "12px", borderRadius: "var(--r)", fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 12, textAlign: "center" }}>Parsing...</div>}
              <button className={`bs ${sesListen ? "red" : ""}`} style={{ width: "100%", padding: "12px", marginBottom: 16 }} onClick={startSesVoice}>
                {sesListen ? "⏺ Listening..." : "🎙️ Log Whole Session by Voice"}
              </button>
            </div>
            <div style={{ padding: "0 24px 120px", overflowY: "auto", height: "calc(100vh - 220px)" }}>
              {exList.map(we => {
                const log = sesLogs[we.id] || {};
                const isAct = activeLogEx === we.id;
                const hasD = log.last_weight !== undefined || (log.sets?.length > 0);
                return (
                  <div key={we.id} style={{ background: "var(--wh)", border: `1px solid ${hasD ? "var(--grn)" : "var(--cr3)"}`, borderRadius: "var(--r)", marginBottom: 8, overflow: "hidden" }}>
                    <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={() => setActiveLogEx(isAct ? null : we.id)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{we.exercises?.name}</div>
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{we.sets} sets · {we.reps}{we.exercises?.tempo ? ` · ${we.exercises.tempo}` : ""}</div>
                        {hasD && <div style={{ fontSize: "var(--xs)", color: "var(--grn)", marginTop: 2 }}>✓ {log.sets?.length > 0 ? `${log.sets.length} sets` : `Last: ${log.last_weight > 0 ? `${log.last_weight} lbs` : "BW"} × ${log.last_reps}`}</div>}
                      </div>
                      <div style={{ fontSize: 14, color: "var(--ink3)" }}>{isAct ? "▲" : "▼"}</div>
                    </div>
                    {isAct && (
                      <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--cr2)" }}>
                        {Array.from({ length: we.sets || 1 }).map((_, s) => (
                          <div key={s}>
                            <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", margin: "10px 0 6px" }}>Set {s + 1}{s === (we.sets || 1) - 1 ? " (Last)" : ""}</div>
                            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: "var(--ink3)", marginBottom: 4 }}>Weight</div>
                                <input style={{ width: "100%", background: "var(--cr2)", border: "1px solid var(--cr3)", padding: "8px 10px", borderRadius: "var(--r)", fontSize: "var(--sm)", textAlign: "center", outline: "none" }}
                                  placeholder="lbs" type="number"
                                  value={(log.sets?.[s]?.weight) || (s === (we.sets || 1) - 1 ? log.last_weight || "" : "")}
                                  onChange={e => { const v = parseFloat(e.target.value) || 0; setSesLogs(p => { const sets = [...(p[we.id]?.sets || Array.from({ length: we.sets || 1 }, (_, i) => ({ set_number: i + 1, weight: 0, reps: 0, notes: "" })))]; sets[s] = { ...sets[s], set_number: s + 1, weight: v }; const last = sets[sets.length - 1]; return { ...p, [we.id]: { ...p[we.id], sets, last_weight: last.weight, last_reps: last.reps } }; }); }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: "var(--ink3)", marginBottom: 4 }}>Reps</div>
                                <input style={{ width: "100%", background: "var(--cr2)", border: "1px solid var(--cr3)", padding: "8px 10px", borderRadius: "var(--r)", fontSize: "var(--sm)", textAlign: "center", outline: "none" }}
                                  placeholder="reps" type="number"
                                  value={(log.sets?.[s]?.reps) || (s === (we.sets || 1) - 1 ? log.last_reps || "" : "")}
                                  onChange={e => { const v = parseInt(e.target.value) || 0; setSesLogs(p => { const sets = [...(p[we.id]?.sets || Array.from({ length: we.sets || 1 }, (_, i) => ({ set_number: i + 1, weight: 0, reps: 0, notes: "" })))]; sets[s] = { ...sets[s], set_number: s + 1, reps: v }; const last = sets[sets.length - 1]; return { ...p, [we.id]: { ...p[we.id], sets, last_weight: last.weight, last_reps: last.reps } }; }); }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <input style={{ width: "100%", background: "var(--cr2)", border: "1px solid var(--cr3)", padding: "8px 10px", borderRadius: "var(--r)", fontSize: "var(--xs)", outline: "none", marginTop: 6 }}
                          placeholder="Notes..." value={log.notes || ""} onChange={e => setSesLogs(p => ({ ...p, [we.id]: { ...p[we.id], notes: e.target.value } }))} />
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ marginTop: 8 }}>
                <div className="sl">Session Notes</div>
                <textarea style={{ width: "100%", background: "var(--wh)", border: "1px solid var(--cr3)", padding: "12px", borderRadius: "var(--r)", fontSize: "var(--sm)", outline: "none", resize: "none", fontFamily: "var(--fb)", lineHeight: 1.6 }} rows={4}
                  placeholder="Form, energy, concepts discussed..." value={sesNotes} onChange={e => setSesNotes(e.target.value)} />
              </div>
            </div>
            <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, background: "var(--wh)", borderTop: "1px solid var(--cr3)", padding: "12px 24px 28px" }}>
              <button className="bp" onClick={saveSesLog}>Save Session Log</button>
            </div>
          </div>
          {coachCalPopup && coachCalDay && (
            <>
              <div className="cal-overlay" onClick={closeCoachCalPopup} />
              <div className="cal-popup">
                <div className="cal-popup-date">{fmtDate(coachCalDay.dateStr)}</div>
                {coachCalDay.status === "done" && (() => {
                  const s = clientSessions.find(x => x.completed_at?.split("T")[0] === coachCalDay.dateStr);
                  return <>
                    <div className="cal-popup-status" style={{ color: "var(--grn)" }}>✓ Completed</div>
                    {s && <div className="cal-popup-workout">{s.workouts?.name} · {s.set_logs?.length || 0} sets logged</div>}
                  </>;
                })()}
                {coachCalDay.status === "missed" && <>
                  <div className="cal-popup-status" style={{ color: "var(--red)" }}>✗ Missed</div>
                  <div className="cal-popup-workout">{coachClientProg?.name}</div>
                </>}
                {coachCalDay.status === "scheduled" && <>
                  <div className="cal-popup-status">Upcoming</div>
                  <div className="cal-popup-workout">{coachCalDayWorkout?.name || coachClientProg?.name}</div>
                </>}
                {coachCalDay.status === "today" && <>
                  <div className="cal-popup-status">Today's Workout</div>
                  <div className="cal-popup-workout">{coachCalDayWorkout?.name || coachClientProg?.name}</div>
                </>}
                {coachCalDay.status === "rest" && <div className="cal-popup-status" style={{ color: "var(--ink3)" }}>Rest Day</div>}
                <div className="cal-popup-actions">
                  {!coachCalAction ? (
                    <button className="bs" style={{ padding: "12px" }} onClick={() => setCoachCalAction(true)}>+ Add</button>
                  ) : <>
                    <button className="cal-action-btn" onClick={() => { showNotif("Coming soon — this is being built"); setCoachCalPopup(false); setCoachCalAction(false); setCoachCalDayWorkout(null); setCoachCalAssignMode(null); }}>Assign Workout</button>
                    <button className="cal-action-btn" onClick={() => { showNotif("Coming soon — this is being built"); setCoachCalPopup(false); setCoachCalAction(false); setCoachCalDayWorkout(null); setCoachCalAssignMode(null); }}>Assign Program</button>
                    <button className="cal-action-btn" onClick={() => { showNotif("Coming soon — this is being built"); setCoachCalPopup(false); setCoachCalAction(false); setCoachCalDayWorkout(null); setCoachCalAssignMode(null); }}>Assign Block</button>
                  </>}
                  <button className="bs o" style={{ padding: "10px" }} onClick={() => { setCoachCalPopup(false); setCoachCalAction(false); setCoachCalDayWorkout(null); setCoachCalAssignMode(null); }}>Close</button>
                </div>
              </div>
            </>
          )}
        </>
      );
    }

    if (screen === "standalone-workout-editor" && viewingStandaloneWorkout) {
      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div className="cha" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="cthb" onClick={() => { setScreen("coach-home"); setCoachTab("programs"); setViewingStandaloneWorkout(null); setStandaloneWorkoutExercises([]); setStandaloneExerciseDraft({}); }}>←</button>
              <div style={{ flex: 1 }}>
                <div className="che">Coach View</div>
                <div className="cht" style={{ fontSize: "var(--xl)" }}>{viewingStandaloneWorkout.name}</div>
              </div>
            </div>
            <div style={{ padding: "0 24px 120px", overflowY: "auto", height: "calc(100vh - 120px)" }}>
              <div className="sl" style={{ marginBottom: 8 }}>Exercises</div>
              {standaloneWorkoutExercises.length === 0 && (
                <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", marginBottom: 16 }}>No exercises in this workout.</div>
              )}
              {standaloneWorkoutExercises.map((we, i) => {
                const d = standaloneExerciseDraft[we.id] || {};
                const exName = we.exercises?.name || "Exercise";
                const sets = d.sets !== undefined ? d.sets : (we.sets ?? "");
                const reps = d.reps !== undefined ? d.reps : (we.reps ?? "");
                const tempo = d.tempo !== undefined ? d.tempo : (we.exercises?.tempo ?? "");
                return (
                  <div key={we.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px 16px", marginBottom: 10 }}>
                    <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600, marginBottom: 10 }}>{String(i + 1).padStart(2, "0")} {exName}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                      <div className="fl" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "var(--xs)", letterSpacing: "1px" }}>Sets</label>
                        <input className="fi" type="number" value={sets} onChange={e => setStandaloneExerciseDraft(p => ({ ...p, [we.id]: { ...p[we.id], sets: e.target.value } }))} placeholder="3" />
                      </div>
                      <div className="fl" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "var(--xs)", letterSpacing: "1px" }}>Reps</label>
                        <input className="fi" value={reps} onChange={e => setStandaloneExerciseDraft(p => ({ ...p, [we.id]: { ...p[we.id], reps: e.target.value } }))} placeholder="10" />
                      </div>
                      <div className="fl" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "var(--xs)", letterSpacing: "1px" }}>Tempo</label>
                        <input className="fi" value={tempo} onChange={e => setStandaloneExerciseDraft(p => ({ ...p, [we.id]: { ...p[we.id], tempo: e.target.value } }))} placeholder="31X0" />
                      </div>
                    </div>
                  </div>
                );
              })}
              <button className="bp grn" style={{ marginTop: 8 }} onClick={saveStandaloneWorkoutEdits}>Save Changes</button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "workout-builder") {
      const addWbEx = () => setWorkoutDraft(p => ({ ...p, exercises: [...p.exercises, { name: "", sets: 3, reps: "10", tempo: "", superset_group: "" }] }));
      const removeWbEx = (i) => setWorkoutDraft(p => ({ ...p, exercises: p.exercises.filter((_, xi) => xi !== i) }));
      const updateWbEx = (i, field, val) => setWorkoutDraft(p => { const exs = [...p.exercises]; exs[i] = { ...exs[i], [field]: val }; return { ...p, exercises: exs }; });
      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div className="cha" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="cthb" onClick={() => { setScreen("coach-home"); setCoachTab("programs"); setWorkoutDraft({ name: "", focus: "", exercises: [] }); loadAllPrograms(); }}>←</button>
              <div style={{ flex: 1 }}>
                <div className="che">Coach View</div>
                <div className="cht" style={{ fontSize: "var(--xl)" }}>New Workout</div>
              </div>
            </div>
            <div style={{ padding: "0 24px 120px", overflowY: "auto", height: "calc(100vh - 120px)" }}>
              {workoutBuilderSaved && (
                <div style={{ background: "#f0f8f0", border: "1px solid #c0dcc0", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 16 }}>
                  <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--grn)", fontWeight: 600 }}>✓ Workout Saved</div>
                </div>
              )}
              <div className="fl" style={{ marginBottom: 12 }}>
                <label>Workout Name</label>
                <input className="fi" value={workoutDraft.name} onChange={e => setWorkoutDraft(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Lower Body Strength Day" />
              </div>
              <div className="fl" style={{ marginBottom: 16 }}>
                <label>Focus / Description</label>
                <input className="fi" value={workoutDraft.focus} onChange={e => setWorkoutDraft(p => ({ ...p, focus: e.target.value }))} placeholder="e.g. Knee-dominant + posterior chain" />
              </div>
              <div className="sl" style={{ marginBottom: 8 }}>Exercises</div>
              {workoutDraft.exercises.map((ex, i) => (
                <div key={i} className="pb-ex" style={{ marginBottom: 10 }}>
                  <button className="pb-rm" onClick={() => removeWbEx(i)}>✕</button>
                  <div style={{ marginBottom: 6 }}>
                    <div className="pb-lbl">Exercise Name</div>
                    <input className="pb-inp" value={ex.name} onChange={e => updateWbEx(i, "name", e.target.value)} placeholder="Search exercises..." list={`wb-ex-list-${i}`} />
                    <datalist id={`wb-ex-list-${i}`}>
                      {exercises.filter(e => e.name?.toLowerCase().includes(ex.name?.toLowerCase() || "")).slice(0, 10).map(e => <option key={e.id} value={e.name} />)}
                    </datalist>
                  </div>
                  <div className="pb-ex-row">
                    <div><div className="pb-lbl">Sets</div><input className="pb-inp" type="number" value={ex.sets} onChange={e => updateWbEx(i, "sets", e.target.value)} placeholder="3" /></div>
                    <div><div className="pb-lbl">Reps</div><input className="pb-inp" value={ex.reps} onChange={e => updateWbEx(i, "reps", e.target.value)} placeholder="10" /></div>
                    <div><div className="pb-lbl">Tempo</div><input className="pb-inp" value={ex.tempo} onChange={e => updateWbEx(i, "tempo", e.target.value)} placeholder="31X0" /></div>
                    <div><div className="pb-lbl">SS Group</div><input className="pb-inp" type="number" value={ex.superset_group} onChange={e => updateWbEx(i, "superset_group", e.target.value)} placeholder="—" /></div>
                  </div>
                </div>
              ))}
              <button className="bs o" style={{ width: "100%", marginBottom: 20 }} onClick={addWbEx}>+ Add Exercise</button>
              <button className="bp" disabled={!workoutDraft.name.trim()} onClick={async () => {
                if (!workoutDraft.name.trim()) { showNotif("Enter a workout name"); return; }
                const { data: wkt, error } = await supabase.from("workouts").insert({ name: workoutDraft.name.trim(), focus: workoutDraft.focus || null, day_number: 1 }).select().single();
                if (error) { showNotif("Error: " + error.message); return; }
                for (let ei = 0; ei < workoutDraft.exercises.length; ei++) {
                  const ex = workoutDraft.exercises[ei];
                  if (!ex.name.trim()) continue;
                  let exId = null;
                  const { data: found } = await supabase.from("exercises").select("id").ilike("name", ex.name.trim()).maybeSingle();
                  if (found) { exId = found.id; } else {
                    const { data: created } = await supabase.from("exercises").insert({ name: ex.name.trim(), tempo: ex.tempo || null }).select().single();
                    if (created) exId = created.id;
                  }
                  if (!exId) continue;
                  await supabase.from("workout_exercises").insert({ workout_id: wkt.id, exercise_id: exId, sets: Number(ex.sets) || 3, reps: String(ex.reps || "10"), superset_group: ex.superset_group ? Number(ex.superset_group) : null, order_index: ei });
                }
                setWorkoutBuilderSaved(true);
                showNotif("Workout saved!");
                await loadAllPrograms();
              }}>Save Workout</button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "block-builder") {
      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div className="cha" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="cthb" onClick={() => { setScreen("coach-home"); setBuildingBlock(false); setBlockDraft({ id: null, name: "", goal: "", programs: [] }); }}>←</button>
              <div style={{ flex: 1 }}>
                <div className="che">Coach View</div>
                <div className="cht" style={{ fontSize: "var(--xl)" }}>{blockDraft.id ? "Edit Block" : "New Block"}</div>
              </div>
            </div>
            <div style={{ padding: "0 24px 120px", overflowY: "auto", height: "calc(100vh - 120px)" }}>
              <div className="fl" style={{ marginBottom: 12 }}>
                <label>Block Name</label>
                <input className="fi" value={blockDraft.name} onChange={e => setBlockDraft(p => ({ ...p, name: e.target.value }))} placeholder="e.g. 16-Week Squat Focus" />
              </div>
              <div className="fl" style={{ marginBottom: 16 }}>
                <label>Block Goal</label>
                <input className="fi" value={blockDraft.goal} onChange={e => setBlockDraft(p => ({ ...p, goal: e.target.value }))} placeholder="e.g. Build squat strength from the ground up" />
              </div>
              {blockDraft.programs.length > 0 && (
                <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 16 }}>
                  <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", marginBottom: 4 }}>Total Duration</div>
                  <div style={{ fontFamily: "var(--fd)", fontSize: "var(--2xl)", fontWeight: 600, color: "var(--red)" }}>
                    {blockDraft.programs.reduce((sum, bp) => sum + (allPrograms.find(x => x.id === bp.id)?.weeks || 0), 0)} weeks
                  </div>
                </div>
              )}
              <div className="sl">Programs in Block</div>
              {blockDraft.programs.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", marginBottom: 12 }}>No programs added yet</div>}
              {blockDraft.programs.map((bp, i) => {
                const progData = allPrograms.find(x => x.id === bp.id);
                return (
                  <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600, color: "var(--cr3)", width: 28, flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{progData?.name || "Unknown"}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{progData?.weeks || "?"} weeks</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {i > 0 && <button className="bs o" style={{ padding: "4px 8px" }} onClick={() => setBlockDraft(p => { const prgs = [...p.programs]; [prgs[i-1], prgs[i]] = [prgs[i], prgs[i-1]]; return { ...p, programs: prgs }; })}>↑</button>}
                      {i < blockDraft.programs.length - 1 && <button className="bs o" style={{ padding: "4px 8px" }} onClick={() => setBlockDraft(p => { const prgs = [...p.programs]; [prgs[i], prgs[i+1]] = [prgs[i+1], prgs[i]]; return { ...p, programs: prgs }; })}>↓</button>}
                      <button className="pb-rm" style={{ position: "relative", top: "auto", right: "auto" }} onClick={() => setBlockDraft(p => ({ ...p, programs: p.programs.filter((_, xi) => xi !== i) }))}>✕</button>
                    </div>
                  </div>
                );
              })}
              <div style={{ marginBottom: 16 }}>
                <div className="sl" style={{ marginBottom: 8 }}>Add Program to Block</div>
                {allPrograms.filter(p => !blockDraft.programs.find(bp => bp.id === p.id)).length === 0 && (
                  <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", marginBottom: 8 }}>All programs already added</div>
                )}
                {allPrograms.filter(p => !blockDraft.programs.find(bp => bp.id === p.id)).map(p => (
                  <div key={p.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                    onClick={() => setBlockDraft(prev => ({ ...prev, programs: [...prev.programs, { id: p.id }] }))}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{p.weeks || 4} weeks · {p.days_per_week || 5}x/week</div>
                    </div>
                    <div style={{ fontSize: "var(--sm)", color: "var(--red)", fontWeight: 600 }}>+ Add</div>
                  </div>
                ))}
              </div>
              <button className="bp" style={{ marginTop: 8 }} disabled={!blockDraft.name.trim()} onClick={async () => {
                if (!blockDraft.name.trim()) { showNotif("Enter a block name"); return; }
                let blockId = blockDraft.id;
                if (blockId) {
                  const { error } = await supabase.from("training_blocks")
                    .update({ name: blockDraft.name.trim(), goal: blockDraft.goal || null })
                    .eq("id", blockId);
                  if (error) { showNotif("Error: " + error.message); return; }
                  await supabase.from("programs").update({ block_id: null, sequence_order: null }).eq("block_id", blockId);
                } else {
                  const { data: newBlock, error } = await supabase.from("training_blocks")
                    .insert({ name: blockDraft.name.trim(), goal: blockDraft.goal || null, coach_id: user.id })
                    .select().single();
                  if (error) { showNotif("Error: " + error.message); return; }
                  blockId = newBlock.id;
                }
                for (let i = 0; i < blockDraft.programs.length; i++) {
                  await supabase.from("programs").update({ block_id: blockId, sequence_order: i + 1 }).eq("id", blockDraft.programs[i].id);
                }
                showNotif(blockDraft.id ? "Block updated!" : "Block saved!");
                const { data: blkData } = await supabase.from("training_blocks").select("*").eq("coach_id", user.id).order("created_at", { ascending: false });
                if (blkData) setAllBlocks(blkData);
                await loadAllPrograms();
                setScreen("coach-home"); setBuildingBlock(false); setBlockDraft({ id: null, name: "", goal: "", programs: [] });
              }}>Save Block</button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "program-builder") {
      const activeDays = pbDays.length === pbDaysPerWeek
        ? pbDays
        : Array.from({ length: pbDaysPerWeek }, (_, i) => pbDays[i] || {
            day_number: i + 1, name: "", day_of_week: null,
            blocks: [{ name: "", exercises: [{ name: "", sets: 3, reps: "10", tempo: "", superset_group: "" }] }]
          });

      const updateDay = (di, field, val) => setPbDays(prev => { const d = [...prev]; d[di] = { ...d[di], [field]: val }; return d; });
      const updateBlockName = (di, bi, val) => setPbDays(prev => { const d = [...prev]; const blks = [...d[di].blocks]; blks[bi] = { ...blks[bi], name: val }; d[di] = { ...d[di], blocks: blks }; return d; });
      const updateEx = (di, bi, ei, field, val) => setPbDays(prev => { const d = [...prev]; const blks = [...d[di].blocks]; const exs = [...blks[bi].exercises]; exs[ei] = { ...exs[ei], [field]: val }; blks[bi] = { ...blks[bi], exercises: exs }; d[di] = { ...d[di], blocks: blks }; return d; });
      const SS_COLORS = ["#e06b6b", "#e09e6b", "#6bb3e0", "#8c6be0", "#6be09e"];
      const toggleSuperset = (di, bi, ei) => setPbDays(prev => {
        const d = [...prev]; const blks = [...d[di].blocks]; const exs = [...blks[bi].exercises];
        const currG = Number(exs[ei].superset_group) || 0;
        const nextG = Number(exs[ei + 1].superset_group) || 0;
        if (currG && currG === nextG) {
          // Unlink: split the group at this boundary
          let runStart = ei;
          while (runStart > 0 && Number(exs[runStart - 1].superset_group) === currG) runStart--;
          let runEnd = ei + 1;
          while (runEnd < exs.length - 1 && Number(exs[runEnd + 1].superset_group) === currG) runEnd++;
          const leftCount = ei - runStart + 1;
          const rightCount = runEnd - ei;
          if (leftCount === 1) exs[runStart] = { ...exs[runStart], superset_group: "" };
          if (rightCount === 1) {
            exs[ei + 1] = { ...exs[ei + 1], superset_group: "" };
          } else {
            const maxG = exs.reduce((m, e) => Math.max(m, Number(e.superset_group) || 0), 0);
            const newG = String(maxG + 1);
            for (let i = ei + 1; i <= runEnd; i++) {
              if (Number(exs[i].superset_group) === currG) exs[i] = { ...exs[i], superset_group: newG };
            }
          }
        } else {
          // Link: merge into one group
          let targetG;
          if (currG && nextG) {
            targetG = currG;
            for (let i = 0; i < exs.length; i++) {
              if (Number(exs[i].superset_group) === nextG) exs[i] = { ...exs[i], superset_group: String(currG) };
            }
          } else if (currG) {
            targetG = currG;
          } else if (nextG) {
            targetG = nextG;
          } else {
            const maxG = exs.reduce((m, e) => Math.max(m, Number(e.superset_group) || 0), 0);
            targetG = maxG + 1;
          }
          exs[ei] = { ...exs[ei], superset_group: String(targetG) };
          exs[ei + 1] = { ...exs[ei + 1], superset_group: String(targetG) };
        }
        blks[bi] = { ...blks[bi], exercises: exs }; d[di] = { ...d[di], blocks: blks }; return d;
      });
      const addEx = (di, bi) => setPbDays(prev => { const d = [...prev]; const blks = [...d[di].blocks]; blks[bi] = { ...blks[bi], exercises: [...blks[bi].exercises, { name: "", sets: 3, reps: "10", tempo: "", superset_group: "", is_timed: false, duration_seconds: "", each_side: false }] }; d[di] = { ...d[di], blocks: blks }; return d; });
      const removeEx = (di, bi, ei) => setPbDays(prev => { const d = [...prev]; const blks = [...d[di].blocks]; blks[bi] = { ...blks[bi], exercises: blks[bi].exercises.filter((_, x) => x !== ei) }; d[di] = { ...d[di], blocks: blks }; return d; });
      const addBlock = (di) => setPbDays(prev => { const d = [...prev]; d[di] = { ...d[di], blocks: [...d[di].blocks, { name: "", exercises: [{ name: "", sets: 3, reps: "10", tempo: "", superset_group: "", is_timed: false, duration_seconds: "", each_side: false }] }] }; return d; });
      const removeBlock = (di, bi) => setPbDays(prev => { const d = [...prev]; d[di] = { ...d[di], blocks: d[di].blocks.filter((_, x) => x !== bi) }; return d; });

      const changeDays = (delta) => {
        const n = Math.max(1, Math.min(7, pbDaysPerWeek + delta));
        setPbDaysPerWeek(n);
        setPbDays(Array.from({ length: n }, (_, i) => pbDays[i] || {
          day_number: i + 1, name: "", day_of_week: null,
          blocks: [{ name: "", exercises: [{ name: "", sets: 3, reps: "10", tempo: "", superset_group: "", is_timed: false, duration_seconds: "", each_side: false }] }]
        }));
      };

      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div className="cha" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="cthb" onClick={() => { setScreen("coach-home"); setPbSaved(null); setPbProgName(""); setPbDays([]); setPbBlockName(""); setPbBlockGoal(""); setPbSequenceOrder(1); }}>←</button>
              <div style={{ flex: 1 }}>
                <div className="che">Coach View</div>
                <div className="cht" style={{ fontSize: "var(--xl)" }}>Builder</div>
              </div>
            </div>
            <div style={{ padding: "0 24px 120px", overflowY: "auto", height: "calc(100vh - 120px)" }}>
              <button
                className={`bs ${pbVoiceListening ? "red" : ""}`}
                style={{ width: "100%", padding: "14px", marginBottom: 20, fontSize: "var(--md)" }}
                onClick={startPbVoice}
                disabled={pbVoiceParsing}
              >
                {pbVoiceListening ? "⏺ Listening..." : pbVoiceParsing ? "Parsing..." : "🎙️ Build by Voice"}
              </button>

              <div className="fl" style={{ marginBottom: 16 }}>
                <label>Program Name</label>
                <input className="fi" value={pbProgName} onChange={e => setPbProgName(e.target.value)} placeholder="e.g. 12-Week Strength Foundation" />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                <div>
                  <div className="sl" style={{ marginBottom: 8 }}>Days / Week</div>
                  <div className="day-ctrl">
                    <button className="bs o" style={{ padding: "6px 14px", fontSize: "var(--lg)" }} onClick={() => changeDays(-1)}>−</button>
                    <span className="day-ctrl-val">{pbDaysPerWeek}</span>
                    <button className="bs o" style={{ padding: "6px 14px", fontSize: "var(--lg)" }} onClick={() => changeDays(1)}>+</button>
                  </div>
                </div>
                <div>
                  <div className="sl" style={{ marginBottom: 4 }}>Weeks</div>
                  <div className="range-val">{pbWeeks}</div>
                  <input type="range" className="pb-range" min={1} max={52} step={1} value={pbWeeks} onChange={e => setPbWeeks(Number(e.target.value))} />
                </div>
              </div>

              {activeDays.map((day, di) => (
                <div key={di} className="pb-day">
                  <div className="pb-day-hdr">Day {day.day_number}</div>
                  <div className="fl" style={{ marginBottom: 10 }}>
                    <label>Day Focus</label>
                    <input className="fi" value={day.name} onChange={e => updateDay(di, "name", e.target.value)} placeholder="e.g. Lower Body Strength" />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div className="sl" style={{ marginBottom: 6 }}>Day of Week</div>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[["M", 1], ["Tu", 2], ["W", 3], ["Th", 4], ["F", 5], ["Sa", 6], ["Su", 7]].map(([label, dow]) => {
                        const taken = activeDays.some((d2, i2) => i2 !== di && d2.day_of_week === dow);
                        const selected = day.day_of_week === dow;
                        return (
                          <button key={dow} disabled={taken} onClick={() => updateDay(di, "day_of_week", selected ? null : dow)} style={{ flex: 1, padding: "5px 0", borderRadius: 6, border: `1px solid ${selected ? "var(--ink)" : "var(--cr3)"}`, background: selected ? "var(--ink)" : "var(--cr)", color: selected ? "var(--wh)" : taken ? "var(--ink3)" : "var(--ink2)", fontSize: 10, fontWeight: selected ? 700 : 400, cursor: taken ? "default" : "pointer", opacity: taken ? 0.35 : 1 }}>{label}</button>
                        );
                      })}
                    </div>
                  </div>

                  {(day.blocks || []).map((block, bi) => (
                    <div key={bi} className="pb-block">
                      <div className="pb-block-hdr">
                        <div style={{ fontSize: "var(--xs)", letterSpacing: "1px", textTransform: "uppercase", color: "var(--ink3)", fontWeight: 600 }}>
                          {(day.blocks || []).length > 1 ? `Block ${bi + 1}` : "Workout"}
                        </div>
                        {(day.blocks || []).length > 1 && (
                          <button className="pb-rm" style={{ position: "relative", top: "auto", right: "auto" }} onClick={() => removeBlock(di, bi)}>✕</button>
                        )}
                      </div>
                      <div className="fl" style={{ marginBottom: 10 }}>
                        <label>Workout Name</label>
                        <input className="pb-inp" style={{ width: "100%", padding: "8px 10px", fontSize: "var(--xs)" }} value={block.name} onChange={e => updateBlockName(di, bi, e.target.value)} placeholder="e.g. Lower Body Strength Day" />
                      </div>
                      <div className="sl" style={{ marginBottom: 6 }}>Exercises</div>
                      {(block.exercises || []).map((ex, ei) => {
                        const ssGroup = Number(ex.superset_group) || 0;
                        const ssColor = ssGroup ? SS_COLORS[(ssGroup - 1) % SS_COLORS.length] : null;
                        const prevG = ei > 0 ? (Number(block.exercises[ei - 1].superset_group) || 0) : 0;
                        const nextExG = ei < block.exercises.length - 1 ? (Number(block.exercises[ei + 1].superset_group) || 0) : 0;
                        const isGroupStart = ssGroup > 0 && prevG !== ssGroup;
                        const linkedWithNext = ssGroup > 0 && nextExG === ssGroup;
                        return (
                          <Fragment key={ei}>
                            {isGroupStart && (
                              <div style={{ fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", fontWeight: 700, color: ssColor, paddingLeft: 14, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                                <div style={{ width: 7, height: 7, borderRadius: "50%", background: ssColor, flexShrink: 0 }} />
                                Superset · {ex.sets} rounds
                              </div>
                            )}
                            <div className="pb-ex" style={{ borderLeft: ssColor ? `3px solid ${ssColor}` : undefined }}>
                              <button className="pb-rm" onClick={() => removeEx(di, bi, ei)}>✕</button>
                              <div style={{ marginBottom: 6 }}>
                                <div className="pb-lbl">Exercise Name</div>
                                <input className="pb-inp" value={ex.name} onChange={e => updateEx(di, bi, ei, "name", e.target.value)} placeholder="Search exercises..." list={`ex-list-${di}-${bi}-${ei}`} />
                                <datalist id={`ex-list-${di}-${bi}-${ei}`}>
                                  {exercises.filter(e => e.name?.toLowerCase().includes(ex.name?.toLowerCase() || "")).slice(0, 10).map(e => <option key={e.id} value={e.name} />)}
                                </datalist>
                              </div>
                              <div className="pb-ex-row">
                                <div><div className="pb-lbl">Sets</div><input className="pb-inp" type="number" value={ex.sets} onChange={e => updateEx(di, bi, ei, "sets", e.target.value)} placeholder="3" /></div>
                                {ex.is_timed
                                  ? <div><div className="pb-lbl">Seconds</div><input className="pb-inp" type="number" value={ex.duration_seconds} onChange={e => updateEx(di, bi, ei, "duration_seconds", e.target.value)} placeholder="30" /></div>
                                  : <div><div className="pb-lbl">{ex.each_side ? "Reps / Side" : "Reps"}</div><input className="pb-inp" value={ex.reps} onChange={e => updateEx(di, bi, ei, "reps", e.target.value)} placeholder="10" /></div>
                                }
                                <div><div className="pb-lbl">Tempo</div><input className="pb-inp" value={ex.tempo} onChange={e => updateEx(di, bi, ei, "tempo", e.target.value)} placeholder="31X0" /></div>
                              </div>
                              <div className="pb-toggle">
                                <button className={`pb-tbtn${ex.is_timed ? " on" : ""}`} onClick={() => updateEx(di, bi, ei, "is_timed", !ex.is_timed)}>Timed</button>
                                <button className={`pb-tbtn${ex.each_side ? " on" : ""}`} onClick={() => updateEx(di, bi, ei, "each_side", !ex.each_side)}>Each Side</button>
                              </div>
                            </div>
                            {ei < block.exercises.length - 1 && (
                              <div className="pb-ss-wrap" style={linkedWithNext ? { margin: "-12px 0", height: "24px", zIndex: 3 } : undefined}>
                                <button className={`pb-ss-btn${linkedWithNext ? " active" : ""}`} style={linkedWithNext && ssColor ? { background: ssColor, borderColor: ssColor } : undefined} onClick={() => toggleSuperset(di, bi, ei)}>SS</button>
                              </div>
                            )}
                            {ei === block.exercises.length - 1 && <div className="pb-ex-gap" />}
                          </Fragment>
                        );
                      })}
                      <button className="bs o" style={{ width: "100%", marginTop: 4 }} onClick={() => addEx(di, bi)}>+ Add Exercise</button>
                    </div>
                  ))}
                  <button className="bs o" style={{ width: "100%", marginTop: 4, borderStyle: "dashed" }} onClick={() => addBlock(di)}>+ Add Workout</button>
                </div>
              ))}

              {!pbSaved ? (
                <button className="bp" style={{ marginTop: 8 }} onClick={saveProgram}>Save Program</button>
              ) : (
                <div style={{ background: "#f0f8f0", border: "1px solid #c0dcc0", borderRadius: "var(--r)", padding: 16, marginTop: 8 }}>
                  <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--grn)", marginBottom: 10, fontWeight: 600 }}>✓ Program Saved</div>
                  <div className="fl" style={{ marginBottom: 10 }}>
                    <label>Start Date</label>
                    <input type="date" className="fi" value={assignStartDate} onChange={e => setAssignStartDate(e.target.value)} />
                  </div>
                  <div className="fl" style={{ marginBottom: 10 }}>
                    <label>Assign to Client</label>
                    <select className="fi" value={pbAssignClient} onChange={e => setPbAssignClient(e.target.value)}>
                      <option value="">Select client...</option>
                      {clientList.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                    </select>
                  </div>
                  <button className="bp grn" onClick={assignSavedProgram}>Assign to Client</button>
                </div>
              )}
            </div>
          </div>
        </>
      );
    }

    if (selSession) {
      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--cr3)", display: "flex", alignItems: "center", gap: 12 }}>
              <button className="cthb" onClick={() => setSelSession(null)}>←</button>
              <div>
                <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>{selSession.workouts?.name}</div>
                <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{fmtDate(selSession.completed_at)} · {fmtTime(selSession.completed_at)}</div>
              </div>
            </div>
            <div style={{ padding: "20px 24px 100px", overflowY: "auto" }}>
              {(selSession.feel_rating || selSession.client_notes) && (
                <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", marginBottom: 6 }}>Client Feedback</div>
                  {selSession.feel_rating && (
                    <div style={{ marginBottom: selSession.client_notes ? 6 : 0 }}>
                      <span style={{ display: "inline-block", fontSize: "var(--xs)", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", padding: "3px 10px", borderRadius: 99, background: selSession.feel_rating === "strong" ? "var(--grn)" : selSession.feel_rating === "good" ? "var(--gold)" : "var(--red)", color: "var(--wh)" }}>
                        {selSession.feel_rating === "strong" ? "Strong 💪" : selSession.feel_rating === "good" ? "Good 👍" : "Tough 😤"}
                      </span>
                    </div>
                  )}
                  {selSession.client_notes && <div style={{ fontSize: "var(--xs)", fontStyle: "italic", color: "var(--ink2)", marginTop: 4 }}>"{selSession.client_notes}"</div>}
                </div>
              )}
              {selSession.coach_notes && (
                <div style={{ background: "var(--cr2)", borderRadius: "var(--r)", padding: "12px", marginBottom: 16 }}>
                  <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", marginBottom: 6 }}>Session Notes</div>
                  <div style={{ fontSize: "var(--sm)", fontStyle: "italic" }}>"{selSession.coach_notes}"</div>
                </div>
              )}
              <div className="sl">Exercises</div>
              {selSessionWorkoutExercises.length > 0 ? (
                selSessionWorkoutExercises.map((we, i) => {
                  const logs = (selSession.set_logs || []).filter(sl => sl.workout_exercise_id === we.id).sort((a, b) => a.set_number - b.set_number);
                  return (
                    <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8 }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600, marginBottom: 6 }}>{we.exercises?.name || "Exercise"}</div>
                      {logs.length > 0 ? (
                        logs.map((sl, si) => (
                          <div key={si} style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--xs)", color: "var(--ink3)", padding: "4px 0", borderTop: si > 0 ? "1px solid var(--cr2)" : "none" }}>
                            <span>Set {sl.set_number}</span>
                            <span style={{ fontWeight: 600, color: "var(--ink)" }}>{sl.weight > 0 ? `${sl.weight} lbs` : "BW"} × {sl.reps_completed} reps</span>
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Not logged</div>
                      )}
                    </div>
                  );
                })
              ) : (
                <>
                  {(!selSession.set_logs || selSession.set_logs.length === 0) && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No sets recorded</div>}
                  {Object.values((selSession.set_logs || []).reduce((acc, sl) => {
                    const name = sl.exercise_name || "Unknown Exercise";
                    if (!acc[name]) acc[name] = { name, sets: [] };
                    acc[name].sets.push(sl);
                    return acc;
                  }, {})).map((exLog, i) => (
                    <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8 }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600, marginBottom: 8 }}>{exLog.name}</div>
                      {exLog.sets.sort((a, b) => a.set_number - b.set_number).map((sl, si) => (
                        <div key={si} style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--xs)", color: "var(--ink3)", padding: "4px 0", borderTop: si > 0 ? "1px solid var(--cr2)" : "none" }}>
                          <span>Set {sl.set_number}</span>
                          <span style={{ fontWeight: 600, color: "var(--ink)" }}>{sl.weight > 0 ? `${sl.weight} lbs` : "BW"} × {sl.reps_completed} reps</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      );
    }

    if (selClient) {
      return (
        <>
          <style>{CSS}</style>
          {notif && <div className="notif"><div className="nd" />{notif}</div>}
          <div className="ca fu">
            <div style={{ padding: "16px 24px 0", borderBottom: "1px solid var(--cr3)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <button className="cthb" onClick={() => setSelClient(null)}>←</button>
                <div className="cla" style={{ width: 44, height: 44, fontSize: 16, flexShrink: 0 }}>{selClient.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>{selClient.full_name}</div>
                  <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Age {selClient.age || "—"} · {selClient.body_weight ? `${selClient.body_weight} lbs` : "BW not set"}</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <button className="bs red" style={{ padding: "10px" }} onClick={() => startLogSession(selClient)}>+ Log Session</button>
                <button className="bs o" style={{ padding: "10px" }} onClick={openClientMessages}>💬 Messages</button>
              </div>
              <div style={{ display: "flex", marginBottom: "-1px" }}>
                {["overview", "history", "progress", "calendar"].map(tab => (
                  <button key={tab} onClick={() => setCpTab(tab)} style={{ flex: 1, padding: "10px 2px", background: "none", border: "none", borderBottom: `2px solid ${cpTab === tab ? "var(--red)" : "transparent"}`, color: cpTab === tab ? "var(--ink)" : "var(--ink3)", fontSize: "10px", letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer", fontFamily: "var(--fb)", fontWeight: 500 }}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div ref={cThrRef} style={{ padding: "20px 24px 100px", overflowY: "auto", height: "calc(100vh - 230px)" }}>
              {cpTab === "overview" && (<>
                {(() => {
                  const clientCreatedAt = selClient.created_at ? new Date(selClient.created_at) : null;
                  const isNewClient = !clientCreatedAt || (Date.now() - clientCreatedAt.getTime() < 14 * 24 * 60 * 60 * 1000);
                  const dismissed = !!localStorage.getItem(`dismissed_checklist_${selClient.id}`);
                  if (!isNewClient || dismissed) return null;
                  const intakeReviewed = !!localStorage.getItem(`intake_reviewed_${selClient.id}`);
                  const programAssigned = !!clientProg || allPrograms.some(p => p.client_id === selClientRecord?.id && p.active);
                  const checks = [
                    { label: "Assign a program", done: programAssigned, action: () => setShowAssignProgram(true) },
                    { label: "Set billing status", done: !!(selClientRecord?.billing_status), action: null },
                    { label: "Send welcome message", done: clientMsgs.length > 0, action: openClientMessages },
                    { label: "Review intake form", done: intakeReviewed, action: () => { localStorage.setItem(`intake_reviewed_${selClient.id}`, "true"); window.location.reload(); } },
                  ];
                  return (
                    <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px", marginBottom: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", fontWeight: 600 }}>New client checklist</div>
                        <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "var(--xs)", color: "var(--ink3)" }} onClick={() => {
                          localStorage.setItem(`dismissed_checklist_${selClient.id}`, "true");
                          const next = { ...dismissedChecklists, [selClient.id]: true };
                          setDismissedChecklists(next);
                        }}>Dismiss</button>
                      </div>
                      {checks.map((c, i) => (
                        <div key={i} className="checklist-item" onClick={c.action || undefined} style={{ cursor: c.action ? "pointer" : "default" }}>
                          <div className={`check-circle ${c.done ? "done" : ""}`}>
                            {c.done && <span style={{ color: "white", fontSize: 10 }}>✓</span>}
                          </div>
                          <div style={{ fontSize: "var(--sm)", color: c.done ? "var(--ink3)" : "var(--ink)", textDecoration: c.done ? "line-through" : "none" }}>{c.label}</div>
                          {!c.done && c.action && <div style={{ marginLeft: "auto", fontSize: "var(--xs)", color: "var(--red)" }}>→</div>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div className="sl">Client Info</div>
                {[{ l: "Name", v: selClient.full_name }, { l: "Age", v: selClient.age ? `${selClient.age} yrs` : "—" }, { l: "Body Weight", v: selClient.body_weight ? `${selClient.body_weight} lbs` : "—" }, { l: "Sessions", v: `${clientSessions.length} logged` }].map((r, i) => (
                  <div key={i} className="ir2"><div className="il">{r.l}</div><div className="iv">{r.v || "—"}</div></div>
                ))}
                <div className="ir2">
                  <div className="il">Billing</div>
                  <select value={selClientRecord?.billing_status || "active"} onChange={async e => {
                    const v = e.target.value;
                    if (!selClientRecord) return;
                    await supabase.from("clients").update({ billing_status: v }).eq("id", selClientRecord.id);
                    setSelClientRecord(p => ({ ...p, billing_status: v }));
                  }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "var(--sm)", color: "var(--ink)", fontFamily: "var(--fb)" }}>
                    {["active","trial","inactive","pending"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div style={{ height: 12 }} />
                <div style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showAssignProgram ? 12 : 0 }}>
                    <div>
                      <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", marginBottom: 3 }}>Assigned Program</div>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600, color: clientProg ? "var(--ink)" : "var(--ink3)" }}>{clientProg?.name || "No program assigned"}</div>
                      {clientProg && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{clientProg.weeks || 4} weeks</div>}
                    </div>
                    <button className="bs o" style={{ padding: "6px 14px" }} onClick={() => setShowAssignProgram(p => !p)}>
                      {showAssignProgram ? "Cancel" : "Change Program"}
                    </button>
                  </div>
                  {showAssignProgram && (
                    <>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 4 }}>Start date:</div>
                        <input type="date" className="fi" value={assignStartDate} onChange={e => setAssignStartDate(e.target.value)} style={{ marginBottom: 8 }} />
                      </div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 8 }}>Select a program:</div>
                      <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 10 }}>
                        {allPrograms.map(p => (
                          <div key={p.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--cr2)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                            onClick={() => { assignProgramToClient(p.id, selClient.id, assignStartDate); setShowAssignProgram(false); }}>
                            <div>
                              <div style={{ fontSize: "var(--sm)", fontWeight: 600, color: p.id === clientProg?.id ? "var(--grn)" : "var(--ink)" }}>{p.name}{p.id === clientProg?.id ? " · current" : ""}</div>
                              <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{p.weeks || 4} weeks · {p.days_per_week || 5}x/week</div>
                            </div>
                            <div style={{ fontSize: "var(--xs)", color: "var(--red)" }}>Assign →</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="sl">Intake</div>
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 4, letterSpacing: "1px", textTransform: "uppercase" }}>Injuries</div>
                  {(selClient.injuries?.length > 0) ? selClient.injuries.map((x, i) => <span key={i} className="intake-chip injury">{x}</span>) : <span style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Not provided</span>}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 4, letterSpacing: "1px", textTransform: "uppercase" }}>Goals</div>
                  {(selClient.goals?.length > 0) ? selClient.goals.map((x, i) => <span key={i} className="intake-chip goal">{x}</span>) : <span style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Not provided</span>}
                </div>
                <div className="sl">Recent Sessions</div>
                {clientSessions.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No sessions yet</div>}
                {clientSessions.slice(0, 3).map((ses, i) => (
                  <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8, cursor: "pointer" }} onClick={() => setSelSession(ses)}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{ses.workouts?.name}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{fmtDate(ses.completed_at)}</div>
                    </div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 3 }}>{ses.set_logs?.length || 0} sets{ses.coach_notes ? " · notes" : ""}</div>
                  </div>
                ))}
                <div style={{ height: 16 }} />
                <div className="sl">Private Notes</div>
                <textarea
                  style={{ width: "100%", background: "var(--wh)", border: "1px solid var(--cr3)", padding: "12px", borderRadius: "var(--r)", fontSize: "var(--sm)", outline: "none", resize: "none", fontFamily: "var(--fb)", lineHeight: 1.6, marginBottom: 8 }}
                  rows={4} placeholder="Private notes about this client..."
                  value={coachPrivateNotes} onChange={e => setCoachPrivateNotes(e.target.value)}
                />
                <button className="bs" style={{ width: "100%" }} onClick={saveCoachNotes} disabled={savingNotes}>
                  {savingNotes ? "Saving..." : "Save Notes"}
                </button>
              </>)}
              {cpTab === "messages" && (<>
                <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingBottom: 80 }}>
                  {clientMsgs.length === 0 && <div style={{ textAlign: "center", color: "var(--ink3)", fontSize: "var(--xs)", marginTop: 40 }}>No messages yet</div>}
                  {clientMsgs.map((m, i) => {
                    const isMe = m.from_id === user.id;
                    return (
                      <div key={m.id || i} className={`bw ${isMe ? "me" : "them"}`}>
                        <div className="bsn">{isMe ? "Coach Max" : selClient.full_name}</div>
                        {m.media_url ? (m.media_type === "video" ? (<video src={m.media_url} controls style={{ maxWidth: "80%", borderRadius: 12 }} />) : (<div className="med"><img src={m.media_url} alt="form check" /></div>)) : (<div className={`bb ${isMe ? "me" : "them"}`}>{m.body}</div>)}
                        <div className="bt">{fmtTime(m.created_at)}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, background: "var(--cr)", borderTop: "1px solid var(--cr3)", padding: "10px 16px 24px", zIndex: 200 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <textarea className="cta" placeholder={`Message ${selClient.full_name?.split(" ")[0]}...`} rows={1} value={coachDrafts[selClient?.id] || ""} onChange={e => setCoachDrafts(p => ({ ...p, [selClient.id]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCoachMsg(); } }} />
                    <button className="csend" onClick={sendCoachMsg}>→</button>
                  </div>
                </div>
              </>)}
              {cpTab === "history" && (<>
                <div className="sl">Session History</div>
                {clientSessions.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No sessions yet</div>}
                {clientSessions.map((ses, i) => (
                  <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px", marginBottom: 10, cursor: "pointer" }} onClick={() => setSelSession(ses)}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{ses.workouts?.name}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{fmtDate(ses.completed_at)}</div>
                    </div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{ses.workouts?.focus} · {ses.set_logs?.length || 0} sets · {fmtTime(ses.completed_at)}</div>
                    {ses.coach_notes && <div style={{ background: "var(--cr2)", padding: "6px 10px", borderRadius: "var(--r)", fontSize: "var(--xs)", fontStyle: "italic", marginTop: 6 }}>"{ses.coach_notes}"</div>}
                  </div>
                ))}
              </>)}
              {cpTab === "progress" && (<>
                <div className="sl">Structural Balance</div>
                <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", marginBottom: 12 }}>{selClient.body_weight ? `Based on ${selClient.body_weight} lbs BW` : "BW not set"}</div>
                {clientBalanceLoading
                  ? <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>Calculating...</div>
                  : clientBalanceData.length === 0
                    ? <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>Complete some workouts to see structural balance.</div>
                    : clientBalanceData.map((b, i) => (
                        <div key={i} className="bac">
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><div className="ban">{b.name}</div><div className={`bas ${b.status}`}>{b.status}</div></div>
                          <div className="bab"><div className="baf" style={{ width: `${b.current}%`, background: statusColor(b.status) }} /></div>
                          <div className="bam"><span>{b.detail}</span><span>{b.current}%</span></div>
                          {b.next && <div style={{ fontSize: "var(--xs)", color: "var(--gold)", marginTop: 6 }}>→ {b.next}</div>}
                        </div>
                      ))
                }
              </>)}
              {cpTab === "calendar" && (() => {
                if (!coachClientProg) return (
                  <div style={{ textAlign: "center", padding: "32px 0" }}>
                    <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", marginBottom: 16 }}>No program assigned yet.</div>
                    <button className="bp" style={{ maxWidth: 240, margin: "0 auto" }} onClick={() => setShowAssignProgram(true)}>+ Assign Program</button>
                    {showAssignProgram && (
                      <div style={{ textAlign: "left", marginTop: 16 }}>
                        <input type="date" className="fi" value={assignStartDate} onChange={e => setAssignStartDate(e.target.value)} style={{ marginBottom: 10 }} />
                        {allPrograms.map(p => (
                          <div key={p.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--cr2)", cursor: "pointer", display: "flex", justifyContent: "space-between" }}
                            onClick={() => { assignProgramToClient(p.id, selClient.id, assignStartDate); setShowAssignProgram(false); }}>
                            <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{p.name}</div>
                            <div style={{ fontSize: "var(--xs)", color: "var(--red)" }}>Assign →</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
                const coachCalDays = () => {
                  if (!coachClientProg?.start_date) return [];
                  const coachDayMap = buildDayMapFromWorkouts(coachClientWorkouts) || getDayMapForProgram(coachClientProg.days_per_week || 5);
                  const startDate = new Date(coachClientProg.start_date + "T12:00:00");
                  const daysToMon = (startDate.getDay() + 6) % 7;
                  const weekStartMs = startDate.getTime() - daysToMon * 24 * 60 * 60 * 1000;
                  const todayMs = new Date(todayStr + "T12:00:00").getTime();
                  const days = [];
                  for (let w = 0; w < (coachClientProg.weeks || 4); w++) {
                    for (let d = 0; d < 7; d++) {
                      const ms = weekStartMs + (w * 7 + d) * 24 * 60 * 60 * 1000;
                      const date = new Date(ms);
                      const dateStr = date.toISOString().split("T")[0];
                      const dayNum = getProgramDay(coachClientProg.start_date, dateStr, coachDayMap);
                      const isToday = dateStr === todayStr;
                      const isPast = ms < todayMs;
                      const isDone = coachClientCompletedDates.includes(dateStr);
                      let status = "rest";
                      if (dayNum) {
                        if (isDone) status = "done";
                        else if (isToday) status = "today";
                        else if (isPast && dateStr !== todayStr) status = "missed";
                        else status = "scheduled";
                      }
                      days.push({ date, dateStr, dayNum, status, weekNum: w + 1 });
                    }
                  }
                  return days;
                };
                const cells = buildMonthCalGrid(coachClientCalMonth, coachClientProg, coachClientCompletedDates, coachClientWorkouts);
                const monthName = coachClientCalMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div className="cal-toggle">
                        <button className={`cal-toggle-btn ${coachCalView === "week" ? "on" : ""}`} onClick={() => { setCoachCalView("week"); setCoachWeekOffset(0); }}>Week</button>
                        <button className={`cal-toggle-btn ${coachCalView === "month" ? "on" : ""}`} onClick={() => setCoachCalView("month")}>Month</button>
                      </div>
                      {coachCalView === "month" && <>
                        <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600 }}>{monthName}</div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="bs o" style={{ padding: "5px 10px" }} onClick={() => { const m = new Date(coachClientCalMonth); m.setMonth(m.getMonth() - 1); setCoachClientCalMonth(m); }}>←</button>
                          <button className="bs o" style={{ padding: "5px 10px" }} onClick={() => { const m = new Date(coachClientCalMonth); m.setMonth(m.getMonth() + 1); setCoachClientCalMonth(m); }}>→</button>
                        </div>
                      </>}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 8, fontSize: "var(--xs)" }}>
                      <span style={{ color: "var(--grn)" }}>● Done</span>
                      <span style={{ color: "var(--red)" }}>● Missed</span>
                      <span style={{ color: "var(--ink)" }}>● Today</span>
                    </div>
                    {coachCalView === "week" ? (<>
                      {(() => {
                        const todayDate = new Date(todayStr + "T12:00:00");
                        const daysToMon = (todayDate.getDay() + 6) % 7;
                        const thisMondayMs = todayDate.getTime() - daysToMon * 24 * 60 * 60 * 1000;
                        const startMs = thisMondayMs + coachWeekOffset * 14 * 24 * 60 * 60 * 1000;
                        const todayMs = todayDate.getTime();
                        const coachDayMap = buildDayMapFromWorkouts(coachClientWorkouts) || getDayMapForProgram(coachClientProg.days_per_week || 5);
                        const progStartMs = new Date(coachClientProg.start_date + "T12:00:00").getTime();
                        const progEndMs = progStartMs + (coachClientProg.weeks || 4) * 7 * 24 * 60 * 60 * 1000;
                        const coachDays = [];
                        for (let i = 0; i < 14; i++) {
                          const ms = startMs + i * 24 * 60 * 60 * 1000;
                          const date = new Date(ms);
                          const dateStr = date.toISOString().split("T")[0];
                          const inProgram = ms >= progStartMs && ms < progEndMs;
                          const dayNum = inProgram ? getProgramDay(coachClientProg.start_date, dateStr, coachDayMap) : null;
                          const isToday = dateStr === todayStr;
                          const isPast = ms < todayMs;
                          const isDone = coachClientCompletedDates.includes(dateStr);
                          let status = "rest";
                          if (dayNum) {
                            if (isDone) status = "done";
                            else if (isToday) status = "today";
                            else if (isPast) status = "missed";
                            else status = "scheduled";
                          } else if (isToday) {
                            status = "today";
                          }
                          coachDays.push({ date, dateStr, dayNum, status });
                        }
                        const startLabel = new Date(startMs).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                        const endLabel = new Date(startMs + 13 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                        return (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                              <button className="bs o" onClick={() => setCoachWeekOffset(p => p - 1)}>← Prev</button>
                              <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", letterSpacing: "1px" }}>{startLabel} – {endLabel}</div>
                              <button className="bs o" onClick={() => setCoachWeekOffset(p => p + 1)}>Next →</button>
                            </div>
                            {[coachDays.slice(0, 7), coachDays.slice(7, 14)].map((wkDays, wi) => (
                              <div key={wi} style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", marginBottom: 6 }}>
                                  Week of {wkDays[0].date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
                                  {["M","T","W","T","F","S","S"].map((h, i) => <div key={i} style={{ fontSize: 9, textAlign: "center", color: "var(--ink3)", paddingBottom: 3 }}>{h}</div>)}
                                  {wkDays.map((d, i) => (
                                    <div key={i} className={`cal-day ${d.status}`}
                                      style={{ pointerEvents: "auto", cursor: "pointer" }}
                                      onClick={() => { console.log("[coach-cal-day click] status:", d.status, "dayNum:", d.dayNum, "date:", d.dateStr); openCoachCalDay(d); }}>
                                      {d.date.getDate()}
                                      {(d.status === "done" || d.status === "missed" || d.status === "today" || d.status === "scheduled") && <div className={`cal-dot dot-${d.status}`} />}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </>
                        );
                      })()}
                    </>) : (<>
                      <div className="month-cal-grid" style={{ marginBottom: 4 }}>
                        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(h => <div key={h} className="month-cal-hdr">{h}</div>)}
                      </div>
                      <div className="month-cal-grid">
                        {cells.map((cell, ci) => (
                          <div key={ci} className={`month-cal-cell ${cell.status}`}
                            style={{ pointerEvents: "auto", cursor: cell.isCurrentMonth ? "pointer" : "default" }}
                            onClick={() => { console.log("[coach-month-cal click] status:", cell.status, "dayNum:", cell.dayNum, "date:", cell.dateStr); if (cell.isCurrentMonth) { openCoachCalDay(cell); } }}>
                            {cell.isCurrentMonth ? cell.date.getDate() : ""}
                            {cell.isCurrentMonth && (cell.status === "done" || cell.status === "missed" || cell.status === "today" || cell.status === "scheduled") && <div className={`cal-dot dot-${cell.status}`} />}
                          </div>
                        ))}
                      </div>
                    </>)}
                  </>
                );
              })()}
            </div>
          </div>
          {coachCalPopup && coachCalDay && (
            <>
              <div className="cal-overlay" onClick={closeCoachCalPopup} />
              <div className="cal-popup">
                <div className="cal-popup-date">{coachCalDay.date?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
                {(() => {
                  const doneSes = coachCalDay.status === "done"
                    ? clientSessions.find(x => x.completed_at?.split("T")[0] === coachCalDay.dateStr)
                    : null;
                  const workoutName = doneSes?.workouts?.name || coachCalDayWorkout?.name || coachClientProg?.name || "Workout";
                  return (
                    <>
                      {coachCalDay.status === "done" && <div className="cal-popup-status" style={{ color: "var(--grn)" }}>✓ Completed</div>}
                      {coachCalDay.status === "missed" && <div className="cal-popup-status" style={{ color: "var(--red)" }}>✗ Missed</div>}
                      {coachCalDay.status === "scheduled" && <div className="cal-popup-status">Upcoming</div>}
                      {coachCalDay.status === "today" && <div className="cal-popup-status">Today's Workout</div>}
                      <div className="cal-popup-workout">{workoutName}</div>
                      {coachCalDay.status === "done" && doneSes && (
                        <div style={{ marginTop: 12 }}>
                          {doneSes.set_logs?.length === 0 && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>No sets recorded</div>}
                          {Object.values((doneSes.set_logs || []).reduce((acc, sl) => {
                            const name = sl.exercise_name || sl.workout_exercises?.exercises?.name || "Exercise";
                            if (!acc[name]) acc[name] = { name, sets: [] };
                            acc[name].sets.push(sl);
                            return acc;
                          }, {})).map((exLog, ei) => {
                            const lastSet = exLog.sets.reduce((b, s) => s.set_number > b.set_number ? s : b, exLog.sets[0]);
                            return (
                              <div key={ei} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid var(--cr2)", fontSize: "var(--xs)" }}>
                                <span style={{ color: "var(--ink2)" }}>{exLog.name}</span>
                                <span style={{ fontWeight: 600 }}>{lastSet?.weight > 0 ? `${lastSet.weight} lbs` : "BW"} × {lastSet?.reps_completed}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {coachCalDay.status === "missed" && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 8 }}>No session logged</div>}
                      {(coachCalDay.status === "scheduled" || coachCalDay.status === "today") && coachCalDayWorkout && (
                        <div style={{ marginTop: 12 }}>
                          {(coachCalDayWorkout.workout_exercises || []).sort((a, b) => a.order_index - b.order_index).map((we, ei) => (
                            <div key={ei} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid var(--cr2)", fontSize: "var(--xs)" }}>
                              <span style={{ color: "var(--ink2)" }}>{we.exercises?.name}</span>
                              <span style={{ fontWeight: 600 }}>{we.sets} × {we.reps}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {(coachCalDay.status === "scheduled" || coachCalDay.status === "today") && !coachCalDayWorkout && (
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 8 }}>Loading exercises...</div>
                      )}
                    </>
                  );
                })()}
                <div className="cal-popup-actions">
                  {coachCalSessionMode === "confirm-delete" ? (() => {
                    const doneSes = clientSessions.find(x => x.completed_at?.split("T")[0] === coachCalDay.dateStr);
                    return (<>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink2)", marginBottom: 10, textAlign: "center" }}>Delete this session? All set data will be lost.</div>
                      <button className="bs" style={{ padding: "10px", background: "var(--red)", borderColor: "var(--red)", color: "var(--wh)" }} onClick={() => doneSes && deleteSession(doneSes.id)}>Confirm Delete</button>
                      <button className="bs o" style={{ padding: "10px", marginTop: 4 }} onClick={() => setCoachCalSessionMode(null)}>Cancel</button>
                    </>);
                  })() : coachCalSessionMode === "confirm-remove" ? (<>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink2)", marginBottom: 10, textAlign: "center" }}>Remove this workout from the day? It will become a rest day.</div>
                    <button className="bs" style={{ padding: "10px", background: "var(--red)", borderColor: "var(--red)", color: "var(--wh)" }} onClick={() => coachCalDayWorkout && removeWorkout(coachCalDayWorkout.id)}>Confirm Remove</button>
                    <button className="bs o" style={{ padding: "10px", marginTop: 4 }} onClick={() => setCoachCalSessionMode(null)}>Cancel</button>
                  </>) : coachCalSessionMode === "move" ? (() => {
                    const doneSes = clientSessions.find(x => x.completed_at?.split("T")[0] === coachCalDay.dateStr);
                    return (<>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6 }}>Move session to a new date:</div>
                      <input type="date" className="fi" value={coachCalMoveDate} onChange={e => setCoachCalMoveDate(e.target.value)} style={{ marginBottom: 8 }} />
                      <button className="bs" style={{ padding: "10px" }} onClick={() => doneSes && moveSession(doneSes.id, coachCalMoveDate)}>Move Session</button>
                      <button className="bs o" style={{ padding: "10px", marginTop: 4 }} onClick={() => setCoachCalSessionMode(null)}>Cancel</button>
                    </>);
                  })() : coachCalSessionMode === "move-workout" ? (<>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6 }}>Move workout to a new date:</div>
                    <input type="date" className="fi" value={coachCalMoveDate} onChange={e => setCoachCalMoveDate(e.target.value)} style={{ marginBottom: 8 }} />
                    <button className="bs" style={{ padding: "10px" }} onClick={() => coachCalDayWorkout && moveWorkoutToDate(coachCalDayWorkout.id, coachCalMoveDate)}>Move Workout</button>
                    <button className="bs o" style={{ padding: "10px", marginTop: 4 }} onClick={() => setCoachCalSessionMode(null)}>Cancel</button>
                  </>) : coachCalSessionMode === "edit-notes" ? (() => {
                    const doneSes = clientSessions.find(x => x.completed_at?.split("T")[0] === coachCalDay.dateStr);
                    return (<>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6 }}>Session notes:</div>
                      <textarea className="fi" value={coachCalEditNotes} onChange={e => setCoachCalEditNotes(e.target.value)} rows={3} style={{ marginBottom: 8, resize: "vertical" }} placeholder="Add coach notes..." />
                      <button className="bs" style={{ padding: "10px" }} onClick={() => doneSes && editSessionNotes(doneSes.id, coachCalEditNotes)}>Save Notes</button>
                      <button className="bs o" style={{ padding: "10px", marginTop: 4 }} onClick={() => setCoachCalSessionMode(null)}>Cancel</button>
                    </>);
                  })() : !coachCalAction ? (<>
                    {coachCalDay.status === "done" && (() => {
                      const doneSes = clientSessions.find(x => x.completed_at?.split("T")[0] === coachCalDay.dateStr);
                      return doneSes ? (<>
                        <button className="cal-action-btn" style={{ color: "var(--red)" }} onClick={() => setCoachCalSessionMode("confirm-delete")}>Delete Session</button>
                        <button className="cal-action-btn" onClick={() => { setCoachCalMoveDate(coachCalDay.dateStr); setCoachCalSessionMode("move"); }}>Move to Different Day</button>
                        <button className="cal-action-btn" onClick={() => { setCoachCalEditNotes(doneSes.coach_notes || ""); setCoachCalSessionMode("edit-notes"); }}>Edit Session Notes</button>
                      </>) : null;
                    })()}
                    {coachCalDay.status === "missed" && (<>
                      <button className="cal-action-btn" style={{ color: "var(--grn)" }} onClick={() => excuseDay(coachCalDay.dateStr)}>Mark as Excused</button>
                      {coachCalDayWorkout && (<>
                        <button className="cal-action-btn" onClick={() => { const todayStr = new Date().toISOString().split("T")[0]; moveWorkoutToDate(coachCalDayWorkout.id, todayStr); }}>Move to Today</button>
                        <button className="cal-action-btn" style={{ color: "var(--red)" }} onClick={() => setCoachCalSessionMode("confirm-remove")}>Remove Workout</button>
                      </>)}
                    </>)}
                    {coachCalDay.status === "scheduled" && (<>
                      {coachCalDayWorkout && (<>
                        <button className="cal-action-btn" onClick={() => { setCoachClientPreview(coachCalDayWorkout); setCoachCalPopup(false); }}>View as Client</button>
                        <button className="cal-action-btn" onClick={() => { setCoachCalMoveDate(coachCalDay.dateStr); setCoachCalSessionMode("move-workout"); }}>Move Workout</button>
                        <button className="cal-action-btn" style={{ color: "var(--red)" }} onClick={() => setCoachCalSessionMode("confirm-remove")}>Remove Workout</button>
                      </>)}
                      <button className="cal-action-btn" onClick={() => { setCoachCalAction(true); setCoachCalAssignMode("workout"); }}>+ Add Workout</button>
                    </>)}
                    {coachCalDay.status === "today" && (<>
                      <button className="cal-action-btn" style={{ fontWeight: 600 }} onClick={() => { startLogSession(selClient); closeCoachCalPopup(); }}>Log Session</button>
                      {coachCalDayWorkout && (<>
                        <button className="cal-action-btn" onClick={() => { setCoachClientPreview(coachCalDayWorkout); setCoachCalPopup(false); }}>View as Client</button>
                        <button className="cal-action-btn" onClick={() => { setCoachCalMoveDate(coachCalDay.dateStr); setCoachCalSessionMode("move-workout"); }}>Move Workout</button>
                        <button className="cal-action-btn" style={{ color: "var(--red)" }} onClick={() => setCoachCalSessionMode("confirm-remove")}>Remove Workout</button>
                      </>)}
                    </>)}
                    {coachCalDay.status === "rest" && (<>
                      <button className="cal-action-btn" onClick={() => { setCoachCalAction(true); setCoachCalAssignMode("workout"); }}>+ Assign Workout</button>
                      <button className="cal-action-btn" onClick={() => { setCoachCalAction(true); setCoachCalAssignMode("program"); }}>+ Assign Program</button>
                      <button className="cal-action-btn" onClick={() => { setCoachCalAction(true); setCoachCalAssignMode("block"); }}>+ Assign Block</button>
                    </>)}
                  </>) : coachCalAssignMode === "program" ? (<>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6 }}>Select a program — starts {coachCalDay.dateStr}:</div>
                    <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
                      {allPrograms.map(p => (
                        <button key={p.id} className="cal-action-btn" onClick={async () => {
                          await assignProgramToClient(p.id, selClient.id, coachCalDay.dateStr);
                          setCoachCalPopup(false); setCoachCalAction(false); setCoachCalAssignMode(null);
                        }}>{p.name} · {p.weeks || 4}wk</button>
                      ))}
                    </div>
                    <button className="bs o" style={{ padding: "8px" }} onClick={() => setCoachCalAssignMode(null)}>← Back</button>
                  </>) : coachCalAssignMode === "block" ? (<>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6 }}>Select a block — starts {coachCalDay.dateStr}:</div>
                    <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
                      {allBlocks.map(b => {
                        const firstProg = allPrograms.filter(p => p.block_id === b.id).sort((a, c) => (a.sequence_order || 0) - (c.sequence_order || 0))[0];
                        return (
                          <button key={b.id} className="cal-action-btn" onClick={async () => {
                            if (firstProg) { await assignProgramToClient(firstProg.id, selClient.id, coachCalDay.dateStr); showNotif("Block assigned!"); }
                            else showNotif("No programs in this block");
                            setCoachCalPopup(false); setCoachCalAction(false); setCoachCalAssignMode(null);
                          }}>{b.name}{b.goal ? ` · ${b.goal.slice(0, 30)}` : ""}</button>
                        );
                      })}
                      {allBlocks.length === 0 && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", padding: "8px 12px" }}>No blocks yet</div>}
                    </div>
                    <button className="bs o" style={{ padding: "8px" }} onClick={() => setCoachCalAssignMode(null)}>← Back</button>
                  </>) : coachCalAssignMode === "workout" ? (<>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6 }}>Select a workout for {coachCalDay.dateStr}:</div>
                    <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
                      {(() => {
                        const progMap = {};
                        allPrograms.forEach(p => { progMap[p.id] = p.name; });
                        const workoutList = standaloneWorkouts.map(w => ({ ...w, programName: w.program_id ? (progMap[w.program_id] || "Unknown") : "Standalone" }));
                        if (workoutList.length === 0) return <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", padding: "8px 12px" }}>No workouts found</div>;
                        return workoutList.map(w => (
                          <button key={w.id} className="cal-action-btn" style={{ textAlign: "left" }} onClick={() => assignWorkoutToDay(w)}>
                            <div style={{ fontWeight: 600 }}>{w.name}</div>
                            <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{w.programName}</div>
                          </button>
                        ));
                      })()}
                    </div>
                    <button className="bs o" style={{ padding: "8px" }} onClick={() => setCoachCalAssignMode(null)}>← Back</button>
                  </>) : <>
                    <button className="cal-action-btn" onClick={() => setCoachCalAssignMode("workout")}>Assign Workout</button>
                    <button className="cal-action-btn" onClick={() => setCoachCalAssignMode("program")}>Assign Program</button>
                    <button className="cal-action-btn" onClick={() => setCoachCalAssignMode("block")}>Assign Block</button>
                  </>}
                  <button className="bs o" style={{ padding: "10px", marginTop: 4 }} onClick={closeCoachCalPopup}>Close</button>
                </div>
              </div>
            </>
          )}
        </>
      );
    }

    return (
      <>
        <style>{CSS}</style>
        {notif && <div className="notif"><div className="nd" />{notif}</div>}
        <div className="ca fu">
          <div className="cha" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="che">Coach View</div>
              <div className="cht">Coach Max</div>
              <div className="chs">Max Strength Personal Training</div>
            </div>
            <button className="bs o" style={{ padding: "6px 14px", marginTop: 4, flexShrink: 0 }} onClick={() => setShowBroadcast(true)}>📢 Broadcast</button>
          </div>
          <div className="cstat">
            <div className="csb"><div className="csn">{clientList.length}</div><div className="csl">Clients</div></div>
            <div className="csb"><div className="csn" style={{ color: "var(--gold)" }}>{drafts.length}</div><div className="csl">Drafts</div></div>
            <div className="csb"><div className="csn">87%</div><div className="csl">Rate</div></div>
          </div>
          <div className="ctabs">
            <button className={`ctab ${coachTab === "clients" ? "on" : ""}`} onClick={() => setCoachTab("clients")}>Clients</button>
            <button className={`ctab ${coachTab === "programs" ? "on" : ""}`} onClick={() => { setCoachTab("programs"); loadAllPrograms(); }}>Library</button>
            <button className={`ctab ${coachTab === "drafts" ? "on" : ""}`} onClick={() => setCoachTab("drafts")}>
              Drafts {drafts.length > 0 && <span style={{ background: "var(--red)", color: "white", borderRadius: "50%", width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, marginLeft: 4 }}>{drafts.length}</span>}
            </button>
            <button className={`ctab ${coachTab === "messages" ? "on" : ""}`} onClick={() => { setCoachTab("messages"); setCoachMsgOpenClient(null); loadCoachConversations(); }}>💬 Messages</button>
            <button className={`ctab ${coachTab === "settings" ? "on" : ""}`} onClick={() => setCoachTab("settings")}>Settings</button>
          </div>
          <div className="cc">
            {coachTab === "clients" && (<>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div className="sl" style={{ marginBottom: 0 }}>Clients</div>
              </div>
              <div className="cli-filter">
                {["all","active","trial","inactive","pending"].map(f => (
                  <button key={f} className={`cli-filter-btn ${clientFilter === f ? "on" : ""}`} onClick={() => setClientFilter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
                ))}
              </div>
              {clientList.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No clients yet</div>}
              {clientList.filter(c => clientFilter === "all" || (c.billing_status || "active") === clientFilter).map(c => {
                const bs = c.billing_status || "active";
                const dotColor = { active: "var(--grn)", trial: "var(--gold)", inactive: "var(--ink3)", pending: "#3a6b8a" }[bs] || "var(--grn)";
                const hasUnread = unreadClientIds.has(c.id);
                return (
                  <div key={c.id} className="clc" onClick={() => openClient(c)}>
                    <div style={{ position: "relative" }}>
                      <div className="cla">{c.initials}</div>
                      {hasUnread && <span style={{ position: "absolute", top: 0, right: 0, width: 9, height: 9, borderRadius: "50%", background: "var(--red)", border: "2px solid var(--cr)" }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div className="cln">{c.displayName || c.full_name}</div>
                        <span className="bill-dot" style={{ background: dotColor }} />
                      </div>
                      <div className="clm">Age {c.age || "—"} · {c.body_weight ? `${c.body_weight} lbs` : "BW not set"}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{fmtLastActive(c.lastActive)}</div>
                    </div>
                    <button className="bs o" style={{ padding: "6px 12px", flexShrink: 0 }} onClick={e => { e.stopPropagation(); startLogSession(c); }}>Log</button>
                  </div>
                );
              })}
            </>)}
            {coachTab === "programs" && (<>
              {libraryNewChoice ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <button className="cthb" onClick={() => setLibraryNewChoice(false)}>←</button>
                    <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>What are you creating?</div>
                  </div>
                  {[
                    { title: "New Block", desc: "A training block containing multiple programs with one overarching goal.", example: "Example: 16-week squat focus", action: () => { setLibraryNewChoice(false); setBuildingBlock(true); setBlockDraft({ id: null, name: "", goal: "", programs: [] }); setScreen("block-builder"); } },
                    { title: "New Program", desc: "A standalone program with multiple workout days.", example: "Example: Back Ability 4-week", action: () => { setLibraryNewChoice(false); setPbProgName(""); setPbDaysPerWeek(3); setPbWeeks(4); setPbDays(Array.from({ length: 3 }, (_, i) => ({ day_number: i+1, name: "", blocks: [{ name: "", exercises: [{ name: "", sets: 3, reps: "10", tempo: "", superset_group: "" }] }] }))); setPbSaved(null); setPbBlockName(""); setPbBlockGoal(""); setPbSequenceOrder(1); setScreen("program-builder"); } },
                    { title: "New Workout", desc: "A single workout day that can be assigned directly.", example: "Example: Lower Body Strength Day", action: () => { setLibraryNewChoice(false); setWorkoutDraft({ name: "", focus: "", exercises: [{ name: "", sets: 3, reps: "10", tempo: "", superset_group: "" }] }); setWorkoutBuilderSaved(false); setScreen("workout-builder"); } },
                  ].map((choice, i) => (
                    <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "18px 20px", marginBottom: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }} onClick={choice.action}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600, marginBottom: 4 }}>{choice.title}</div>
                        <div style={{ fontSize: "var(--sm)", color: "var(--ink2)", marginBottom: 4, lineHeight: 1.5 }}>{choice.desc}</div>
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", fontStyle: "italic" }}>{choice.example}</div>
                      </div>
                      <div style={{ color: "var(--ink3)", fontSize: 20 }}>›</div>
                    </div>
                  ))}
                </>
              ) : viewingProgram ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <button className="cthb" onClick={() => { setViewingProgram(null); setProgramEditDraft(null); setAssignProgramId(""); }}>←</button>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)" }}>Program</div>
                      <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>{programEditDraft?.name || viewingProgram.name}</div>
                    </div>
                  </div>
                  {!programEditDraft ? (
                    <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", padding: "16px 0" }}>Loading...</div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                        <div className="fl" style={{ flex: 2 }}>
                          <label>Name</label>
                          <input className="fi" value={programEditDraft.name} onChange={e => setProgramEditDraft(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div className="fl" style={{ flex: 1 }}>
                          <label>Weeks</label>
                          <input className="fi" type="number" min="1" value={programEditDraft.weeks} onChange={e => setProgramEditDraft(p => ({ ...p, weeks: parseInt(e.target.value) || 1 }))} />
                        </div>
                        <div className="fl" style={{ flex: 1 }}>
                          <label>Days/Wk</label>
                          <input className="fi" type="number" min="1" max="7" value={programEditDraft.days_per_week} onChange={e => setProgramEditDraft(p => ({ ...p, days_per_week: parseInt(e.target.value) || 1 }))} />
                        </div>
                      </div>

                      {programEditDraft.workouts.length === 0 && (
                        <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", padding: "8px 0 16px" }}>No workout days found for this program.</div>
                      )}
                      {programEditDraft.workouts.map((wkt, wi) => (
                        <div key={wkt.id} style={{ marginBottom: 8, border: "1px solid var(--cr3)", borderRadius: "var(--r)", overflow: "hidden" }}>
                          <button
                            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--cr2)", border: "none", padding: "10px 14px", cursor: "pointer", textAlign: "left" }}
                            onClick={() => setProgramEditDraft(p => { const wkts = [...p.workouts]; wkts[wi] = { ...wkts[wi], isExpanded: !wkts[wi].isExpanded }; return { ...p, workouts: wkts }; })}
                          >
                            <div>
                              <span style={{ fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", fontWeight: 700, color: "var(--ink3)" }}>Day {wkt.day_number}</span>
                              {wkt.name ? <span style={{ marginLeft: 8, fontSize: "var(--sm)", fontWeight: 600, color: "var(--ink)" }}>{wkt.name}</span> : null}
                              <span style={{ marginLeft: 8, fontSize: "var(--xs)", color: "var(--ink3)" }}>{wkt.exercises.length} exercise{wkt.exercises.length !== 1 ? "s" : ""}</span>
                            </div>
                            <span style={{ fontSize: 10, color: "var(--ink3)" }}>{wkt.isExpanded ? "▲" : "▼"}</span>
                          </button>
                          {wkt.isExpanded && (
                            <div style={{ padding: "12px 14px" }}>
                              {wkt.exercises.length > 0 && (
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 44px 54px 54px 24px", gap: 4, marginBottom: 4 }}>
                                  {["Exercise", "Sets", "Reps", "Tempo", ""].map((h, i) => (
                                    <div key={i} style={{ fontSize: 9, color: "var(--ink3)", letterSpacing: "1px", textTransform: "uppercase", textAlign: i > 0 ? "center" : "left" }}>{h}</div>
                                  ))}
                                </div>
                              )}
                              {wkt.exercises.map((ex, ei) => (
                                <div key={ei} style={{ display: "grid", gridTemplateColumns: "1fr 44px 54px 54px 24px", gap: 4, marginBottom: 5 }}>
                                  <input className="fi" style={{ padding: "6px 8px", fontSize: "var(--xs)" }} value={ex.name} placeholder="Exercise" onChange={e => setProgramEditDraft(p => { const wkts = [...p.workouts]; const exs = [...wkts[wi].exercises]; exs[ei] = { ...exs[ei], name: e.target.value }; wkts[wi] = { ...wkts[wi], exercises: exs }; return { ...p, workouts: wkts }; })} />
                                  <input className="fi" style={{ padding: "6px 4px", fontSize: "var(--xs)", textAlign: "center" }} type="number" value={ex.sets} onChange={e => setProgramEditDraft(p => { const wkts = [...p.workouts]; const exs = [...wkts[wi].exercises]; exs[ei] = { ...exs[ei], sets: e.target.value }; wkts[wi] = { ...wkts[wi], exercises: exs }; return { ...p, workouts: wkts }; })} />
                                  <input className="fi" style={{ padding: "6px 4px", fontSize: "var(--xs)", textAlign: "center" }} value={ex.reps} onChange={e => setProgramEditDraft(p => { const wkts = [...p.workouts]; const exs = [...wkts[wi].exercises]; exs[ei] = { ...exs[ei], reps: e.target.value }; wkts[wi] = { ...wkts[wi], exercises: exs }; return { ...p, workouts: wkts }; })} />
                                  <input className="fi" style={{ padding: "6px 4px", fontSize: "var(--xs)", textAlign: "center" }} value={ex.tempo} placeholder="—" onChange={e => setProgramEditDraft(p => { const wkts = [...p.workouts]; const exs = [...wkts[wi].exercises]; exs[ei] = { ...exs[ei], tempo: e.target.value }; wkts[wi] = { ...wkts[wi], exercises: exs }; return { ...p, workouts: wkts }; })} />
                                  <button style={{ background: "none", border: "none", color: "var(--cr3)", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }} onClick={() => setProgramEditDraft(p => { const wkts = [...p.workouts]; wkts[wi] = { ...wkts[wi], exercises: wkts[wi].exercises.filter((_, xi) => xi !== ei) }; return { ...p, workouts: wkts }; })}>✕</button>
                                </div>
                              ))}
                              <button className="bs o" style={{ width: "100%", padding: "5px", fontSize: "var(--xs)", marginTop: 4 }} onClick={() => setProgramEditDraft(p => { const wkts = [...p.workouts]; wkts[wi] = { ...wkts[wi], exercises: [...wkts[wi].exercises, { we_id: null, exercise_id: null, name: "", sets: 3, reps: "10", tempo: "" }] }; return { ...p, workouts: wkts }; })}>+ Add Exercise</button>
                            </div>
                          )}
                        </div>
                      ))}

                      <div style={{ height: 4 }} />
                      {[{ l: "Assigned To", v: viewingProgram.clientName || "Unassigned" }, { l: "Block", v: allBlocks.find(b => b.id === viewingProgram.block_id)?.name || "—" }, { l: "Status", v: viewingProgram.active ? "Active" : "Inactive" }].map((r, i) => (
                        <div key={i} className="ir2"><div className="il">{r.l}</div><div className="iv">{r.v}</div></div>
                      ))}
                      <div style={{ height: 16 }} />
                      <div className="sl">Assign to Client</div>
                      <select className="fi" style={{ marginBottom: 10 }} value={assignProgramId} onChange={e => setAssignProgramId(e.target.value)}>
                        <option value="">Select client...</option>
                        {clientList.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                      </select>
                      <button className="bp grn" style={{ marginBottom: 16 }} onClick={() => { if (assignProgramId) assignProgramToClient(viewingProgram.id, assignProgramId); }}>Assign to Client</button>
                      <button className="bp" onClick={async () => {
                        await supabase.from("programs").update({ name: programEditDraft.name, weeks: programEditDraft.weeks, days_per_week: programEditDraft.days_per_week }).eq("id", programEditDraft.id);
                        for (const wkt of programEditDraft.workouts) {
                          await supabase.from("workouts").update({ name: wkt.name }).eq("id", wkt.id);
                          const keptWeIds = wkt.exercises.filter(ex => ex.we_id).map(ex => ex.we_id);
                          const toDelete = wkt.originalWeIds.filter(id => !keptWeIds.includes(id));
                          for (const id of toDelete) await supabase.from("workout_exercises").delete().eq("id", id);
                          for (let ei = 0; ei < wkt.exercises.length; ei++) {
                            const ex = wkt.exercises[ei];
                            if (!ex.name.trim()) continue;
                            let exId = null;
                            const { data: found } = await supabase.from("exercises").select("id").ilike("name", ex.name.trim()).maybeSingle();
                            if (found) { exId = found.id; } else {
                              const { data: created } = await supabase.from("exercises").insert({ name: ex.name.trim() }).select().single();
                              if (created) exId = created.id;
                            }
                            if (!exId) continue;
                            if (ex.we_id) {
                              await supabase.from("workout_exercises").update({ exercise_id: exId, sets: Number(ex.sets) || 3, reps: String(ex.reps || "10"), tempo: ex.tempo || null, order_index: ei }).eq("id", ex.we_id);
                            } else {
                              await supabase.from("workout_exercises").insert({ workout_id: wkt.id, exercise_id: exId, sets: Number(ex.sets) || 3, reps: String(ex.reps || "10"), tempo: ex.tempo || null, order_index: ei });
                            }
                          }
                        }
                        showNotif("Program updated!");
                        await loadAllPrograms();
                        setViewingProgram(p => ({ ...p, name: programEditDraft.name, weeks: programEditDraft.weeks, days_per_week: programEditDraft.days_per_week }));
                      }}>Save Changes</button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>Library</div>
                    <button className="bs" onClick={() => { setLibraryNewChoice(true); loadAllPrograms(); }}>+ Add New</button>
                  </div>
                  <div className="cal-toggle" style={{ marginBottom: 16 }}>
                    {["blocks","programs","workouts","exercises"].map(s => (
                      <button key={s} className={`cal-toggle-btn ${librarySection === s ? "on" : ""}`} onClick={() => setLibrarySection(s)} style={{ flex: 1, textTransform: "capitalize" }}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                    ))}
                  </div>
                  {librarySection === "blocks" && (<>
                    {allBlocks.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No training blocks yet</div>}
                    {allBlocks.map(b => (
                      <div key={b.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px 16px", marginBottom: 8 }}>
                        <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600, marginBottom: 2 }}>{b.name}</div>
                        {b.goal && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 6, fontStyle: "italic" }}>{b.goal}</div>}
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 10 }}>{allPrograms.filter(p => p.block_id === b.id).length} programs</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="bs o" style={{ flex: 1, padding: "8px" }} onClick={() => { setBlockDraft({ id: b.id, name: b.name, goal: b.goal || "", programs: allPrograms.filter(p => p.block_id === b.id).sort((a, c) => (a.sequence_order || 0) - (c.sequence_order || 0)).map(p => ({ id: p.id })) }); setScreen("block-builder"); }}>View / Edit</button>
                          <button className="bs" style={{ flex: 1, padding: "8px" }} onClick={() => { const blockProgs = allPrograms.filter(p => p.block_id === b.id).sort((a, c) => (a.sequence_order || 0) - (c.sequence_order || 0)); if (blockProgs.length > 0) setAssigningProgram(blockProgs[0]); else showNotif("No programs in this block"); }}>Assign</button>
                        </div>
                      </div>
                    ))}
                  </>)}
                  {librarySection === "programs" && (<>
                    {allPrograms.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No programs yet</div>}
                    {allPrograms.map(p => (
                      <div key={p.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px 16px", marginBottom: 8 }}>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600, marginBottom: 2 }}>{p.name}</div>
                          <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>
                            {p.weeks || 4} weeks · {p.days_per_week || 5}x/wk
                            {p.block_id ? ` · ${allBlocks.find(b => b.id === p.block_id)?.name || "Block"}` : ""}
                            {p.clientName ? ` · ${p.clientName}` : " · Unassigned"}
                          </div>
                        </div>
                        {deleteConfirmProgId === p.id ? (
                          <div style={{ background: "var(--cr2)", borderRadius: "var(--r)", padding: "10px 12px", marginBottom: 4 }}>
                            <div style={{ fontSize: "var(--xs)", color: "var(--ink2)", marginBottom: 8 }}>Delete this program and all its workouts? This cannot be undone.</div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="bs" style={{ flex: 1, padding: "8px", background: "var(--red)", borderColor: "var(--red)", color: "var(--wh)" }} onClick={() => deleteProgram(p.id)}>Confirm Delete</button>
                              <button className="bs o" style={{ flex: 1, padding: "8px" }} onClick={() => setDeleteConfirmProgId(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 8 }}>
                            <button className="bs o" style={{ flex: 1, padding: "8px" }} onClick={() => { setViewingProgram(p); setAssignProgramId(""); loadProgramDraft(p); }}>View / Edit</button>
                            <button className="bs" style={{ flex: 1, padding: "8px" }} onClick={() => setAssigningProgram(p)}>Assign</button>
                            <button className="bs o" style={{ padding: "8px 12px", color: "var(--red)", borderColor: "var(--red)" }} onClick={() => setDeleteConfirmProgId(p.id)}>Delete</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </>)}
                  {librarySection === "workouts" && (<>
                    {standaloneWorkouts.length === 0 ? (
                      <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No standalone workouts yet</div>
                    ) : (
                      standaloneWorkouts.map(w => (
                        <div key={w.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px 16px", marginBottom: 8 }}>
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600, marginBottom: 2 }}>{w.name}</div>
                            {w.focus && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", fontStyle: "italic" }}>{w.focus}</div>}
                          </div>
                          {deleteConfirmWorkoutId === w.id ? (
                            <div style={{ background: "var(--cr2)", borderRadius: "var(--r)", padding: "10px 12px" }}>
                              <div style={{ fontSize: "var(--xs)", color: "var(--ink2)", marginBottom: 8 }}>Delete this workout and all its exercises? This cannot be undone.</div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button className="bs" style={{ flex: 1, padding: "8px", background: "var(--red)", borderColor: "var(--red)", color: "var(--wh)" }} onClick={() => deleteStandaloneWorkout(w.id)}>Confirm Delete</button>
                                <button className="bs o" style={{ flex: 1, padding: "8px" }} onClick={() => setDeleteConfirmWorkoutId(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="bs o" style={{ flex: 1, padding: "8px" }} onClick={() => loadStandaloneWorkoutForEdit(w)}>View / Edit</button>
                              <button className="bs" style={{ flex: 1, padding: "8px" }} onClick={() => setAssigningStandaloneWorkout(w)}>Assign</button>
                              <button className="bs o" style={{ padding: "8px 12px", color: "var(--red)", borderColor: "var(--red)" }} onClick={() => setDeleteConfirmWorkoutId(w.id)}>Delete</button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </>)}
                  {librarySection === "exercises" && (<>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div className="sl" style={{ marginBottom: 0 }}>Exercise Library</div>
                      <button className="bs" onClick={() => setShowAddExercise(p => !p)}>
                        {showAddExercise ? "Cancel" : "+ Add"}
                      </button>
                    </div>
                    {showAddExercise && (
                      <div style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: 16, marginBottom: 16 }}>
                        <div className="sl" style={{ marginBottom: 10 }}>New Exercise</div>
                        {[
                          { key: "name", label: "Name", ph: "e.g. ATG Split Squat" },
                          { key: "muscle_groups", label: "Muscle Groups", ph: "e.g. Quads, Hip Flexors" },
                          { key: "tempo", label: "Tempo", ph: "e.g. 31X0" },
                          { key: "video_url", label: "Video URL", ph: "https://youtube.com/..." },
                          { key: "cue_1", label: "Cue 1", ph: "Primary coaching cue" },
                          { key: "cue_2", label: "Cue 2", ph: "Secondary cue" },
                          { key: "cue_3", label: "Cue 3", ph: "Third cue" },
                        ].map(f => (
                          <div key={f.key} className="fl" style={{ marginBottom: 10 }}>
                            <label>{f.label}</label>
                            <input className="fi" value={newExercise[f.key]} onChange={e => setNewExercise(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.ph} />
                          </div>
                        ))}
                        <button className="bp grn" onClick={saveExercise}>Save Exercise</button>
                      </div>
                    )}
                    <input className="fi" style={{ marginBottom: 14 }} placeholder="Search exercises..." value={exerciseSearch} onChange={e => setExerciseSearch(e.target.value)} />
                    {exercises.filter(e => e.name?.toLowerCase().includes(exerciseSearch.toLowerCase())).map(ex => (
                      <div key={ex.id} className={`ex-card ${expandedExercise === ex.id ? "open" : ""}`} onClick={() => setExpandedExercise(expandedExercise === ex.id ? null : ex.id)}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div className="ex-name">{ex.name}</div>
                            <div className="ex-meta">{[ex.muscle_groups, ex.tempo].filter(Boolean).join(" · ")}</div>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--ink3)" }}>{expandedExercise === ex.id ? "▲" : "▼"}</div>
                        </div>
                        {expandedExercise === ex.id && (
                          <div className="ex-cues">
                            {[ex.cue_1, ex.cue_2, ex.cue_3].filter(Boolean).map((c, i) => (
                              <div key={i}>{i + 1}. {c}</div>
                            ))}
                            {!ex.cue_1 && !ex.cue_2 && !ex.cue_3 && <div style={{ color: "var(--ink3)" }}>No cues added yet</div>}
                            {ex.video_url && <div style={{ marginTop: 6 }}><a href={ex.video_url} target="_blank" rel="noreferrer" style={{ fontSize: "var(--xs)", color: "var(--red)" }}>▶ Watch video</a></div>}
                          </div>
                        )}
                      </div>
                    ))}
                    {exercises.filter(e => e.name?.toLowerCase().includes(exerciseSearch.toLowerCase())).length === 0 && (
                      <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No exercises found</div>
                    )}
                  </>)}
                  {assigningStandaloneWorkout && (
                    <>
                      <div className="cal-overlay" onClick={() => setAssigningStandaloneWorkout(null)} />
                      <div className="cal-popup">
                        <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600, marginBottom: 4 }}>{assigningStandaloneWorkout.name}</div>
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 8 }}>Select a client to assign this workout to today:</div>
                        <div style={{ maxHeight: 280, overflowY: "auto" }}>
                          {clientList.map(c => (
                            <div key={c.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--cr2)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                              onClick={() => assignStandaloneWorkoutToClient(assigningStandaloneWorkout, c.id)}>
                              <div>
                                <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{c.full_name}</div>
                                <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{c.body_weight ? `${c.body_weight} lbs` : "BW not set"}</div>
                              </div>
                              <div style={{ fontSize: "var(--xs)", color: "var(--red)" }}>Assign →</div>
                            </div>
                          ))}
                        </div>
                        <button className="bs o" style={{ width: "100%", marginTop: 12 }} onClick={() => setAssigningStandaloneWorkout(null)}>Cancel</button>
                      </div>
                    </>
                  )}
                </>
              )}
              {assigningProgram && (
                <>
                  <div className="cal-overlay" onClick={() => setAssigningProgram(null)} />
                  <div className="cal-popup">
                    <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600, marginBottom: 4 }}>{assigningProgram.name}</div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 8 }}>Start date:</div>
                    <input type="date" className="fi" value={assignStartDate} onChange={e => setAssignStartDate(e.target.value)} style={{ marginBottom: 12 }} />
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 8 }}>Select a client:</div>
                    <div style={{ maxHeight: 240, overflowY: "auto" }}>
                      {clientList.map(c => (
                        <div key={c.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--cr2)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                          onClick={() => { assignProgramToClient(assigningProgram.id, c.id, assignStartDate); setAssigningProgram(null); }}>
                          <div>
                            <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{c.full_name}</div>
                            <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{c.body_weight ? `${c.body_weight} lbs` : "BW not set"}</div>
                          </div>
                          <div style={{ fontSize: "var(--xs)", color: "var(--red)" }}>Assign →</div>
                        </div>
                      ))}
                    </div>
                    <button className="bs o" style={{ width: "100%", marginTop: 12 }} onClick={() => setAssigningProgram(null)}>Cancel</button>
                  </div>
                </>
              )}
            </>)}
            {coachTab === "drafts" && (<>
              <div className="sl">{drafts.length > 0 ? `${drafts.length} Awaiting Review` : "No pending drafts"}</div>
              {drafts.map(d => (
                <div key={d.id} className="dc">
                  <div className="de">{d.media_type === "video" ? "🎬" : "📸"} Form Check</div>
                  <div className="dcl">{d.profiles?.full_name || "Client"} · {fmtTime(d.created_at)}</div>
                  {d.media_url && (d.media_type === "video" ? (<video src={d.media_url} controls style={{ width: "100%", borderRadius: "var(--r)", maxHeight: 200, marginBottom: 10 }} />) : (<img src={d.media_url} alt="form check" style={{ width: "100%", borderRadius: "var(--r)", maxHeight: 200, objectFit: "cover", cursor: "pointer", marginBottom: 10 }} onClick={() => window.open(d.media_url, "_blank")} />))}
                  <textarea className="dta" rows={6} value={editDraft[d.id] !== undefined ? editDraft[d.id] : d.ai_draft} onChange={e => setEditDraft(p => ({ ...p, [d.id]: e.target.value }))} />
                  <div className="da">
                    <button className="bs grn" onClick={() => sendDraft(d)}>✓ Send</button>
                    <button className="bs o" onClick={() => discardDraft(d.id)}>Discard</button>
                  </div>
                </div>
              ))}
            </>)}
            {coachTab === "messages" && (<>
              {coachMsgOpenClient ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <button className="cthb" onClick={() => { setCoachMsgOpenClient(null); loadCoachConversations(); }}>←</button>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="cla" style={{ width: 34, height: 34, fontSize: 13 }}>{coachMsgOpenClient.initials}</div>
                      <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600 }}>{coachMsgOpenClient.displayName || coachMsgOpenClient.full_name}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingBottom: 80 }}>
                    {coachMsgThread.length === 0 && <div style={{ textAlign: "center", color: "var(--ink3)", fontSize: "var(--xs)", marginTop: 40 }}>No messages yet</div>}
                    {coachMsgThread.map((m, i) => {
                      const isMe = m.from_id === user.id;
                      return (
                        <div key={m.id || i} className={`bw ${isMe ? "me" : "them"}`}>
                          <div className="bsn">{isMe ? "Coach Max" : (coachMsgOpenClient.displayName || coachMsgOpenClient.full_name)}</div>
                          {m.media_url ? (m.media_type === "video" ? (<video src={m.media_url} controls style={{ maxWidth: "80%", borderRadius: 12 }} />) : (<div className="med"><img src={m.media_url} alt="media" /></div>)) : (<div className={`bb ${isMe ? "me" : "them"}`}>{m.body}</div>)}
                          <div className="bt">{fmtTime(m.created_at)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, background: "var(--cr)", borderTop: "1px solid var(--cr3)", padding: "10px 16px 24px", zIndex: 200 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                      <textarea className="cta" placeholder={`Message ${coachMsgOpenClient.displayName?.split(" ")[0] || "client"}...`} rows={1} value={coachDrafts[coachMsgOpenClient?.id] || ""} onChange={e => setCoachDrafts(p => ({ ...p, [coachMsgOpenClient.id]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCoachMsgFromThread(); } }} />
                      <button className="csend" onClick={sendCoachMsgFromThread}>→</button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="sl" style={{ marginBottom: 12 }}>Messages</div>
                  {coachConversations.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No conversations yet</div>}
                  {coachConversations.map(convo => {
                    const msgTime = (() => {
                      if (!convo.lastAt) return "";
                      const d = new Date(convo.lastAt);
                      const today = new Date();
                      return d.toDateString() === today.toDateString() ? fmtTime(convo.lastAt) : fmtDate(convo.lastAt);
                    })();
                    return (
                      <div key={convo.clientId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--cr2)", cursor: "pointer" }} onClick={() => openCoachMsgThread(convo)}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <div className="cla" style={{ width: 40, height: 40, fontSize: 14 }}>{convo.initials}</div>
                          {convo.hasUnread && <span style={{ position: "absolute", top: 0, right: 0, width: 10, height: 10, borderRadius: "50%", background: "var(--red)", border: "2px solid var(--cr)" }} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                            <div style={{ fontWeight: 600, fontSize: "var(--sm)" }}>{convo.clientName}</div>
                            <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", flexShrink: 0, marginLeft: 8 }}>{msgTime}</div>
                          </div>
                          <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{convo.lastMessage.slice(0, 60)}{convo.lastMessage.length > 60 ? "…" : ""}</div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>)}
            {coachTab === "settings" && (<>
              <div className="sl">Account</div>
              <div style={{ color: "var(--ink3)", fontSize: "var(--sm)", marginBottom: 12 }}>Signed in as Coach Max</div>
              <button className="bg" onClick={signOut}>Sign Out</button>
              <div style={{ marginTop: 16, marginBottom: 4, fontSize: "var(--xs)", color: "var(--ink3)", letterSpacing: "1px" }}>Version 1.0.0</div>

              <div style={{ height: 20 }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div className="sl" style={{ marginBottom: 0 }}>Bible Verses</div>
                <button className="bs o" style={{ padding: "4px 10px" }} onClick={() => setShowAddVerse(p => !p)}>{showAddVerse ? "Cancel" : "+ Add"}</button>
              </div>
              {showAddVerse && (
                <div style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: 14, marginBottom: 12 }}>
                  <div className="fl" style={{ marginBottom: 8 }}><label>Week Of (Monday)</label><input className="fi" type="date" value={newVerse.week_of} onChange={e => setNewVerse(p => ({ ...p, week_of: e.target.value }))} /></div>
                  <div className="fl" style={{ marginBottom: 8 }}><label>Verse</label><textarea className="fi" rows={3} style={{ resize: "none" }} value={newVerse.verse} onChange={e => setNewVerse(p => ({ ...p, verse: e.target.value }))} placeholder="Verse text..." /></div>
                  <div className="fl" style={{ marginBottom: 10 }}><label>Reference</label><input className="fi" value={newVerse.reference} onChange={e => setNewVerse(p => ({ ...p, reference: e.target.value }))} placeholder="e.g. Philippians 4:13" /></div>
                  <button className="bp grn" onClick={saveVerse}>Save Verse</button>
                </div>
              )}
              {verses.map(v => (
                <div key={v.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8 }}>
                  {editingVerse?.id === v.id ? (
                    <>
                      <div className="fl" style={{ marginBottom: 6 }}><label>Week Of</label><input className="fi" type="date" value={editingVerse.week_of} onChange={e => setEditingVerse(p => ({ ...p, week_of: e.target.value }))} /></div>
                      <div className="fl" style={{ marginBottom: 6 }}><label>Verse</label><textarea className="fi" rows={3} style={{ resize: "none" }} value={editingVerse.verse} onChange={e => setEditingVerse(p => ({ ...p, verse: e.target.value }))} /></div>
                      <div className="fl" style={{ marginBottom: 8 }}><label>Reference</label><input className="fi" value={editingVerse.reference} onChange={e => setEditingVerse(p => ({ ...p, reference: e.target.value }))} /></div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="bs grn" onClick={saveVerse}>Save</button>
                        <button className="bs o" onClick={() => setEditingVerse(null)}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginBottom: 3 }}>{fmtDate(v.week_of)}</div>
                        <div style={{ fontSize: "var(--xs)", fontStyle: "italic", marginBottom: 2 }}>"{v.verse?.slice(0, 60)}{v.verse?.length > 60 ? "…" : ""}"</div>
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>— {v.reference}</div>
                      </div>
                      <button className="bs o" style={{ padding: "4px 10px", flexShrink: 0 }} onClick={() => setEditingVerse({ ...v })}>Edit</button>
                    </div>
                  )}
                </div>
              ))}

              <div style={{ height: 20 }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div className="sl" style={{ marginBottom: 0 }}>Resources</div>
                <button className="bs o" style={{ padding: "4px 10px" }} onClick={() => setShowAddResource(p => !p)}>{showAddResource ? "Cancel" : "+ Add"}</button>
              </div>
              {showAddResource && (
                <div style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: 14, marginBottom: 12 }}>
                  <div className="fl" style={{ marginBottom: 8 }}>
                    <label>Category</label>
                    <select className="fi" value={newResource.category} onChange={e => setNewResource(p => ({ ...p, category: e.target.value }))}>
                      {["Methodology","Recovery","Lifestyle","Nutrition"].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="fl" style={{ marginBottom: 8 }}><label>Title</label><input className="fi" value={newResource.title} onChange={e => setNewResource(p => ({ ...p, title: e.target.value }))} placeholder="Article title..." /></div>
                  <div className="fl" style={{ marginBottom: 8 }}><label>Description</label><input className="fi" value={newResource.description} onChange={e => setNewResource(p => ({ ...p, description: e.target.value }))} placeholder="Short description..." /></div>
                  <div className="fl" style={{ marginBottom: 10 }}><label>URL (optional)</label><input className="fi" value={newResource.url} onChange={e => setNewResource(p => ({ ...p, url: e.target.value }))} placeholder="https://..." /></div>
                  <button className="bp grn" onClick={saveResource}>Add Resource</button>
                </div>
              )}
              {resourcesList.map(r => (
                <div key={r.id} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--red)", marginBottom: 2 }}>{r.cat || r.category}</div>
                    <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{r.title}</div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{r.desc || r.description}</div>
                  </div>
                  <button style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: "var(--xs)", padding: "0 0 0 10px", flexShrink: 0 }} onClick={() => deleteResource(r.id)}>✕</button>
                </div>
              ))}
            </>)}
          </div>
          <div className="cbn">
            {[{ id: "clients", icon: "👥", lbl: "Clients" }, { id: "programs", icon: "📚", lbl: "Library" }, { id: "drafts", icon: "✏️", lbl: "Drafts" }, { id: "exercises", icon: "🏋️", lbl: "Exercises" }, { id: "settings", icon: "⚙️", lbl: "Settings" }].map(n => (
              <button key={n.id} className={`cnt ${coachTab === n.id ? "on" : ""}`} onClick={() => setCoachTab(n.id)}>
                <span className="cni">{n.icon}</span><span className="cnl">{n.lbl}</span>
              </button>
            ))}
          </div>
        </div>
        {showBroadcast && (
          <div style={{ position: "fixed", inset: 0, background: "var(--cr)", zIndex: 600, overflowY: "auto" }}>
            <div style={{ padding: "28px 24px 100px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <button className="cthb" onClick={() => { setShowBroadcast(false); setBroadcastMsg(""); setBroadcastTargets("all"); setSelectedBroadcastClients([]); }}>←</button>
                <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>Broadcast Message</div>
              </div>
              <div className="sl" style={{ marginBottom: 10 }}>Send to:</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <button className={`bs ${broadcastTargets === "all" ? "" : "o"}`} style={{ flex: 1, padding: "10px" }} onClick={() => setBroadcastTargets("all")}>All Clients</button>
                <button className={`bs ${broadcastTargets === "select" ? "" : "o"}`} style={{ flex: 1, padding: "10px" }} onClick={() => setBroadcastTargets("select")}>Select Clients</button>
              </div>
              {broadcastTargets === "select" && (
                <div style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "10px 14px", marginBottom: 16 }}>
                  {clientList.map(c => {
                    const sel = selectedBroadcastClients.includes(c.id);
                    return (
                      <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--cr2)", cursor: "pointer" }}
                        onClick={() => setSelectedBroadcastClients(p => sel ? p.filter(x => x !== c.id) : [...p, c.id])}>
                        <div style={{ width: 20, height: 20, borderRadius: 4, border: "1.5px solid var(--cr3)", background: sel ? "var(--ink)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {sel && <span style={{ color: "white", fontSize: 10 }}>✓</span>}
                        </div>
                        <div style={{ fontSize: "var(--sm)" }}>{c.full_name}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="fl" style={{ marginBottom: 16 }}>
                <label>Message</label>
                <textarea className="fi" rows={5} style={{ resize: "none" }} placeholder="Write your message..." value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} />
              </div>
              <button className="bp" disabled={!broadcastMsg.trim() || (broadcastTargets === "select" && selectedBroadcastClients.length === 0)} onClick={async () => {
                const targets = broadcastTargets === "all" ? clientList.map(c => c.id) : selectedBroadcastClients;
                if (!targets.length || !broadcastMsg.trim()) return;
                let sent = 0;
                for (const toId of targets) {
                  await callFn("save-message", { from_id: user.id, to_id: toId, body: broadcastMsg.trim(), triage_type: "broadcast", sent: true });
                  await callFn("send-notification", { title: "Message from Coach Max", message: broadcastMsg.trim().slice(0, 80), externalId: toId });
                  sent++;
                }
                showNotif(`Broadcast sent to ${sent} client${sent !== 1 ? "s" : ""}!`);
                setShowBroadcast(false); setBroadcastMsg(""); setBroadcastTargets("all"); setSelectedBroadcastClients([]);
              }}>
                Send to {broadcastTargets === "all" ? `All ${clientList.length} Clients` : `${selectedBroadcastClients.length} Client${selectedBroadcastClients.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        )}

        {coachClientPreview && (
          <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, height: "100%", background: "var(--bg)", zIndex: 500, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ background: "#c9a84c", color: "#fff", padding: "10px 16px", textAlign: "center", fontWeight: 700, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", flexShrink: 0 }}>Client View — Read Only</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid var(--cr3)", flexShrink: 0 }}>
              <button onClick={() => setCoachClientPreview(null)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--ink)", padding: "0 4px", lineHeight: 1 }}>✕</button>
              <div>
                <div style={{ fontWeight: 700, fontSize: "var(--sm)" }}>{coachClientPreview.name}</div>
                {coachClientPreview.focus && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{coachClientPreview.focus}</div>}
              </div>
            </div>
            <div style={{ overflowY: "auto", flex: 1, padding: "12px 16px 32px" }}>
              {groupExercises((coachClientPreview.workout_exercises || []).sort((a, b) => a.order_index - b.order_index)).map((item, gi) => {
                const renderPreviewEx = (we, key) => {
                  const exName = we.exercises?.name || "Exercise";
                  const videoUrl = we.exercises?.video_url;
                  const videoId = videoUrl?.match(/(?:v=|youtu\.be\/)([^&?\s]+)/)?.[1];
                  const thumbUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
                  return (
                    <div key={key} style={{ padding: "14px 0", borderBottom: "1px solid var(--cr2)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: (we.exercises?.cue_1 || we.exercises?.cue_2) ? 10 : 0 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: "var(--sm)" }}>{exName}</div>
                          <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{we.sets} sets · {we.reps}{we.exercises?.tempo ? ` · ${we.exercises.tempo}` : ""}</div>
                        </div>
                        {thumbUrl && (
                          <img src={thumbUrl} alt={exName} style={{ width: 72, height: 48, objectFit: "cover", borderRadius: 4, cursor: "pointer", flexShrink: 0, marginLeft: 10 }}
                            onClick={() => window.open(videoUrl, "_blank")} />
                        )}
                      </div>
                      {(we.exercises?.cue_1 || we.exercises?.cue_2) && (
                        <div style={{ background: "var(--cr2)", padding: "8px 10px", borderRadius: "var(--r)", fontSize: "var(--xs)", color: "var(--ink2)", lineHeight: 1.6 }}>
                          <strong>Cues:</strong> {[we.exercises?.cue_1, we.exercises?.cue_2, we.exercises?.cue_3].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  );
                };
                if (item.type === "group") {
                  return (
                    <div key={gi} style={{ border: "2px solid var(--cr3)", borderRadius: "var(--r)", marginBottom: 12, overflow: "hidden" }}>
                      <div style={{ background: "var(--cr2)", padding: "5px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--ink3)" }}>Superset</div>
                      {item.exercises.map((we, ei) => renderPreviewEx(we, ei))}
                    </div>
                  );
                }
                return renderPreviewEx(item.ex, gi);
              })}
            </div>
          </div>
        )}
      </>
    );
  }

  // ── CLIENT RENDER ─────────────────────────────────────────────────────────
  const missed = missedYesterday();
  const bw = profile?.body_weight || 150;

  return (
    <>
      <style>{CSS}</style>
      {notif && <div className="notif"><div className="nd" />{notif}</div>}
      <input ref={fileRef} type="file" style={{ display: "none" }} onChange={handleFile} />
      <div className={`app${isLarge ? " lt" : ""}`}>

        {screen === "splash" && (
          <div className="spl">
            <div className="spl-bg" /><div className="spl-ln" />
            <div className="spl-c fu">
              <div className="spl-wm">Max <span>Strength</span></div>
              <div className="spl-tg">Personal Training</div>
              <div className="spl-dv" />
              <div className="spl-v">"{verse?.verse || "I can do all things through Christ who strengthens me."}"</div>
              <div className="spl-vr">{verse?.reference || "Philippians 4:13"}</div>
              <div style={{ height: 40 }} />
              <button className="bp" onClick={() => { setAuthMode("login"); setScreen("auth"); }}>Sign In</button>
              <button className="bg" onClick={() => { setAuthMode("signup"); setScreen("auth"); }}>Create Account</button>
            </div>
          </div>
        )}

        {screen === "auth" && (
          <div className="ap fu">
            <div className="ah">
              <div className="at">{authMode === "login" ? "Welcome back." : "Get started."}</div>
              <div className="as">{authMode === "login" ? "Sign in to your program." : "Create your account."}</div>
            </div>
            {authErr && <div className="em">{authErr}</div>}
            {authMode === "signup" && <div className="fl"><label>Your Name</label><input className="fi" value={uname} onChange={e => setUname(e.target.value)} placeholder="First Last" autoComplete="name" /></div>}
            <div className="fl"><label>Email</label><input className="fi" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" autoComplete="email" onKeyDown={e => { if (e.key === "Enter") authMode === "login" ? signIn() : signUp(); }} /></div>
            <div className="fl"><label>Password</label><input className="fi" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" autoComplete={authMode === "login" ? "current-password" : "new-password"} onKeyDown={e => { if (e.key === "Enter") authMode === "login" ? signIn() : signUp(); }} /></div>
            <button className="bp" onClick={authMode === "login" ? signIn : signUp} disabled={loading}>{loading ? "..." : authMode === "login" ? "Sign In" : "Create Account"}</button>
            <button className="bg" onClick={() => setAuthMode(authMode === "login" ? "signup" : "login")}>{authMode === "login" ? "New here? Create account" : "Have an account? Sign in"}</button>
            <button className="bl" onClick={() => setScreen("splash")}>← Back</button>
          </div>
        )}

        {screen === "reset-password" && (
          <div className="ap fu">
            <div className="ah">
              <div className="at">Reset password.</div>
              <div className="as">Enter your new password below.</div>
            </div>
            {resetMsg && <div className={`em`} style={{ background: resetMsg.includes("updated") ? "#f0f8f0" : undefined, borderColor: resetMsg.includes("updated") ? "#c0dcc0" : undefined, color: resetMsg.includes("updated") ? "var(--grn)" : undefined }}>{resetMsg}</div>}
            <div className="fl"><label>New Password</label><input className="fi" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" /></div>
            <div className="fl"><label>Confirm Password</label><input className="fi" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" /></div>
            <button className="bp" disabled={loading} onClick={async () => {
              if (!newPassword || newPassword.length < 6) { setResetMsg("Password must be at least 6 characters"); return; }
              if (newPassword !== confirmPassword) { setResetMsg("Passwords don't match"); return; }
              setLoading(true);
              const { error } = await supabase.auth.updateUser({ password: newPassword });
              setLoading(false);
              if (error) { setResetMsg(error.message); return; }
              setResetMsg("Password updated! Redirecting...");
              setTimeout(() => { setNewPassword(""); setConfirmPassword(""); setResetMsg(""); setScreen("home"); }, 2000);
            }}>{loading ? "..." : "Update Password"}</button>
          </div>
        )}

        {screen === "onboarding" && (
          <div className="pg fu">
            <div className="op">{[0, 1, 2].map(i => <div key={i} className={`os ${i < obStep ? "done" : i === obStep ? "act" : ""}`} />)}</div>
            {obStep === 0 && (<>
              <div className="oq">A few quick things.</div>
              <div className="oo">Personalizes your experience.</div>
              <div className="fl"><label>Date of Birth</label><input className="fi" type="date" value={dob} onChange={e => setDob(e.target.value)} /></div>
              <div className="fl"><label>Body Weight (lbs)</label><input className="fi" type="number" value={bwVal} onChange={e => setBwVal(e.target.value)} placeholder="e.g. 175" /></div>
              <button className="bp" onClick={() => setObStep(1)}>Next →</button>
            </>)}
            {obStep === 1 && (<>
              <div className="oq">Any past injuries?</div>
              <div className="oo">Helps us build the right program.</div>
              <div className="cg">{["Ankle", "Knee", "Back", "Hip", "Shoulder", "Neck", "Elbow", "Wrist", "None"].map(x => <div key={x} className={`cp ${injuries.includes(x) ? "on" : ""}`} onClick={() => toggleChip(injuries, setInjuries, x)}>{x}</div>)}</div>
              <button className="bp" onClick={() => setObStep(2)}>Next →</button>
              <button className="bl" onClick={() => setObStep(0)}>← Back</button>
            </>)}
            {obStep === 2 && (<>
              <div className="oq">What are you training for?</div>
              <div className="oo">Your goals shape your program.</div>
              <div className="cg">{["Get Stronger", "Lose Weight", "Build Muscle", "Move Better", "Prevent Injury", "More Energy", "General Health"].map(x => <div key={x} className={`cp ${goals.includes(x) ? "on" : ""}`} onClick={() => toggleChip(goals, setGoals, x)}>{x}</div>)}</div>
              <button className="bp" onClick={finishOb}>Let's Go →</button>
              <button className="bl" onClick={() => setObStep(1)}>← Back</button>
            </>)}
          </div>
        )}

        {screen === "home" && (
          <div className="pg fu" onTouchStart={onPullStart} onTouchMove={onPullMove} onTouchEnd={onPullEnd}>
            {pullDistance > 0 && <div className="pull-hint">{pullDistance >= PULL_THRESHOLD ? "↑ Release to refresh" : "↓ Pull to refresh"}</div>}
            {clientLoading && !prog ? (
              <div className="load-center"><div className="spin" /></div>
            ) : clientError && !prog ? (
              <div className="err-center">
                <div style={{ fontSize: 22, marginBottom: 8 }}>⚠️</div>
                <div style={{ fontSize: "var(--sm)" }}>{clientError}</div>
              </div>
            ) : (
              <>
                <div className="ph">
              <div className="pe">Good to have you back</div>
              <div className="ptt">{profile?.full_name?.split(" ")[0] || "Athlete"}.</div>
            </div>
            <div className="vc fu2">
              <div className="vt">"{verse?.verse || "I can do all things through Christ who strengthens me."}"</div>
              <div className="vr">{verse?.reference || "Philippians 4:13"} · Verse of the Week</div>
            </div>
            {showNotifBanner && (
              <div className="notif-banner fu2">
                <div className="notif-banner-text">Enable notifications to get updates from Coach Max</div>
                <button className="bs" style={{ padding: "6px 12px", whiteSpace: "nowrap" }} onClick={async () => {
                  localStorage.setItem(`notif_requested_${user.id}`, "true");
                  setShowNotifBanner(false);
                  if (window.OneSignalDeferred && process.env.REACT_APP_ONESIGNAL_APP_ID) {
                    window.OneSignalDeferred.push(async (OneSignal) => {
                      await OneSignal.User.PushSubscription.optIn();
                      if (user?.id) await OneSignal.login(user.id);
                    });
                  } else {
                    const perm = await Notification.requestPermission();
                    if (perm === "granted") showNotif("Notifications enabled!");
                    return;
                  }
                  showNotif("Notifications enabled!");
                }}>Enable</button>
                <button style={{ background: "none", border: "none", color: "var(--ink3)", cursor: "pointer", fontSize: 16, padding: "0 4px" }} onClick={() => { localStorage.setItem(`notif_requested_${user.id}`, "true"); setShowNotifBanner(false); }}>✕</button>
              </div>
            )}
            {!prog ? (
              <>
                <div style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "24px 20px", marginBottom: 24, textAlign: "center" }} className="fu2">
                  <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600, marginBottom: 10 }}>Welcome to Max Strength.</div>
                  <div style={{ fontSize: "var(--sm)", color: "var(--ink3)", lineHeight: 1.6, marginBottom: 20 }}>Your program will appear here once Coach Max assigns one. Reach out if you have any questions.</div>
                  <button className="bp" onClick={() => go("messages", "messages")}>Message Coach Max</button>
                </div>
                <div className="ml fu3">
                  {[
                    { icon: "📊", name: "Progress", desc: "Standards & session history", s: "progress", t: "progress" },
                    { icon: "📖", name: "Resources", desc: "Methodology, recovery", s: "resources", t: "resources" },
                    { icon: "💬", name: "Coach Max", desc: "Questions, form checks", s: "messages", t: "messages" },
                  ].map((m, i) => (
                    <div key={i} className="mi" onClick={() => go(m.s, m.t)}>
                      <div className="mico">{m.icon}</div>
                      <div><div className="mna">{m.name}</div><div className="mde">{m.desc}</div></div>
                      <div className="maro">›</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="sr fu3">
                  <div className="sc"><div className="sn">{completedDates.length}</div><div className="sl2">Done</div></div>
                  <div className="sc"><div className="sn">{getWeekNum(prog.start_date, todayStr)}</div><div className="sl2">Week</div></div>
                  <div className="sc">
                    <div className="sn" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                      {calculateStreak()}{calculateStreak() > 0 && <span className="streak-fire">🔥</span>}
                    </div>
                    <div className="sl2">Streak</div>
                  </div>
                </div>
                {missed && (
                  <div className="tb missed fu3" onClick={async () => { await loadWorkoutForDate(prog, missed); go("workout", "workout"); }}>
                    <div className="te">Missed · {fmtDate(missed)}</div>
                    <div className="tn">Yesterday's Workout</div>
                    <div className="tm">Tap to complete it now</div>
                  </div>
                )}
                <div className="sl fu3">Today · {fmtDate(todayStr)}</div>
                {workout && getProgramDay(prog.start_date, viewingDate, buildDayMapFromWorkouts(loadedWorkouts) || getDayMapForProgram(prog.days_per_week || 5)) ? (
                  <div className="tb fu4" onClick={() => go("workout", "workout")}>
                    <div className="te">{prog.name}{completedDates.includes(todayStr) ? " · ✓ Done" : ""}</div>
                    <div className="tn">{workout.name}</div>
                    <div className="tm">{workout.duration} · {workout.focus}</div>
                  </div>
                ) : (
                  <div className="rest-card fu4">
                    <div className="rest-card-title">Rest & Recover</div>
                    <div className="rest-scripture">"He makes me lie down in green pastures, he leads me beside quiet waters, he refreshes my soul." — Psalm 23:2-3</div>
                    <div className="rest-card-tip">{REST_TIPS[new Date().getDay() % REST_TIPS.length]}</div>
                    <button className="bs o" style={{ width: "100%" }} onClick={() => setShowMobilityFlow(true)}>🧘 Mobility Flow</button>
                  </div>
                )}
                <div className="sl fu4">Quick Access</div>
                <div className="ml fu4">
                  {[
                    { icon: "📅", name: "Program", desc: "Your full schedule", s: "calendar", t: "calendar" },
                    { icon: "📊", name: "Progress", desc: "Standards & session history", s: "progress", t: "progress" },
                    { icon: "📖", name: "Resources", desc: "Methodology, recovery", s: "resources", t: "resources" },
                    { icon: "💬", name: "Coach Max", desc: "Questions, form checks", s: "messages", t: "messages" },
                  ].map((m, i) => (
                    <div key={i} className="mi" onClick={() => go(m.s, m.t)}>
                      <div className="mico">{m.icon}</div>
                      <div><div className="mna">{m.name}</div><div className="mde">{m.desc}</div></div>
                      <div className="maro">›</div>
                    </div>
                  ))}
                </div>
              </>
            )}
              </>
            )}
          </div>
        )}

        {screen === "calendar" && calView === "week" && (
          <div className="pg fu">
            <div className="ph">
              <div className="pe">{prog?.name || "Your Program"}</div>
              <div className="ptt">Schedule</div>
              <div className="pss">{prog?.weeks || 4} weeks · Mon–Fri</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 10, fontSize: "var(--xs)" }}>
                <span style={{ color: "var(--grn)" }}>● Done</span>
                <span style={{ color: "var(--red)" }}>● Missed</span>
                <span style={{ color: "var(--ink)" }}>● Today</span>
              </div>
              <div className="cal-toggle">
                <button className={`cal-toggle-btn ${calView === "week" ? "on" : ""}`} onClick={() => { setCalView("week"); setWeekOffset(0); }}>Week</button>
                <button className={`cal-toggle-btn ${calView === "month" ? "on" : ""}`} onClick={() => { setCalView("month"); setCalendarMonth(new Date()); }}>Month</button>
              </div>
            </div>
            {(() => {
              const todayDate = new Date(todayStr + "T12:00:00");
              const daysToMon = (todayDate.getDay() + 6) % 7;
              const thisMondayMs = todayDate.getTime() - daysToMon * 24 * 60 * 60 * 1000;
              const startMs = thisMondayMs + weekOffset * 14 * 24 * 60 * 60 * 1000;
              const todayMs = todayDate.getTime();
              const dayMap = prog ? (buildDayMapFromWorkouts(loadedWorkouts) || getDayMapForProgram(prog.days_per_week || 5)) : null;
              const progStartMs = prog?.start_date ? new Date(prog.start_date + "T12:00:00").getTime() : null;
              const progEndMs = prog ? progStartMs + (prog.weeks || 4) * 7 * 24 * 60 * 60 * 1000 : null;
              const days = [];
              for (let i = 0; i < 14; i++) {
                const ms = startMs + i * 24 * 60 * 60 * 1000;
                const date = new Date(ms);
                const dateStr = date.toISOString().split("T")[0];
                const inProgram = prog && progStartMs !== null && ms >= progStartMs && ms < progEndMs;
                const dayNum = inProgram ? getProgramDay(prog.start_date, dateStr, dayMap) : null;
                const isToday = dateStr === todayStr;
                const isPast = ms < todayMs;
                const isDone = completedDates.includes(dateStr);
                let status = "rest";
                if (dayNum) {
                  if (isDone) status = "done";
                  else if (isToday) status = "today";
                  else if (isPast) status = "missed";
                  else status = "scheduled";
                } else if (isToday) {
                  status = "today";
                }
                days.push({ date, dateStr, dayNum, status });
              }
              const startLabel = new Date(startMs).toLocaleDateString("en-US", { month: "short", day: "numeric" });
              const endLabel = new Date(startMs + 13 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" });
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <button className="bs o" onClick={() => setWeekOffset(p => p - 1)}>← Prev</button>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", letterSpacing: "1px" }}>{startLabel} – {endLabel}</div>
                    <button className="bs o" onClick={() => setWeekOffset(p => p + 1)}>Next →</button>
                  </div>
                  {[days.slice(0, 7), days.slice(7, 14)].map((wkDays, wi) => (
                    <div key={wi} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", color: "var(--ink3)", marginBottom: 8 }}>
                        Week of {wkDays[0].date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => <div key={i} style={{ fontSize: 11, textAlign: "center", color: "var(--ink3)", paddingBottom: 4 }}>{d}</div>)}
                        {wkDays.map((d, i) => (
                          <div key={i} className={`cal-day ${d.status}`}
                            onClick={async () => { console.log("[cal-day click] status:", d.status, "dayNum:", d.dayNum, "date:", d.dateStr); if (d.dayNum && prog) { await loadWorkoutForDate(prog, d.dateStr); go("workout", "workout"); } }}
                            style={{ cursor: d.dayNum ? "pointer" : "default" }}
                          >
                            {d.date.getDate()}
                            {(d.status === "done" || d.status === "missed" || d.status === "today" || d.status === "scheduled") && <div className={`cal-dot dot-${d.status}`} />}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}

        {screen === "calendar" && calView === "month" && (
          <div className="pg fu">
            <div style={{ paddingTop: 28, paddingBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div className="cal-toggle">
                  <button className={`cal-toggle-btn ${calView === "week" ? "on" : ""}`} onClick={() => { setCalView("week"); setWeekOffset(0); }}>Week</button>
                  <button className={`cal-toggle-btn ${calView === "month" ? "on" : ""}`} onClick={() => setCalView("month")}>Month</button>
                </div>
                <div style={{ fontFamily: "var(--fd)", fontSize: "var(--lg)", fontWeight: 600 }}>
                  {calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button className="bs o" style={{ padding: "5px 10px" }} onClick={() => { const m = new Date(calendarMonth); m.setMonth(m.getMonth() - 1); setCalendarMonth(m); }}>←</button>
                  <button className="bs o" style={{ padding: "5px 10px" }} onClick={() => { const m = new Date(calendarMonth); m.setMonth(m.getMonth() + 1); setCalendarMonth(m); }}>→</button>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, fontSize: "var(--xs)" }}>
                <span style={{ color: "var(--grn)" }}>● Done</span>
                <span style={{ color: "var(--red)" }}>● Missed</span>
                <span style={{ color: "var(--ink)" }}>● Today</span>
              </div>
            </div>
            <div className="month-cal-grid" style={{ marginBottom: 4 }}>
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(h => <div key={h} className="month-cal-hdr">{h}</div>)}
            </div>
            <div className="month-cal-grid">
              {buildMonthCalGrid(calendarMonth, prog, completedDates, loadedWorkouts).map((cell, ci) => (
                <div
                  key={ci}
                  className={`month-cal-cell ${cell.status}`}
                  onClick={async () => {
                    console.log("[month-cal click] status:", cell.status, "dayNum:", cell.dayNum, "date:", cell.dateStr);
                    if (cell.dayNum && cell.isCurrentMonth && prog) {
                      await loadWorkoutForDate(prog, cell.dateStr);
                      setCalView("week");
                      go("workout", "workout");
                    }
                  }}
                >
                  {cell.isCurrentMonth ? cell.date.getDate() : ""}
                  {cell.isCurrentMonth && (cell.status === "done" || cell.status === "missed" || cell.status === "today" || cell.status === "scheduled") && <div className={`cal-dot dot-${cell.status}`} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {screen === "workout" && !viewingWorkout && (
          <div className="pg fu" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
            {clientLoading
              ? <div className="spin" />
              : <div style={{ textAlign: "center", color: "var(--ink3)" }}>
                  <div style={{ fontSize: "var(--lg)", fontFamily: "var(--fd)", marginBottom: 6 }}>Rest Day</div>
                  <div style={{ fontSize: "var(--xs)" }}>{clientError || "No workout scheduled"}</div>
                </div>
            }
          </div>
        )}
        {previewVideo && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}
            onClick={e => { if (e.target === e.currentTarget) setPreviewVideo(null); }}>
            <div style={{ position: "absolute", top: 20, right: 20 }}>
              <button style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setPreviewVideo(null)}>✕</button>
            </div>
            <div style={{ fontSize: "var(--sm)", fontWeight: 600, color: "#fff", textAlign: "center", padding: "0 24px" }}>{previewVideo.name}</div>
            <img
              src={`https://img.youtube.com/vi/${previewVideo.videoId}/mqdefault.jpg`}
              alt={previewVideo.name}
              style={{ width: "calc(100% - 48px)", maxWidth: 380, borderRadius: 8, objectFit: "cover" }}
            />
            <button style={{ padding: "14px 32px", background: "var(--red)", color: "#fff", border: "none", borderRadius: "var(--r)", fontSize: "var(--sm)", fontWeight: 600, cursor: "pointer", letterSpacing: "1px" }}
              onClick={() => window.open(previewVideo.url, "_blank")}>
              ▶ Watch Video
            </button>
          </div>
        )}

        {showTimerOverlay && (
          <div className="timer-overlay" onClick={e => { if (e.target === e.currentTarget) setShowTimerOverlay(false); }}>
            <div className="timer-big">{timer.fmt}</div>
            {timer.secs === 0 && <div className="timer-done">Rest 💤</div>}
            <div className="timer-btns">
              <button className={`timer-btn ${timer.running ? "pause" : "start"}`} onClick={timer.toggle}>{timer.running ? "Pause" : timer.secs === 0 ? "Restart" : "Start"}</button>
              <button className="timer-btn reset" onClick={() => timer.reset()}>Reset</button>
              <button className="timer-btn reset" onClick={() => setShowTimerOverlay(false)}>Done</button>
            </div>
          </div>
        )}

        {screen === "workout" && viewingWorkout && (
          <div className="pg fu">
            {completedDates.includes(viewingDate) && (
              <div style={{ background: "var(--grn)", color: "white", padding: "12px 20px", margin: "-28px -24px 20px", textAlign: "center", fontSize: "var(--sm)", fontWeight: 600, letterSpacing: "1px" }}>
                ✓ Completed · {fmtDate(viewingDate)}
              </div>
            )}
            <div className="ph">
              <div className="pe">{prog?.name} · {fmtDate(viewingDate)}</div>
              <div className="ptt">{workout?.name || "Workout"}</div>
              <div className="wmt">
                <span className="wtag">⏱ {workout?.duration}</span>
                <span className="wtag">🎯 {workout?.focus}</span>
                <span className="wtag">{workout?.exercises.length} exercises</span>
                {completedDates.includes(viewingDate) && <span className="wtag" style={{ background: "#f0f5f0", color: "var(--grn)" }}>✓ Done</span>}
              </div>
            </div>
            {completedDates.includes(viewingDate) && completedSessionLog?.set_logs?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div className="sl">What You Logged</div>
                {Object.values(
                  (completedSessionLog.set_logs || []).reduce((acc, sl) => {
                    const name = sl.workout_exercises?.exercises?.name || "Exercise";
                    if (!acc[name]) acc[name] = { name, sets: [] };
                    acc[name].sets.push(sl);
                    return acc;
                  }, {})
                ).map((exLog, i) => (
                  <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8 }}>
                    <div style={{ fontSize: "var(--sm)", fontWeight: 600, marginBottom: 6 }}>{exLog.name}</div>
                    {exLog.sets.map((sl, si) => (
                      <div key={si} style={{ fontSize: "var(--xs)", color: "var(--ink3)", display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                        <span>Set {sl.set_number}</span>
                        <span>{sl.weight > 0 ? `${sl.weight} lbs` : "BW"} × {sl.reps_completed} reps</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {/* Coach Max can customize this prayer later */}
            <div style={{ background: "var(--cr2)", borderLeft: "3px solid var(--red)", borderRadius: "0 var(--r) var(--r) 0", padding: "12px 16px", marginBottom: 20, opacity: 0.85 }}>
              <div style={{ fontFamily: "var(--fd)", fontSize: "var(--sm)", fontStyle: "italic", color: "var(--ink2)", lineHeight: 1.7 }}>
                "Lord, let nothing be wasted. Help us to be more human. Give us strength for what is ahead. Amen."
              </div>
            </div>
            <div className="sl">Exercises</div>
            <div style={{ marginBottom: 20 }}>
              {workout?.exercises && renderExercises(workout.exercises)}
            </div>
            <div style={{ height: 80 }} />
            {!completedDates.includes(viewingDate) ? (
              <button className="cb rdy" style={{ position: "fixed", bottom: 70, left: "50%", transform: "translateX(-50%)", width: "calc(100% - 48px)", maxWidth: 382, zIndex: 90 }} onClick={completeWorkout}>
                {viewingDate > todayStr ? "Complete Early" : "Complete Session"}
              </button>
            ) : (
              <button className="cb" disabled style={{ position: "fixed", bottom: 70, left: "50%", transform: "translateX(-50%)", width: "calc(100% - 48px)", maxWidth: 382, zIndex: 90, background: "var(--grn)", opacity: 0.6, cursor: "not-allowed" }}>
                Already Completed
              </button>
            )}
          </div>
        )}

        {screen === "workout-summary" && sessionSummary && (
          <div className="pg fu" style={{ paddingTop: 0 }}>
            <div className="sum-hero">
              <div className="sum-icon">🏆</div>
              <div className="sum-title">Session Complete</div>
              <div className="sum-sub">Keep showing up — that's how it's built.</div>
            </div>
            {sessionSummary.improvements?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div className="sl">Personal Bests 🎉</div>
                {sessionSummary.improvements.map((ex, i) => (
                  <div key={i} className="sum-pb">
                    <div className="sum-pb-icon">⬆️</div>
                    <div className="sum-pb-text">New personal best on {ex.name}!{ex.weight > 0 ? ` ${ex.weight} lbs × ${ex.reps}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="sr" style={{ marginBottom: 20 }}>
              <div className="sum-stat"><div className="sum-stat-n">{sessionSummary.totalDone}</div><div className="sum-stat-l">Total Sessions</div></div>
              <div className="sum-stat"><div className="sum-stat-n">{prog ? getWeekNum(prog.start_date, viewingDate) : "—"}</div><div className="sum-stat-l">Week</div></div>
              <div className="sum-stat"><div className="sum-stat-n">{sessionSummary.exercises.filter(e => e.hasData).length}</div><div className="sum-stat-l">Logged</div></div>
            </div>
            <div className="sl">What You Did</div>
            {sessionSummary.exercises.map((ex, i) => {
              const isEditing = editingSummaryEx === ex.idx;
              return (
                <div key={i} style={{ background: "var(--wh)", border: `1px solid ${!ex.wasChecked ? "var(--red)" : "var(--cr3)"}`, borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8, cursor: isEditing ? "default" : "pointer" }}
                  onClick={() => { if (!isEditing) { setEditingSummaryEx(ex.idx); setEditSummaryData({}); } }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: isEditing ? 10 : 4 }}>
                    <div>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{ex.name}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{ex.sets} sets assigned</div>
                    </div>
                    {!isEditing && (
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Edit</div>
                    )}
                  </div>
                  {isEditing ? (
                    <div>
                      {ex.setsData.map((sd, s) => (
                        <div key={s} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                          <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", width: 40 }}>Set {s + 1}</div>
                          <input type="number" placeholder="lbs" defaultValue={sd.weight || ""} style={{ flex: 1, padding: "6px 8px", border: "1px solid var(--cr3)", borderRadius: 6, fontSize: "var(--sm)", background: "var(--cr2)", outline: "none" }}
                            onChange={e => setEditSummaryData(p => ({ ...p, [`${s}-w`]: e.target.value }))} />
                          <input type="number" placeholder="reps" defaultValue={sd.reps || ""} style={{ flex: 1, padding: "6px 8px", border: "1px solid var(--cr3)", borderRadius: 6, fontSize: "var(--sm)", background: "var(--cr2)", outline: "none" }}
                            onChange={e => setEditSummaryData(p => ({ ...p, [`${s}-r`]: e.target.value }))} />
                        </div>
                      ))}
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button style={{ flex: 1, padding: "8px", background: "var(--grn)", color: "var(--wh)", border: "none", borderRadius: 6, fontSize: "var(--xs)", fontWeight: 600, cursor: "pointer" }} onClick={e => { e.stopPropagation(); saveEditedSummaryEx(ex); }}>Save</button>
                        <button style={{ flex: 1, padding: "8px", background: "var(--cr2)", color: "var(--ink3)", border: "1px solid var(--cr3)", borderRadius: 6, fontSize: "var(--xs)", cursor: "pointer" }} onClick={e => { e.stopPropagation(); setEditingSummaryEx(null); setEditSummaryData({}); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {!ex.wasChecked ? (
                        <div style={{ fontSize: "var(--xs)", color: "var(--red)", fontWeight: 600 }}>Missed</div>
                      ) : !ex.hasData ? (
                        <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Completed — no data logged</div>
                      ) : (
                        ex.setsData.filter(sd => sd.logged).map((sd, si) => (
                          <div key={si} style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--xs)", padding: "2px 0", borderTop: si > 0 ? "1px solid var(--cr2)" : "none" }}>
                            <span style={{ color: "var(--ink3)" }}>Set {sd.set}</span>
                            <span style={{ fontWeight: 600, color: "var(--ink)" }}>{sd.weight > 0 ? `${sd.weight} lbs` : "BW"} × {sd.reps}</span>
                          </div>
                        ))
                      )}
                      {ex.improved && <div style={{ fontSize: "var(--xs)", color: "var(--grn)", marginTop: 4 }}>↑ New best</div>}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ height: 24 }} />
            <div style={{ background: "var(--wh)", border: "2px solid var(--cr3)", borderRadius: "var(--r)", padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: "var(--xs)", letterSpacing: "2px", textTransform: "uppercase", fontWeight: 700, color: "var(--ink3)", marginBottom: 12 }}>How did this feel?</div>
              <div className="feel-btns" style={{ marginBottom: 12 }}>
                {[["Strong 💪", "strong"], ["Good 👍", "good"], ["Tough 😤", "tough"]].map(([lbl, val]) => (
                  <button key={val} className={`feel-btn ${sessionFeel === val ? "on" : ""}`} style={sessionFeel === val ? { background: val === "strong" ? "var(--grn)" : val === "good" ? "var(--ink)" : "var(--red)", color: "#fff", borderColor: "transparent" } : {}} onClick={() => setSessionFeel(val)}>{lbl}</button>
                ))}
              </div>
              <textarea
                style={{ width: "100%", background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "10px 12px", fontSize: "var(--sm)", outline: "none", resize: "none", fontFamily: "var(--fb)", lineHeight: 1.5 }}
                rows={2} placeholder="Any notes? (optional)"
                value={sessionClientNote} onChange={e => setSessionClientNote(e.target.value)}
              />
            </div>
            <button className="bp grn" onClick={async () => {
              if (lastSessionId && (sessionFeel || sessionClientNote)) {
                await supabase.from("session_logs").update({ feel_rating: sessionFeel || null, client_notes: sessionClientNote || null }).eq("id", lastSessionId);
              }
              setSessionSummary(null); setSessionFeel(""); setSessionClientNote("");
              go("home", "home");
            }}>
              Done
            </button>
          </div>
        )}

        {screen === "progress" && (
          <div className="pg fu">
            <div className="ph">
              <div className="pe">Your Numbers</div>
              <div className="ptt">Progress</div>
              <div className="pss">{profile?.body_weight ? `${profile.body_weight} lbs bodyweight` : "Set your bodyweight in profile"}</div>
            </div>
            {clientLoading && fullSessionHistory.length === 0 && <div className="load-center"><div className="spin" /></div>}
            {clientError && fullSessionHistory.length === 0 && !clientLoading && <div className="err-center" style={{ fontSize: "var(--sm)" }}>⚠️ {clientError}</div>}
            <div style={{ display: "flex", marginBottom: 20, borderBottom: "1px solid var(--cr3)" }}>
              {["marks", "history"].map(t => (
                <button key={t} onClick={() => setProgressTab(t)} style={{ flex: 1, padding: "13px 4px", background: "none", border: "none", borderBottom: `2px solid ${progressTab === t ? "var(--red)" : "transparent"}`, color: progressTab === t ? "var(--ink)" : "var(--ink3)", fontSize: "var(--sm)", letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer", fontFamily: "var(--fb)", fontWeight: 500 }}>
                  {t}
                </button>
              ))}
            </div>

            {progressTab === "marks" && (() => {
              const gender = profile?.gender || "m";
              const g = gender === "f" ? "f" : "m";
              const resolveLevel = (tier, gKey) => {
                if (!tier) return null;
                if (tier[`apostle_${gKey}`]) return tier[`apostle_${gKey}`](bw);
                if (tier[`disciple_${gKey}`]) return tier[`disciple_${gKey}`](bw);
                if (typeof tier === "function") return tier(bw);
                return tier;
              };
              console.log("[marks] clientBestPerformances:", clientBestPerformances);
              const marksWithLevel = MARKS_STANDARDS.map(std => {
                let bestWeight = 0, bestReps = 0;
                (std.exercises || []).forEach(exName => {
                  const key = exName.toLowerCase();
                  const best = clientBestPerformances[key];
                  console.log(`[marks] ${std.cat} → "${exName}" → best:`, best);
                  if (best && (best.weight > bestWeight || (best.weight === bestWeight && best.reps > bestReps))) {
                    bestWeight = best.weight; bestReps = best.reps;
                  }
                });
                const lvl = getClientLevel(std, bestWeight, bestReps, bw, g);
                return { std, bestWeight, bestReps, ...lvl };
              });
              const weak = marksWithLevel.filter(m => !m.level || m.level === "follower");
              const strong = marksWithLevel.filter(m => m.level === "disciple" || m.level === "apostle");
              const borderCol = (level) => level === "apostle" || level === "disciple" ? "var(--grn)" : level === "follower" ? "var(--gold)" : "var(--red)";
              const badgeBg = (level) => level === "apostle" || level === "disciple" ? "#f0f5f0" : level === "follower" ? "#fdf3de" : "var(--cr2)";
              const badgeColor = (level) => level === "apostle" || level === "disciple" ? "var(--grn)" : level === "follower" ? "var(--gold)" : "var(--ink3)";
              const renderCard = (m) => {
                const isOpen = expandedMark === m.std.cat;
                const hasData = m.bestReps > 0 || m.bestWeight > 0;
                const followerData = m.std.follower;
                const discipleData = m.std[`disciple_${g}`] ? m.std[`disciple_${g}`](bw) : m.std.disciple;
                const apostleData = m.std[`apostle_${g}`] ? m.std[`apostle_${g}`](bw) : null;
                const fa = m.level === "follower" || m.level === "disciple" || m.level === "apostle";
                const da = m.level === "disciple" || m.level === "apostle";
                const aa = m.level === "apostle";
                return (
                  <div key={m.std.cat} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderLeft: `3px solid ${borderCol(m.level)}`, borderRadius: "var(--r)", padding: "14px", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setExpandedMark(isOpen ? null : m.std.cat)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{m.std.cat}</div>
                          <span style={{ fontSize: 9, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700, color: badgeColor(m.level), background: badgeBg(m.level), padding: "2px 8px", borderRadius: 20 }}>{m.level || (hasData ? "below follower" : "no data")}</span>
                        </div>
                        <div style={{ height: 4, background: "var(--cr3)", borderRadius: 2, marginBottom: 6, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${m.pct || 0}%`, background: borderCol(m.level), borderRadius: 2 }} />
                        </div>
                        {hasData && <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{m.bestWeight > 0 ? `${m.bestWeight} lbs × ${m.bestReps} reps` : `${m.bestReps} reps`}</div>}
                        {m.nextDesc && <div style={{ fontSize: "var(--xs)", color: "var(--gold)", marginTop: 3 }}>→ {m.next ? m.next.charAt(0).toUpperCase() + m.next.slice(1) : ""}: {m.nextDesc}</div>}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink3)" }}>{isOpen ? "▲" : "▼"}</div>
                    </div>
                    {isOpen && (
                      <div style={{ borderTop: "1px solid var(--cr2)", paddingTop: 10, marginTop: 10 }}>
                        {[
                          { label: "Follower", data: followerData, achieved: fa, isCurrent: !fa, color: "var(--ink3)" },
                          { label: "Disciple", data: discipleData, achieved: da, isCurrent: fa && !da, color: "var(--gold)" },
                          { label: "Apostle", data: apostleData, achieved: aa, isCurrent: da && !aa, color: "var(--grn)" },
                        ].map((tier, ti) => tier.data && (
                          <div key={ti} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", borderTop: ti > 0 ? "1px solid var(--cr2)" : "none" }}>
                            <div style={{ fontSize: 9, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700, width: 64, flexShrink: 0, color: tier.achieved ? tier.color : "var(--ink3)", paddingTop: 1 }}>{tier.label}</div>
                            <div style={{ fontSize: "var(--xs)", color: tier.achieved ? "var(--ink)" : "var(--ink3)", flex: 1, lineHeight: 1.5 }}>
                              {typeof tier.data === "object" && tier.data.desc ? tier.data.desc : typeof tier.data === "string" ? tier.data : ""}
                            </div>
                            <div style={{ fontSize: 12, flexShrink: 0 }}>
                              {tier.achieved ? <span style={{ color: "var(--grn)" }}>✓</span> : tier.isCurrent ? <span style={{ color: "var(--gold)" }}>←</span> : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              };
              if (Object.keys(clientBestPerformances).length === 0) return (
                <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: 16 }}>
                  <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>Complete some workouts to see your marks.</div>
                </div>
              );
              return (
                <>
                  {weak.length > 0 && <><div className="sl">Priority Weak Links</div>{weak.map(m => renderCard(m))}</>}
                  {strong.length > 0 && <><div className="sl" style={{ marginTop: 8 }}>Strong Points</div>{strong.map(m => renderCard(m))}</>}
                </>
              );
            })()}

            {progressTab === "history" && (<>
              {!mySelSession && (
                <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                  {[["all","All Sessions"],["byExercise","By Exercise"]].map(([v,l]) => (
                    <button key={v} className={`cal-toggle-btn ${historyView === v ? "on" : ""}`} onClick={() => setHistoryView(v)}>{l}</button>
                  ))}
                </div>
              )}
              {!mySelSession && historyView === "byExercise" && (<>
                {Object.keys(exerciseHistoryData).length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No exercise history yet.</div>}
                {Object.entries(exerciseHistoryData).map(([exName, entries]) => {
                  const isOpen = expandedHistoryEx === exName;
                  // entries sorted desc (newest first); compare per-session max weight
                  const dateGroups = {};
                  entries.forEach(e => {
                    const d = (e.completed_at || "").split("T")[0];
                    if (!dateGroups[d]) dateGroups[d] = [];
                    dateGroups[d].push(e.weight || 0);
                  });
                  const sortedDates = Object.keys(dateGroups).sort();
                  const firstMax = sortedDates.length > 1 ? Math.max(...dateGroups[sortedDates[0]]) : null;
                  const lastMax = sortedDates.length > 1 ? Math.max(...dateGroups[sortedDates[sortedDates.length - 1]]) : null;
                  const trend = firstMax !== null && lastMax !== null
                    ? (lastMax > firstMax ? "↑ Improving" : lastMax < firstMax ? "↓ Declining" : "→ Holding")
                    : null;
                  const trendColor = trend?.startsWith("↑") ? "var(--grn)" : trend?.startsWith("↓") ? "var(--red)" : "var(--ink3)";
                  return (
                    <div key={exName} style={{ background: "var(--wh)", border: `1px solid ${isOpen ? "var(--ink)" : "var(--cr3)"}`, borderRadius: "var(--r)", marginBottom: 8 }}>
                      <div style={{ padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                        onClick={() => setExpandedHistoryEx(isOpen ? null : exName)}>
                        <div>
                          <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{exName}</div>
                          <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>{entries.length} sets logged</div>
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          {trend && <div style={{ fontSize: "var(--xs)", color: trendColor, fontWeight: 500 }}>{trend}</div>}
                          <div style={{ fontSize: 12, color: "var(--ink3)" }}>{isOpen ? "▲" : "▼"}</div>
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{ borderTop: "1px solid var(--cr2)", padding: "8px 14px 12px" }}>
                          {entries.map((e, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: i > 0 ? "1px solid var(--cr2)" : "none" }}>
                              <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{fmtDate(e.completed_at)} · Set {e.set_number}</div>
                              <div style={{ fontSize: "var(--xs)", fontWeight: 500 }}>{e.weight > 0 ? `${e.weight} lbs` : "BW"} × {e.reps_completed}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>)}
              {historyView === "all" && (mySelSession ? (<>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <button style={{ background: "none", border: "none", fontSize: "var(--lg)", cursor: "pointer", color: "var(--ink3)", padding: 0 }} onClick={() => setMySelSession(null)}>←</button>
                  <div>
                    <div style={{ fontFamily: "var(--fd)", fontSize: "var(--xl)", fontWeight: 600 }}>{mySelSession.workouts?.name || "Session"}</div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{fmtDate(mySelSession.completed_at)} · {fmtTime(mySelSession.completed_at)}</div>
                  </div>
                </div>
                {(mySelSession.feel_rating || mySelSession.client_notes) && (
                  <div style={{ background: "var(--cr2)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 14 }}>
                    {mySelSession.feel_rating && (
                      <div style={{ fontSize: "var(--xs)", marginBottom: mySelSession.client_notes ? 6 : 0 }}>
                        <span style={{ color: "var(--ink3)", marginRight: 6 }}>Feel:</span>
                        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{mySelSession.feel_rating}</span>
                      </div>
                    )}
                    {mySelSession.client_notes && (
                      <div style={{ fontSize: "var(--xs)", fontStyle: "italic", color: "var(--ink2)" }}>"{mySelSession.client_notes}"</div>
                    )}
                  </div>
                )}
                {(!mySelSession.set_logs || mySelSession.set_logs.length === 0) && (
                  <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No sets were logged for this session.</div>
                )}
                {Object.values(
                  (mySelSession.set_logs || []).reduce((acc, sl) => {
                    const name = sl.workout_exercises?.exercises?.name || "Exercise";
                    if (!acc[name]) acc[name] = { name, sets: [] };
                    acc[name].sets.push(sl);
                    return acc;
                  }, {})
                ).map((exLog, i) => (
                  <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 8 }}>
                    <div style={{ fontSize: "var(--sm)", fontWeight: 600, marginBottom: 8 }}>{exLog.name}</div>
                    {exLog.sets.sort((a, b) => a.set_number - b.set_number).map((sl, si) => (
                      <div key={si} style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--xs)", color: "var(--ink3)", padding: "4px 0", borderTop: si > 0 ? "1px solid var(--cr2)" : "none" }}>
                        <span>Set {sl.set_number}</span>
                        <span style={{ fontWeight: 600, color: "var(--ink)" }}>{sl.weight > 0 ? `${sl.weight} lbs` : "BW"} × {sl.reps_completed} reps</span>
                      </div>
                    ))}
                  </div>
                ))}
              </>) : (<>
                <div className="sl">Session History</div>
                {fullSessionHistory.length === 0 && <div style={{ color: "var(--ink3)", fontSize: "var(--sm)" }}>No sessions yet — complete a workout to see your history here.</div>}
                {fullSessionHistory.map((ses, i) => (
                  <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "14px", marginBottom: 10, cursor: "pointer" }} onClick={() => setMySelSession(ses)}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{ses.workouts?.name || "Session"}</div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>{fmtDate(ses.completed_at)}</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>
                        {ses.workouts?.focus}{ses.set_logs?.length > 0 ? ` · ${ses.set_logs.length} sets` : ""}
                      </div>
                      <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>›</div>
                    </div>
                  </div>
                ))}
              </>))}
            </>)}
          </div>
        )}

        {screen === "messages" && (
          <div className="msg fu">
            <div className="mhdr"><h2>Coach Max</h2><p>Form checks · Questions · Support</p></div>
            {!triage && (
              <div className="tri">
                <div className="trl">What do you need?</div>
                <button className="trb" onClick={() => setTriage("coach")}><div className="tri2">📹</div><div><div className="trt">Submit Form Check</div><div className="trd">Send a video or photo</div></div></button>
                <button className="trb" onClick={() => setTriage("tech")}><div className="tri2">⚙️</div><div><div className="trt">Tech Support</div><div className="trd">App issues, login problems</div></div></button>
                <button className="trb" onClick={() => setTriage("coach")}><div className="tri2">💬</div><div><div className="trt">Message Coach</div><div className="trd">Training questions</div></div></button>
              </div>
            )}
            {triage === "coach" && (<>
              <button className="bbt" onClick={() => setTriage(null)}>← Back</button>
              <div className="thr" ref={thrRef}>
                {msgs.length === 0 && <div style={{ textAlign: "center", color: "var(--ink3)", fontSize: "var(--xs)", marginTop: 40 }}>Send a message to get started</div>}
                {msgs.map((m, i) => {
                  const isMe = m.from_id === user?.id;
                  return (
                    <div key={m.id || i} className={`bw ${isMe ? "me" : "them"}`}>
                      <div className="bsn">{isMe ? "You" : "Coach Max"}</div>
                      {m.media_url ? (m.media_type === "video" ? (<video src={m.media_url} controls style={{ maxWidth: "80%", borderRadius: 12 }} />) : (<div className="med"><img src={m.media_url} alt="form check" /></div>)) : (<div className={`bb ${isMe ? "me" : "them"}`}>{m.body}</div>)}
                      <div className="bt">{fmtTime(m.created_at)}</div>
                    </div>
                  );
                })}
                {uploading && <div className="bw me"><div className="uprog"><div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Uploading...</div><div className="upb"><div className="upf" /></div></div></div>}
              </div>
              <div className="mia">
                <div className="atr">
                  <button className="ab" onClick={() => triggerFile("video")}>🎬 Video</button>
                  <button className="ab" onClick={() => triggerFile("photo")}>📸 Photo</button>
                  <button className="ab" onClick={() => triggerFile("file")}>📎 File</button>
                </div>
                <div className="ir">
                  <textarea className="mta" placeholder="Message Coach Max..." rows={1} value={msgTxt} onChange={e => setMsgTxt(e.target.value)} onInput={e => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} style={{ maxHeight: 120, overflowY: "auto" }} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTxt(); } }} />
                  <button className="snd" onClick={sendTxt}>→</button>
                </div>
              </div>
            </>)}
            {triage === "tech" && (<>
              <button className="bbt" onClick={() => setTriage(null)}>← Back</button>
              <div className="thr">
                {techMsgs.map(m => (
                  <div key={m.id} className={`bw ${m.from === "me" ? "me" : "them"}`}>
                    <div className="bsn">{m.sender}</div>
                    <div className={`bb ${m.from === "me" ? "me" : "sys"}`}>{m.text}</div>
                    <div className="bt">{m.time}</div>
                  </div>
                ))}
              </div>
              <div className="mia">
                <div className="ir">
                  <textarea className="mta" placeholder="Describe the issue..." rows={1} value={msgTxt} onChange={e => setMsgTxt(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTech(); } }} />
                  <button className="snd" onClick={sendTech}>→</button>
                </div>
              </div>
            </>)}
          </div>
        )}

        {screen === "resources" && (
          <div className="pg fu">
            <div className="ph"><div className="pe">Library</div><div className="ptt">Resources</div></div>
            {(resourcesList.length ? resourcesList : RESOURCES).reduce((cats, r) => {
              const cat = r.cat || r.category || "Other";
              if (!cats.includes(cat)) cats.push(cat);
              return cats;
            }, []).map(cat => {
              const items = (resourcesList.length ? resourcesList : RESOURCES).filter(r => (r.cat || r.category) === cat);
              return (
                <div key={cat}>
                  <div className="sl" style={{ marginTop: 8 }}>{cat}</div>
                  {items.map(r => (
                    <div key={r.id} className="rc" onClick={() => r.url && window.open(r.url, "_blank")} style={{ cursor: r.url ? "pointer" : "default" }}>
                      <div className="rcat">{cat}</div>
                      <div className="rt">{r.title}</div>
                      <div className="rd">{r.desc || r.description}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {screen === "profile" && (
          <div className="pg fu">
            <div className="ph">
              <div className="pav">{profile?.full_name?.[0] || "A"}</div>
              <div className="pnm">{profile?.full_name || "Athlete"}</div>
              <div className="pmt">Max Strength · {prog?.name || "1:1 Client"}</div>
            </div>
            <div className="sl">Info</div>
            {[
              { l: "Age", v: profile?.age ? `${profile.age} yrs` : "—" },
              { l: "Program", v: prog?.name || "—" },
              { l: "Week", v: prog ? `Week ${getWeekNum(prog.start_date, todayStr)} of ${prog.weeks || 4}` : "—" },
              { l: "Sessions Done", v: `${completedDates.length}` },
            ].map((r, i) => (
              <div key={i} className="ir2"><div className="il">{r.l}</div><div className="iv">{r.v || "—"}</div></div>
            ))}
            <div className="ir2">
              <div className="il">Body Weight</div>
              {editingBw ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input className="fi" type="number" style={{ width: 90, padding: "6px 10px" }} placeholder="lbs" value={newBwVal} onChange={e => setNewBwVal(e.target.value)} autoFocus />
                  <button className="bs grn" onClick={saveBw}>Save</button>
                  <button className="bs o" onClick={() => { setEditingBw(false); setNewBwVal(""); }}>✕</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div className="iv">{profile?.body_weight ? `${profile.body_weight} lbs` : "—"}</div>
                  <button className="bs" style={{ padding: "4px 12px" }} onClick={() => { setEditingBw(true); setNewBwVal(profile?.body_weight || ""); }}>Edit</button>
                </div>
              )}
            </div>
            <div style={{ height: 16 }} />
            <div className="sl">Weight History</div>
            {bwHistory.length < 2 ? (
              <div style={{ color: "var(--ink3)", fontSize: "var(--xs)", marginBottom: 16 }}>Log your weight regularly to see trends</div>
            ) : (() => {
              const vals = bwHistory.map(b => b.weight);
              const mn = Math.min(...vals), mx = Math.max(...vals);
              const range = mx - mn || 1;
              const W = 360, H = 60, pad = 8;
              const pts = vals.map((v, i) => `${pad + (i / (vals.length - 1)) * (W - pad * 2)},${H - pad - ((v - mn) / range) * (H - pad * 2)}`).join(" ");
              return (
                <div className="sparkline-wrap">
                  <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
                    <polyline points={pts} fill="none" stroke="var(--red)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    {vals.map((v, i) => <circle key={i} cx={pad + (i / (vals.length - 1)) * (W - pad * 2)} cy={H - pad - ((v - mn) / range) * (H - pad * 2)} r="3" fill="var(--red)" />)}
                  </svg>
                  <div className="sparkline-labels">
                    <span>{bwHistory[0].weight} lbs · {fmtDate(bwHistory[0].logged_at)}</span>
                    <span>{bwHistory[bwHistory.length - 1].weight} lbs · {fmtDate(bwHistory[bwHistory.length - 1].logged_at)}</span>
                  </div>
                </div>
              );
            })()}
            <div style={{ height: 12 }} />
            <button className="bg" onClick={signOut}>Sign Out</button>
          </div>
        )}

        {showMobilityFlow && (
          <div style={{ position: "fixed", inset: 0, background: "var(--cr)", zIndex: 600, overflowY: "auto", padding: "28px 24px 100px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
              <button style={{ background: "none", border: "none", fontSize: "var(--lg)", cursor: "pointer", color: "var(--ink3)" }} onClick={() => setShowMobilityFlow(false)}>←</button>
              <div>
                <div style={{ fontFamily: "var(--fd)", fontSize: "var(--2xl)", fontWeight: 600 }}>Mobility Flow</div>
                <div style={{ fontSize: "var(--xs)", color: "var(--ink3)" }}>Rest day recovery · 5 exercises</div>
              </div>
            </div>
            {MOBILITY_FLOW.map((ex, i) => (
              <div key={i} style={{ background: "var(--wh)", border: "1px solid var(--cr3)", borderRadius: "var(--r)", padding: "16px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: "var(--sm)", fontWeight: 600 }}>{ex.name}</div>
                    <div style={{ fontSize: "var(--xs)", color: "var(--ink3)", marginTop: 2 }}>
                      {ex.duration > 0 ? `${ex.duration}s hold` : `${ex.reps} reps`}{ex.note ? ` · ${ex.note}` : ""}
                    </div>
                  </div>
                  {ex.duration > 0 && (
                    <button className="bs o" style={{ padding: "6px 14px" }} onClick={() => {
                      setMobilityTimers(p => {
                        if (p[i]?.running) return { ...p, [i]: { ...p[i], running: false } };
                        const start = Date.now() - (p[i]?.elapsed || 0) * 1000;
                        const iv = setInterval(() => {
                          setMobilityTimers(pp => {
                            const elapsed = (Date.now() - start) / 1000;
                            if (elapsed >= ex.duration) { clearInterval(iv); return { ...pp, [i]: { elapsed: ex.duration, running: false, done: true } }; }
                            return { ...pp, [i]: { ...pp[i], elapsed, running: true } };
                          });
                        }, 250);
                        return { ...p, [i]: { ...p[i], running: true, iv, start } };
                      });
                    }}>
                      {mobilityTimers[i]?.done ? "✓" : mobilityTimers[i]?.running ? `${ex.duration - Math.floor(mobilityTimers[i]?.elapsed || 0)}s` : "Start"}
                    </button>
                  )}
                </div>
                {ex.duration > 0 && (
                  <div style={{ height: 4, background: "var(--cr3)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: mobilityTimers[i]?.done ? "var(--grn)" : "var(--red)", width: `${Math.min(100, ((mobilityTimers[i]?.elapsed || 0) / ex.duration) * 100)}%`, transition: "width 0.25s" }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {["home", "workout", "calendar", "progress", "messages", "resources", "profile"].includes(screen) && (
          <div className="bn">
            {[
              { id: "home", icon: "⚡", lbl: "Home", s: "home" },
              { id: "calendar", icon: "📅", lbl: "Program", s: "calendar" },
              { id: "progress", icon: "📊", lbl: "Progress", s: "progress" },
              { id: "messages", icon: "💬", lbl: "Coach", s: "messages" },
              { id: "profile", icon: "👤", lbl: "Profile", s: "profile" },
            ].map(n => (
              <button key={n.id} className={`nb ${navTab === n.id ? "on" : ""}`} onClick={() => go(n.s, n.id)}>
                <span className="ni">{n.icon}</span><span className="nl">{n.lbl}</span>
              </button>
            ))}
          </div>
        )}

      </div>
    </>
  );
}
